import { j as jsxRuntimeExports, c as clientExports } from "./chunk.client_v20251016.js";
import { r as reactExports, Y as Ye, y as yt, M as Mo, z as ze, j as je, R as React } from "./chunk.headless_v20251016.js";
import { a as Splide2, b as SplideSlide } from "./chunk.splide_v20251016.js";
import "./chunk.react_v20251016.js";
const AffirmProduct = reactExports.memo(({ price, pageType = "product", color = "blue" }) => {
  reactExports.useEffect(() => {
    var _a, _b, _c, _d;
    if (price) {
      if ("affirm" in window && typeof ((_b = (_a = window == null ? void 0 : window.affirm) == null ? void 0 : _a.ui) == null ? void 0 : _b.refresh) === "function") {
        (_d = (_c = window == null ? void 0 : window.affirm) == null ? void 0 : _c.ui) == null ? void 0 : _d.refresh();
      }
    }
  }, [price]);
  if (!price) {
    return null;
  }
  return /* @__PURE__ */ jsxRuntimeExports.jsx(
    "div",
    {
      className: "affirm-as-low-as my-4 text-xs text-center",
      "data-page-type": pageType,
      "data-affirm-color": color,
      "data-amount": `${parseInt(price.toFixed(2).toString().replace(".", ""))}`
    }
  );
});
const CLIENT$1 = "GraphQL Client";
const MIN_RETRIES = 0;
const MAX_RETRIES = 3;
const GQL_API_ERROR = "An error occurred while fetching from the API. Review 'graphQLErrors' for details.";
const UNEXPECTED_CONTENT_TYPE_ERROR = "Response returned unexpected Content-Type:";
const NO_DATA_OR_ERRORS_ERROR = "An unknown error has occurred. The API did not return a data object or any errors in its response.";
const CONTENT_TYPES = {
  json: "application/json",
  multipart: "multipart/mixed"
};
const SDK_VARIANT_HEADER$1 = "X-SDK-Variant";
const SDK_VERSION_HEADER$1 = "X-SDK-Version";
const DEFAULT_SDK_VARIANT$1 = "shopify-graphql-client";
const DEFAULT_CLIENT_VERSION$1 = "1.3.0";
const RETRY_WAIT_TIME = 1e3;
const RETRIABLE_STATUS_CODES = [429, 503];
const DEFER_OPERATION_REGEX = /@(defer)\b/i;
const NEWLINE_SEPARATOR = "\r\n";
const BOUNDARY_HEADER_REGEX = /boundary="?([^=";]+)"?/i;
const HEADER_SEPARATOR = NEWLINE_SEPARATOR + NEWLINE_SEPARATOR;
function formatErrorMessage(message, client = CLIENT$1) {
  return message.startsWith(`${client}`) ? message : `${client}: ${message}`;
}
function getErrorMessage(error) {
  return error instanceof Error ? error.message : JSON.stringify(error);
}
function getErrorCause(error) {
  return error instanceof Error && error.cause ? error.cause : void 0;
}
function combineErrors(dataArray) {
  return dataArray.flatMap(({ errors }) => {
    return errors ?? [];
  });
}
function validateRetries({ client, retries }) {
  if (retries !== void 0 && (typeof retries !== "number" || retries < MIN_RETRIES || retries > MAX_RETRIES)) {
    throw new Error(`${client}: The provided "retries" value (${retries}) is invalid - it cannot be less than ${MIN_RETRIES} or greater than ${MAX_RETRIES}`);
  }
}
function getKeyValueIfValid(key, value) {
  return value && (typeof value !== "object" || Array.isArray(value) || typeof value === "object" && Object.keys(value).length > 0) ? { [key]: value } : {};
}
function buildDataObjectByPath(path, data) {
  if (path.length === 0) {
    return data;
  }
  const key = path.pop();
  const newData = {
    [key]: data
  };
  if (path.length === 0) {
    return newData;
  }
  return buildDataObjectByPath(path, newData);
}
function combineObjects(baseObject, newObject) {
  return Object.keys(newObject || {}).reduce((acc, key) => {
    if ((typeof newObject[key] === "object" || Array.isArray(newObject[key])) && baseObject[key]) {
      acc[key] = combineObjects(baseObject[key], newObject[key]);
      return acc;
    }
    acc[key] = newObject[key];
    return acc;
  }, Array.isArray(baseObject) ? [...baseObject] : { ...baseObject });
}
function buildCombinedDataObject([initialDatum, ...remainingData]) {
  return remainingData.reduce(combineObjects, { ...initialDatum });
}
function generateHttpFetch({ clientLogger, customFetchApi = fetch, client = CLIENT$1, defaultRetryWaitTime = RETRY_WAIT_TIME, retriableCodes = RETRIABLE_STATUS_CODES }) {
  const httpFetch = async (requestParams, count, maxRetries) => {
    const nextCount = count + 1;
    const maxTries = maxRetries + 1;
    let response;
    try {
      response = await customFetchApi(...requestParams);
      clientLogger({
        type: "HTTP-Response",
        content: {
          requestParams,
          response
        }
      });
      if (!response.ok && retriableCodes.includes(response.status) && nextCount <= maxTries) {
        throw new Error();
      }
      return response;
    } catch (error) {
      if (nextCount <= maxTries) {
        const retryAfter = response == null ? void 0 : response.headers.get("Retry-After");
        await sleep(retryAfter ? parseInt(retryAfter, 10) : defaultRetryWaitTime);
        clientLogger({
          type: "HTTP-Retry",
          content: {
            requestParams,
            lastResponse: response,
            retryAttempt: count,
            maxRetries
          }
        });
        return httpFetch(requestParams, nextCount, maxRetries);
      }
      throw new Error(formatErrorMessage(`${maxRetries > 0 ? `Attempted maximum number of ${maxRetries} network retries. Last message - ` : ""}${getErrorMessage(error)}`, client));
    }
  };
  return httpFetch;
}
async function sleep(waitTime) {
  return new Promise((resolve) => setTimeout(resolve, waitTime));
}
function createGraphQLClient({ headers, url, customFetchApi = fetch, retries = 0, logger }) {
  validateRetries({ client: CLIENT$1, retries });
  const config = {
    headers,
    url,
    retries
  };
  const clientLogger = generateClientLogger(logger);
  const httpFetch = generateHttpFetch({
    customFetchApi,
    clientLogger,
    defaultRetryWaitTime: RETRY_WAIT_TIME
  });
  const fetchFn = generateFetch(httpFetch, config);
  const request = generateRequest(fetchFn);
  const requestStream = generateRequestStream(fetchFn);
  return {
    config,
    fetch: fetchFn,
    request,
    requestStream
  };
}
function generateClientLogger(logger) {
  return (logContent) => {
    if (logger) {
      logger(logContent);
    }
  };
}
async function processJSONResponse(response) {
  const { errors, data, extensions } = await response.json();
  return {
    ...getKeyValueIfValid("data", data),
    ...getKeyValueIfValid("extensions", extensions),
    headers: response.headers,
    ...errors || !data ? {
      errors: {
        networkStatusCode: response.status,
        message: formatErrorMessage(errors ? GQL_API_ERROR : NO_DATA_OR_ERRORS_ERROR),
        ...getKeyValueIfValid("graphQLErrors", errors),
        response
      }
    } : {}
  };
}
function generateFetch(httpFetch, { url, headers, retries }) {
  return async (operation, options = {}) => {
    const { variables, headers: overrideHeaders, url: overrideUrl, retries: overrideRetries, keepalive, signal } = options;
    const body = JSON.stringify({
      query: operation,
      variables
    });
    validateRetries({ client: CLIENT$1, retries: overrideRetries });
    const flatHeaders = Object.entries({
      ...headers,
      ...overrideHeaders
    }).reduce((headers2, [key, value]) => {
      headers2[key] = Array.isArray(value) ? value.join(", ") : value.toString();
      return headers2;
    }, {});
    if (!flatHeaders[SDK_VARIANT_HEADER$1] && !flatHeaders[SDK_VERSION_HEADER$1]) {
      flatHeaders[SDK_VARIANT_HEADER$1] = DEFAULT_SDK_VARIANT$1;
      flatHeaders[SDK_VERSION_HEADER$1] = DEFAULT_CLIENT_VERSION$1;
    }
    const fetchParams = [
      overrideUrl ?? url,
      {
        method: "POST",
        headers: flatHeaders,
        body,
        signal,
        keepalive
      }
    ];
    return httpFetch(fetchParams, 1, overrideRetries ?? retries);
  };
}
function generateRequest(fetchFn) {
  return async (...props) => {
    if (DEFER_OPERATION_REGEX.test(props[0])) {
      throw new Error(formatErrorMessage("This operation will result in a streamable response - use requestStream() instead."));
    }
    try {
      const response = await fetchFn(...props);
      const { status, statusText } = response;
      const contentType = response.headers.get("content-type") || "";
      if (!response.ok) {
        return {
          errors: {
            networkStatusCode: status,
            message: formatErrorMessage(statusText),
            response
          }
        };
      }
      if (!contentType.includes(CONTENT_TYPES.json)) {
        return {
          errors: {
            networkStatusCode: status,
            message: formatErrorMessage(`${UNEXPECTED_CONTENT_TYPE_ERROR} ${contentType}`),
            response
          }
        };
      }
      return processJSONResponse(response);
    } catch (error) {
      return {
        errors: {
          message: getErrorMessage(error)
        }
      };
    }
  };
}
async function* getStreamBodyIterator(response) {
  const decoder = new TextDecoder();
  if (response.body[Symbol.asyncIterator]) {
    for await (const chunk of response.body) {
      yield decoder.decode(chunk);
    }
  } else {
    const reader = response.body.getReader();
    let readResult;
    try {
      while (!(readResult = await reader.read()).done) {
        yield decoder.decode(readResult.value);
      }
    } finally {
      reader.cancel();
    }
  }
}
function readStreamChunk(streamBodyIterator, boundary) {
  return {
    async *[Symbol.asyncIterator]() {
      try {
        let buffer = "";
        for await (const textChunk of streamBodyIterator) {
          buffer += textChunk;
          if (buffer.indexOf(boundary) > -1) {
            const lastBoundaryIndex = buffer.lastIndexOf(boundary);
            const fullResponses = buffer.slice(0, lastBoundaryIndex);
            const chunkBodies = fullResponses.split(boundary).filter((chunk) => chunk.trim().length > 0).map((chunk) => {
              const body = chunk.slice(chunk.indexOf(HEADER_SEPARATOR) + HEADER_SEPARATOR.length).trim();
              return body;
            });
            if (chunkBodies.length > 0) {
              yield chunkBodies;
            }
            buffer = buffer.slice(lastBoundaryIndex + boundary.length);
            if (buffer.trim() === `--`) {
              buffer = "";
            }
          }
        }
      } catch (error) {
        throw new Error(`Error occured while processing stream payload - ${getErrorMessage(error)}`);
      }
    }
  };
}
function createJsonResponseAsyncIterator(response) {
  return {
    async *[Symbol.asyncIterator]() {
      const processedResponse = await processJSONResponse(response);
      yield {
        ...processedResponse,
        hasNext: false
      };
    }
  };
}
function getResponseDataFromChunkBodies(chunkBodies) {
  return chunkBodies.map((value) => {
    try {
      return JSON.parse(value);
    } catch (error) {
      throw new Error(`Error in parsing multipart response - ${getErrorMessage(error)}`);
    }
  }).map((payload) => {
    const { data, incremental, hasNext, extensions, errors } = payload;
    if (!incremental) {
      return {
        data: data || {},
        ...getKeyValueIfValid("errors", errors),
        ...getKeyValueIfValid("extensions", extensions),
        hasNext
      };
    }
    const incrementalArray = incremental.map(({ data: data2, path, errors: errors2 }) => {
      return {
        data: data2 && path ? buildDataObjectByPath(path, data2) : {},
        ...getKeyValueIfValid("errors", errors2)
      };
    });
    return {
      data: incrementalArray.length === 1 ? incrementalArray[0].data : buildCombinedDataObject([
        ...incrementalArray.map(({ data: data2 }) => data2)
      ]),
      ...getKeyValueIfValid("errors", combineErrors(incrementalArray)),
      hasNext
    };
  });
}
function validateResponseData(responseErrors, combinedData) {
  if (responseErrors.length > 0) {
    throw new Error(GQL_API_ERROR, {
      cause: {
        graphQLErrors: responseErrors
      }
    });
  }
  if (Object.keys(combinedData).length === 0) {
    throw new Error(NO_DATA_OR_ERRORS_ERROR);
  }
}
function createMultipartResponseAsyncInterator(response, responseContentType) {
  var _a, _b;
  const boundaryHeader = (responseContentType ?? "").match(BOUNDARY_HEADER_REGEX);
  const boundary = `--${boundaryHeader ? boundaryHeader[1] : "-"}`;
  if (!((_a = response.body) == null ? void 0 : _a.getReader) && !((_b = response.body) == null ? void 0 : _b[Symbol.asyncIterator])) {
    throw new Error("API multipart response did not return an iterable body", {
      cause: response
    });
  }
  const streamBodyIterator = getStreamBodyIterator(response);
  let combinedData = {};
  let responseExtensions;
  return {
    async *[Symbol.asyncIterator]() {
      var _a2;
      try {
        let streamHasNext = true;
        for await (const chunkBodies of readStreamChunk(streamBodyIterator, boundary)) {
          const responseData = getResponseDataFromChunkBodies(chunkBodies);
          responseExtensions = ((_a2 = responseData.find((datum) => datum.extensions)) == null ? void 0 : _a2.extensions) ?? responseExtensions;
          const responseErrors = combineErrors(responseData);
          combinedData = buildCombinedDataObject([
            combinedData,
            ...responseData.map(({ data }) => data)
          ]);
          streamHasNext = responseData.slice(-1)[0].hasNext;
          validateResponseData(responseErrors, combinedData);
          yield {
            ...getKeyValueIfValid("data", combinedData),
            ...getKeyValueIfValid("extensions", responseExtensions),
            hasNext: streamHasNext
          };
        }
        if (streamHasNext) {
          throw new Error(`Response stream terminated unexpectedly`);
        }
      } catch (error) {
        const cause = getErrorCause(error);
        yield {
          ...getKeyValueIfValid("data", combinedData),
          ...getKeyValueIfValid("extensions", responseExtensions),
          errors: {
            message: formatErrorMessage(getErrorMessage(error)),
            networkStatusCode: response.status,
            ...getKeyValueIfValid("graphQLErrors", cause == null ? void 0 : cause.graphQLErrors),
            response
          },
          hasNext: false
        };
      }
    }
  };
}
function generateRequestStream(fetchFn) {
  return async (...props) => {
    if (!DEFER_OPERATION_REGEX.test(props[0])) {
      throw new Error(formatErrorMessage("This operation does not result in a streamable response - use request() instead."));
    }
    try {
      const response = await fetchFn(...props);
      const { statusText } = response;
      if (!response.ok) {
        throw new Error(statusText, { cause: response });
      }
      const responseContentType = response.headers.get("content-type") || "";
      switch (true) {
        case responseContentType.includes(CONTENT_TYPES.json):
          return createJsonResponseAsyncIterator(response);
        case responseContentType.includes(CONTENT_TYPES.multipart):
          return createMultipartResponseAsyncInterator(response, responseContentType);
        default:
          throw new Error(`${UNEXPECTED_CONTENT_TYPE_ERROR} ${responseContentType}`, { cause: response });
      }
    } catch (error) {
      return {
        async *[Symbol.asyncIterator]() {
          const response = getErrorCause(error);
          yield {
            errors: {
              message: formatErrorMessage(getErrorMessage(error)),
              ...getKeyValueIfValid("networkStatusCode", response == null ? void 0 : response.status),
              ...getKeyValueIfValid("response", response)
            },
            hasNext: false
          };
        }
      };
    }
  };
}
function validateDomainAndGetStoreUrl({ client, storeDomain }) {
  try {
    if (!storeDomain || typeof storeDomain !== "string") {
      throw new Error();
    }
    const trimmedDomain = storeDomain.trim();
    const protocolUrl = trimmedDomain.match(/^https?:/) ? trimmedDomain : `https://${trimmedDomain}`;
    const url = new URL(protocolUrl);
    url.protocol = "https";
    return url.origin;
  } catch (error) {
    throw new Error(`${client}: a valid store domain ("${storeDomain}") must be provided`, { cause: error });
  }
}
function validateApiVersion({ client, currentSupportedApiVersions, apiVersion, logger }) {
  const versionError = `${client}: the provided apiVersion ("${apiVersion}")`;
  const supportedVersion = `Currently supported API versions: ${currentSupportedApiVersions.join(", ")}`;
  if (!apiVersion || typeof apiVersion !== "string") {
    throw new Error(`${versionError} is invalid. ${supportedVersion}`);
  }
  const trimmedApiVersion = apiVersion.trim();
  if (!currentSupportedApiVersions.includes(trimmedApiVersion)) {
    if (logger) {
      logger({
        type: "Unsupported_Api_Version",
        content: {
          apiVersion,
          supportedApiVersions: currentSupportedApiVersions
        }
      });
    } else {
      console.warn(`${versionError} is likely deprecated or not supported. ${supportedVersion}`);
    }
  }
}
function getQuarterMonth(quarter) {
  const month = quarter * 3 - 2;
  return month === 10 ? month : `0${month}`;
}
function getPrevousVersion(year, quarter, nQuarter) {
  const versionQuarter = quarter - nQuarter;
  if (versionQuarter <= 0) {
    return `${year - 1}-${getQuarterMonth(versionQuarter + 4)}`;
  }
  return `${year}-${getQuarterMonth(versionQuarter)}`;
}
function getCurrentApiVersion() {
  const date = /* @__PURE__ */ new Date();
  const month = date.getUTCMonth();
  const year = date.getUTCFullYear();
  const quarter = Math.floor(month / 3 + 1);
  return {
    year,
    quarter,
    version: `${year}-${getQuarterMonth(quarter)}`
  };
}
function getCurrentSupportedApiVersions() {
  const { year, quarter, version: currentVersion } = getCurrentApiVersion();
  const nextVersion = quarter === 4 ? `${year + 1}-01` : `${year}-${getQuarterMonth(quarter + 1)}`;
  return [
    getPrevousVersion(year, quarter, 3),
    getPrevousVersion(year, quarter, 2),
    getPrevousVersion(year, quarter, 1),
    currentVersion,
    nextVersion,
    "unstable"
  ];
}
function generateGetHeaders(config) {
  return (customHeaders) => {
    return { ...customHeaders ?? {}, ...config.headers };
  };
}
function generateGetGQLClientParams({ getHeaders, getApiUrl }) {
  return (operation, options) => {
    const props = [operation];
    if (options && Object.keys(options).length > 0) {
      const { variables, apiVersion: propApiVersion, headers, retries } = options;
      props.push({
        ...variables ? { variables } : {},
        ...headers ? { headers: getHeaders(headers) } : {},
        ...propApiVersion ? { url: getApiUrl(propApiVersion) } : {},
        ...retries ? { retries } : {}
      });
    }
    return props;
  };
}
const DEFAULT_CONTENT_TYPE = "application/json";
const DEFAULT_SDK_VARIANT = "storefront-api-client";
const DEFAULT_CLIENT_VERSION = "1.0.5";
const PUBLIC_ACCESS_TOKEN_HEADER = "X-Shopify-Storefront-Access-Token";
const PRIVATE_ACCESS_TOKEN_HEADER = "Shopify-Storefront-Private-Token";
const SDK_VARIANT_HEADER = "X-SDK-Variant";
const SDK_VERSION_HEADER = "X-SDK-Version";
const SDK_VARIANT_SOURCE_HEADER = "X-SDK-Variant-Source";
const CLIENT = "Storefront API Client";
function validatePrivateAccessTokenUsage(privateAccessToken) {
  if (privateAccessToken && typeof window !== "undefined") {
    throw new Error(`${CLIENT}: private access tokens and headers should only be used in a server-to-server implementation. Use the public API access token in nonserver environments.`);
  }
}
function validateRequiredAccessTokens(publicAccessToken, privateAccessToken) {
  if (!publicAccessToken && !privateAccessToken) {
    throw new Error(`${CLIENT}: a public or private access token must be provided`);
  }
  if (publicAccessToken && privateAccessToken) {
    throw new Error(`${CLIENT}: only provide either a public or private access token`);
  }
}
function createStorefrontApiClient({ storeDomain, apiVersion, publicAccessToken, privateAccessToken, clientName, retries = 0, customFetchApi, logger }) {
  const currentSupportedApiVersions = getCurrentSupportedApiVersions();
  const storeUrl = validateDomainAndGetStoreUrl({
    client: CLIENT,
    storeDomain
  });
  const baseApiVersionValidationParams = {
    client: CLIENT,
    currentSupportedApiVersions,
    logger
  };
  validateApiVersion({ ...baseApiVersionValidationParams, apiVersion });
  validateRequiredAccessTokens(publicAccessToken, privateAccessToken);
  validatePrivateAccessTokenUsage(privateAccessToken);
  const apiUrlFormatter = generateApiUrlFormatter(storeUrl, apiVersion, baseApiVersionValidationParams);
  const config = {
    storeDomain: storeUrl,
    apiVersion,
    ...publicAccessToken ? { publicAccessToken } : {
      privateAccessToken
    },
    headers: {
      "Content-Type": DEFAULT_CONTENT_TYPE,
      Accept: DEFAULT_CONTENT_TYPE,
      [SDK_VARIANT_HEADER]: DEFAULT_SDK_VARIANT,
      [SDK_VERSION_HEADER]: DEFAULT_CLIENT_VERSION,
      ...clientName ? { [SDK_VARIANT_SOURCE_HEADER]: clientName } : {},
      ...publicAccessToken ? { [PUBLIC_ACCESS_TOKEN_HEADER]: publicAccessToken } : { [PRIVATE_ACCESS_TOKEN_HEADER]: privateAccessToken }
    },
    apiUrl: apiUrlFormatter(),
    clientName
  };
  const graphqlClient = createGraphQLClient({
    headers: config.headers,
    url: config.apiUrl,
    retries,
    customFetchApi,
    logger
  });
  const getHeaders = generateGetHeaders(config);
  const getApiUrl = generateGetApiUrl(config, apiUrlFormatter);
  const getGQLClientParams = generateGetGQLClientParams({
    getHeaders,
    getApiUrl
  });
  const client = {
    config,
    getHeaders,
    getApiUrl,
    fetch: (...props) => {
      return graphqlClient.fetch(...getGQLClientParams(...props));
    },
    request: (...props) => {
      return graphqlClient.request(...getGQLClientParams(...props));
    },
    requestStream: (...props) => {
      return graphqlClient.requestStream(...getGQLClientParams(...props));
    }
  };
  return Object.freeze(client);
}
function generateApiUrlFormatter(storeUrl, defaultApiVersion, baseApiVersionValidationParams) {
  return (apiVersion) => {
    if (apiVersion) {
      validateApiVersion({
        ...baseApiVersionValidationParams,
        apiVersion
      });
    }
    const urlApiVersion = (apiVersion ?? defaultApiVersion).trim();
    return `${storeUrl}/api/${urlApiVersion}/graphql.json`;
  };
}
function generateGetApiUrl(config, apiUrlFormatter) {
  return (propApiVersion) => {
    return propApiVersion ? apiUrlFormatter(propApiVersion) : config.apiUrl;
  };
}
const storefrontClient = createStorefrontApiClient({
  storeDomain: Shopify.shop,
  apiVersion: "2025-01",
  publicAccessToken: Shopify.storefrontToken
});
const executeStorefrontQuery = async (query, variables = {}) => {
  try {
    const { data, errors } = await storefrontClient.request(query, {
      variables
    });
    if (errors) {
      console.error("Storefront API Errors:", errors);
      throw new Error("Storefront API request failed");
    }
    return data;
  } catch (error) {
    console.error("Storefront API Error:", error);
    throw error;
  }
};
const BASE_PRODUCT_QUERY = `#graphql
  query GetProduct(
    $id: ID!,
    $country: CountryCode,
    $language: LanguageCode
  ) @inContext(country: $country, language: $language) {
    product(id: $id) {
      id
      title
      description
      handle
      tags
      availableForSale
      totalInventory
      priceRange {
        minVariantPrice {
          amount
          currencyCode
        }
      }
      images(first: 1) {
        edges {
          node {
            url
            altText
            width
            height
            small: transformedSrc(maxWidth: 300, crop: CENTER)
            medium: transformedSrc(maxWidth: 600, crop: CENTER)
            large: transformedSrc(maxWidth: 1200, crop: CENTER)
          }
        }
      }
      variants(first: 250) {
        edges {
          node {
            id
            title
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
            availableForSale
          }
        }
      }
      # Base Product Metadata
      baseProductMeta: metafields(identifiers: [
        { namespace: "base_product", key: "default_dexterity" }
        { namespace: "base_product", key: "default_club_model" }
        { namespace: "base_product", key: "default_club_finish" }
        { namespace: "base_product", key: "default_shaft_material" }
        { namespace: "base_product", key: "default_shaft_flex" }
        { namespace: "base_product", key: "default_grip_size" }
        { namespace: "base_product", key: "default_length_adjustment" }
      ]) {
        id
        type
        namespace
        key
        value
      }
      # Product Metadata
      productMeta: metafields(identifiers: [
        { namespace: "product", key: "custom_badge" }
        { namespace: "product", key: "display_title" }
      ]) {
        id
        type
        namespace
        key
        value
      }
      # Get base heads references
      baseHeads: metafields(identifiers: [
        { namespace: "base_product", key: "list_of_base_heads" }
      ]) {
        id
        type
        namespace
        key
        value
        references(first: 250) {
          nodes {
            ... on Product {
              id
              title
              productType
              images(first: 4) {
                edges {
                  node {
                    url
                    altText
                    width
                    height
                    small: transformedSrc(maxWidth: 300, crop: CENTER)
                    medium: transformedSrc(maxWidth: 600, crop: CENTER)
                    large: transformedSrc(maxWidth: 1200, crop: CENTER)
                  }
                }
              }

              # Base Head Metadata
              meta: metafields(identifiers: [
                { namespace: "base_head", key: "list_of_set_compositions" }
                { namespace: "base_head", key: "available_putter_hosels" }
                { namespace: "base_head", key: "list_of_default_heads" }
                { namespace: "base_head", key: "head_model" }
                { namespace: "base_head", key: "head_color" }
                { namespace: "base_head", key: "default_putter_hosel" }
                { namespace: "base_head", key: "list_of_loft" }
                { namespace: "base_head", key: "list_of_lie" }
                { namespace: "base_head", key: "list_of_length" }
                { namespace: "base_head", key: "list_of_default_grip" }
                { namespace: "base_head", key: "default_weight_configuration" }
                { namespace: "base_head", key: "standard_length" }
              ]) {
                id
                type
                namespace
                key
                value
              }

              # Head Components
              headComponents: metafields(identifiers: [
                { namespace: "base_head", key: "list_of_heads" }
              ]) {
                id
                type
                namespace
                key
                references(first: 250) {
                  nodes {
                    ... on Product {
                      id
                      title
                      productType
                      availableForSale
                      totalInventory
                      images(first: 1) {
                        edges {
                          node {
                            url
                            altText
                            width
                            height
                            small: transformedSrc(maxWidth: 300, crop: CENTER)
                            medium: transformedSrc(maxWidth: 600, crop: CENTER)
                            large: transformedSrc(maxWidth: 1200, crop: CENTER)
                          }
                        }
                      }
                      variants(first: 1) {
                        nodes {
                          id
                          sku
                          availableForSale
                          currentlyNotInStock
                          quantityAvailable
                          price {
                            amount
                            currencyCode
                          }
                          compareAtPrice {
                            amount
                            currencyCode
                          }
                        }
                      }
                      meta: metafields(identifiers: [
                        { namespace: "head_component", key: "dexterity" }
                        { namespace: "head_component", key: "bounce" }
                        { namespace: "head_component", key: "loft" }
                        { namespace: "head_component", key: "standard_length" }
                        { namespace: "head_component", key: "default_weight_configuration" }
                        { namespace: "shaft_component", key: "tapered_shaft" }
                        { namespace: "shaft_component", key: "material" }
                        { namespace: "head_component", key: "club_component_mapping" }
                        { namespace: "product", key: "display_title" }
                      ]) {
                        id
                        type
                        namespace
                        key
                        value
                      }
                    }
                  }
                }
              }

              # Shaft Components
              shaftComponents: metafields(identifiers: [
                { namespace: "base_head", key: "list_of_default_shafts" }
                { namespace: "base_head", key: "list_of_shaftsladies" }
                { namespace: "base_head", key: "list_of_shaftssenior" }
                { namespace: "base_head", key: "list_of_shaftsregular" }
                { namespace: "base_head", key: "list_of_shaftsstiff" }
                { namespace: "base_head", key: "list_of_shaftsxstiff" }
                { namespace: "base_head", key: "list_of_shaftsputter" }
              ]) {
                id
                type
                namespace
                key
                references(first: 250) {
                  nodes {
                    ... on Product {
                      id
                      title
                      productType
                      images(first: 1) {
                        edges {
                          node {
                            url
                            altText
                            width
                            height
                            small: transformedSrc(maxWidth: 300, crop: CENTER)
                            medium: transformedSrc(maxWidth: 600, crop: CENTER)
                            large: transformedSrc(maxWidth: 1200, crop: CENTER)
                          }
                        }
                      }
                      availableForSale
                      totalInventory
                      variants(first: 1) {
                        nodes {
                          id
                          sku
                          availableForSale
                          currentlyNotInStock
                          quantityAvailable
                          price {
                            amount
                            currencyCode
                          }
                          compareAtPrice {
                            amount
                            currencyCode
                          }
                        }
                      }
                      meta: metafields(identifiers: [
                        { namespace: "shaft_component", key: "material" }
                        { namespace: "shaft_component", key: "tapered_shaft" }
                        { namespace: "head_component", key: "club_component_mapping" }
                        { namespace: "product", key: "display_title" }
                        { namespace: "product", key: "product_details" }
                      ]) {
                        id
                        type
                        namespace
                        key
                        value
                      }
                      taperedShafts: metafields(identifiers: [
                        { namespace: "tapered_shaft_component", key: "list_of_tapered_shaftstiff" }
                        { namespace: "tapered_shaft_component", key: "list_of_tapered_shaftxstiff" }
                        { namespace: "tapered_shaft_component", key: "list_of_tapered_shaftregular" }
                        { namespace: "tapered_shaft_component", key: "list_of_tapered_shaftsenior" }
                        { namespace: "tapered_shaft_component", key: "list_of_tapered_shaftladies" }
                      ]) {
                        id
                        type
                        namespace
                        key
                        references(first: 250) {
                          nodes {
                            ... on Product {
                              id
                              title
                              productType
                              images(first: 1) {
                                edges {
                                  node {
                                    url
                                    altText
                                    width
                                    height
                                    small: transformedSrc(maxWidth: 300, crop: CENTER)
                                    medium: transformedSrc(maxWidth: 600, crop: CENTER)
                                    large: transformedSrc(maxWidth: 1200, crop: CENTER)
                                  }
                                }
                              }
                              availableForSale
                              totalInventory
                              variants(first: 1) {
                                nodes {
                                  id
                                  sku
                                  availableForSale
                                  currentlyNotInStock
                                  quantityAvailable
                                  price {
                                    amount
                                    currencyCode
                                  }
                                  compareAtPrice {
                                    amount
                                    currencyCode
                                  }
                                }
                              }
                              meta: metafields(identifiers: [
                                { namespace: "shaft_component", key: "material" }
                                { namespace: "head_component", key: "club_component_mapping" }
                                { namespace: "product", key: "display_title" }
                              ]) {
                                id
                                type
                                namespace
                                key
                                value
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }

              # Hosel Components
              hoselComponents: metafields(identifiers: [
                { namespace: "base_head", key: "list_of_hosels" }
              ]) {
                id
                type
                namespace
                key
                references(first: 250) {
                  nodes {
                    ... on Product {
                      id
                      title
                      productType
                      availableForSale
                      totalInventory
                      variants(first: 1) {
                        nodes {
                          id
                          sku
                          availableForSale
                          currentlyNotInStock
                          quantityAvailable
                          price {
                            amount
                            currencyCode
                          }
                          compareAtPrice {
                            amount
                            currencyCode
                          }
                        }
                      }
                      meta: metafields(identifiers: [
                        { namespace: "shaft_component", key: "material" }
                        { namespace: "shaft_component", key: "tapered_shaft" }
                        { namespace: "head_component", key: "club_component_mapping" }
                        { namespace: "head_component", key: "dexterity" }
                      ]) {
                        id
                        type
                        namespace
                        key
                        value
                      }
                    }
                  }
                }
              }

              # Grip Components
              gripComponents: metafields(identifiers: [
                { namespace: "base_head", key: "list_of_gripsputter" }
                { namespace: "base_head", key: "list_of_gripsundersize" }
                { namespace: "base_head", key: "list_of_gripsstandard" }
                { namespace: "base_head", key: "list_of_gripsmidsize" }
                { namespace: "base_head", key: "list_of_gripsoversize" }
              ]) {
                id
                type
                namespace
                key
                references(first: 250) {
                  nodes {
                    ... on Product {
                      id
                      title
                      productType
                      images(first: 1) {
                        edges {
                          node {
                            url
                            altText
                            width
                            height
                            small: transformedSrc(maxWidth: 300, crop: CENTER)
                            medium: transformedSrc(maxWidth: 600, crop: CENTER)
                            large: transformedSrc(maxWidth: 1200, crop: CENTER)
                          }
                        }
                      }
                      availableForSale
                      totalInventory
                      variants(first: 1) {
                        nodes {
                          id
                          sku
                          availableForSale
                          currentlyNotInStock
                          quantityAvailable
                          price {
                            amount
                            currencyCode
                          }
                          compareAtPrice {
                            amount
                            currencyCode
                          }
                        }
                      }
                      meta: metafields(identifiers: [
                        { namespace: "head_component", key: "club_component_mapping" }
                        { namespace: "product", key: "display_title" }
                        { namespace: "head_component", key: "dexterity" }
                        { namespace: "product", key: "product_details" }
                      ]) {
                        id
                        type
                        namespace
                        key
                        value
                      }
                    }
                  }
                }
              }

              # Torque Wrench Components
              torqueWrenchComponents: metafields(identifiers: [
                { namespace: "base_head", key: "torque_wrench_product" }
              ]) {
                id
                type
                namespace
                key
                references(first: 250) {
                  nodes {
                    ... on Product {
                      id
                      title
                      productType
                      availableForSale
                      totalInventory
                      variants(first: 1) {
                        nodes {
                          id
                          sku
                          availableForSale
                          currentlyNotInStock
                          quantityAvailable
                          price {
                            amount
                            currencyCode
                          }
                          compareAtPrice {
                            amount
                            currencyCode
                          }
                        }
                      }
                      meta: metafields(identifiers: [
                        { namespace: "shaft_component", key: "material" }
                        { namespace: "head_component", key: "club_component_mapping" }
                        { namespace: "product", key: "display_title" }
                      ]) {
                        id
                        type
                        namespace
                        key
                        value
                      }
                    }
                  }
                }
              }

              # Patches Components
              patchesComponents: metafields(identifiers: [
                { namespace: "base_head", key: "list_of_patches" }
              ]) {
                id
                type
                namespace
                key
                references(first: 250) {
                  nodes {
                    ... on Product {
                      id
                      title
                      productType
                      availableForSale
                      totalInventory
                      variants(first: 1) {
                        nodes {
                          id
                          sku
                          availableForSale
                          currentlyNotInStock
                          quantityAvailable
                          price {
                            amount
                            currencyCode
                          }
                          compareAtPrice {
                            amount
                            currencyCode
                          }
                        }
                      }
                      meta: metafields(identifiers: [
                        { namespace: "shaft_component", key: "material" }
                        { namespace: "shaft_component", key: "tapered_shaft" }
                        { namespace: "head_component", key: "club_component_mapping" }
                        { namespace: "product", key: "display_title" }
                      ]) {
                        id
                        type
                        namespace
                        key
                        value
                      }
                    }
                  }
                }
              }

              # Headcover Components
              headcoverComponents: metafields(identifiers: [
                { namespace: "base_head", key: "list_of_headcover" }
              ]) {
                id
                type
                namespace
                key
                references(first: 250) {
                  nodes {
                    ... on Product {
                      id
                      title
                      productType
                      images(first: 1) {
                        edges {
                          node {
                            url
                            altText
                            width
                            height
                            small: transformedSrc(maxWidth: 300, crop: CENTER)
                            medium: transformedSrc(maxWidth: 600, crop: CENTER)
                            large: transformedSrc(maxWidth: 1200, crop: CENTER)
                          }
                        }
                      }
                      availableForSale
                      totalInventory
                      variants(first: 1) {
                        nodes {
                          id
                          sku
                          availableForSale
                          currentlyNotInStock
                          quantityAvailable
                          price {
                            amount
                            currencyCode
                          }
                          compareAtPrice {
                            amount
                            currencyCode
                          }
                        }
                      }
                      meta: metafields(identifiers: [
                        { namespace: "shaft_component", key: "material" }
                        { namespace: "shaft_component", key: "tapered_shaft" }
                        { namespace: "head_component", key: "club_component_mapping" }
                        { namespace: "product", key: "display_title" }
                      ]) {
                        id
                        type
                        namespace
                        key
                        value
                      }
                    }
                  }
                }
              }

            }
          }
        }
      }
    }
  }
`;
const formatPrice = (price, currencyCode = "USD") => {
  var _a, _b;
  if (price === 0) return null;
  const locale = ((_a = window.Shopify) == null ? void 0 : _a.locale) || "en";
  const country = ((_b = window.Shopify) == null ? void 0 : _b.country) || "US";
  const localCode = `${locale}-${country}`;
  const formattedPrice = new Intl.NumberFormat(localCode, {
    style: "currency",
    currency: currencyCode,
    currencyDisplay: "narrowSymbol"
  }).format(price);
  return `${formattedPrice}`;
};
const TorqueWrenchSelector = ({ includeTorqueWrench, handleTorqueWrenchChange, torqueWrenchProduct, label }) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j;
  if (!(torqueWrenchProduct == null ? void 0 : torqueWrenchProduct.length)) {
    return null;
  }
  const priceAmount = (_e = (_d = (_c = (_b = (_a = torqueWrenchProduct[0]) == null ? void 0 : _a.variants) == null ? void 0 : _b.nodes) == null ? void 0 : _c[0]) == null ? void 0 : _d.price) == null ? void 0 : _e.amount;
  const currencyCode = (_j = (_i = (_h = (_g = (_f = torqueWrenchProduct[0]) == null ? void 0 : _f.variants) == null ? void 0 : _g.nodes) == null ? void 0 : _h[0]) == null ? void 0 : _i.price) == null ? void 0 : _j.currencyCode;
  const price = priceAmount ? formatPrice(priceAmount, currencyCode) : "";
  return /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "mb-4", children: /* @__PURE__ */ jsxRuntimeExports.jsx(Ye.Group, { children: /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex items-center gap-3", children: [
    /* @__PURE__ */ jsxRuntimeExports.jsxs(
      Ye,
      {
        checked: includeTorqueWrench,
        onChange: handleTorqueWrenchChange,
        className: `${includeTorqueWrench ? "bg-shadow-black" : "bg-transparent"} relative flex !h-6 !w-6 items-center justify-center rounded border border-shadow-black transition-colors focus:outline-none focus:ring-2 focus:ring-shadow-black focus:ring-offset-2`,
        children: [
          /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "sr-only", children: label || "Add a Torque Wrench" }),
          includeTorqueWrench && /* @__PURE__ */ jsxRuntimeExports.jsx(
            "svg",
            {
              xmlns: "http://www.w3.org/2000/svg",
              className: "h-5 w-5 text-fairway",
              viewBox: "0 0 20 20",
              fill: "currentColor",
              children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                "path",
                {
                  fillRule: "evenodd",
                  d: "M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z",
                  clipRule: "evenodd"
                }
              )
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsxRuntimeExports.jsxs(Ye.Label, { className: "p4 text-shadow-black", children: [
      label || "Add a Torque Wrench",
      " ",
      price && `+ ${price}`
    ] })
  ] }) }) });
};
const DexteritySelector = ({
  selectedDexterity,
  setSelectedDexterity,
  selectedBaseHead,
  getSelectedHeadComponents,
  selectedHosel,
  label,
  labelRight,
  labelLeft,
  className = ""
  // Allow custom className to be passed
}) => {
  return /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: `mb-6 flex flex-col ${className}`, children: [
    /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "p4 mb-2 text-shadow-black", children: label }),
    /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex flex-wrap gap-2", children: [
      { value: "R", label: labelRight || "Right" },
      { value: "L", label: labelLeft || "Left" }
    ].map(({ value, label: label2 }) => {
      const hasComponents = selectedBaseHead ? getSelectedHeadComponents(selectedBaseHead, value, "", selectedHosel).length > 0 : true;
      return /* @__PURE__ */ jsxRuntimeExports.jsx(
        "button",
        {
          onClick: () => setSelectedDexterity(value),
          disabled: selectedBaseHead && !hasComponents,
          className: `px-4 py-2 transition-colors border rounded-lg border-shadow-black ${selectedDexterity === value ? "bg-shadow-black text-fairway" : "bg-transparent text-shadow-black hover:bg-grey-v3"} ${selectedBaseHead && !hasComponents && "cursor-not-allowed border-grey-v3 bg-system-disabled text-grey-v3 opacity-50"}`,
          children: label2
        },
        value
      );
    }) })
  ] });
};
const HeadModelColorSelector = ({
  selectedModel,
  setSelectedModel,
  selectedColor,
  setSelectedColor,
  availableModels,
  availableModelsForColor,
  availableColors,
  availableColorsForModel,
  baseHeads,
  labelModel,
  labelColor,
  headModelTranslationsValues = {},
  headColorTranslationsValues = {},
  className = "",
  onUserInteraction
}) => {
  return /* @__PURE__ */ jsxRuntimeExports.jsxs(
    "div",
    {
      className: `mb-4 flex flex-row flex-wrap gap-4 ${className} ${availableModels.length < 2 && availableColors.length < 2 ? "hidden" : ""}`,
      children: [
        /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: `${availableModels.length < 2 ? "hidden" : ""}`, children: [
          /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "p4 mb-2 text-shadow-black", children: labelModel }),
          /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex flex-wrap gap-2", children: availableModels.map((model) => {
            const isAvailable = !selectedColor || availableModelsForColor.includes(model);
            const displayText = headModelTranslationsValues && headModelTranslationsValues[model] || model;
            return /* @__PURE__ */ jsxRuntimeExports.jsx(
              "button",
              {
                onClick: () => {
                  onUserInteraction == null ? void 0 : onUserInteraction();
                  setSelectedModel(model);
                  if (selectedColor) {
                    const validColorsForNewModel = baseHeads.filter((head) => {
                      var _a, _b;
                      return ((_b = (_a = head.meta) == null ? void 0 : _a.find((m) => (m == null ? void 0 : m.key) === "head_model")) == null ? void 0 : _b.value) === model;
                    }).map((head) => {
                      var _a, _b;
                      return (_b = (_a = head.meta) == null ? void 0 : _a.find((m) => (m == null ? void 0 : m.key) === "head_color")) == null ? void 0 : _b.value;
                    }).filter(Boolean);
                    if (!validColorsForNewModel.includes(selectedColor)) {
                      setSelectedColor(validColorsForNewModel[0] || "");
                    }
                  }
                },
                disabled: !isAvailable,
                className: `px-4 py-2 transition-colors border rounded-lg border-shadow-black ${selectedModel === model ? "bg-shadow-black text-fairway" : "bg-transparent text-shadow-black hover:bg-grey-v3"} ${!isAvailable && "cursor-not-allowed border-grey-v3 bg-system-disabled text-grey-v3 opacity-50"}`,
                children: displayText
              },
              model
            );
          }) })
        ] }),
        /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: `${availableColors.length < 2 ? "hidden" : ""}`, children: [
          /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "p4 mb-2 text-shadow-black", children: labelColor }),
          /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex flex-wrap gap-2", children: availableColors.map((color) => {
            const isAvailable = !selectedModel || availableColorsForModel.includes(color);
            const displayText = headColorTranslationsValues && headColorTranslationsValues[color] || color;
            return /* @__PURE__ */ jsxRuntimeExports.jsx(
              "button",
              {
                onClick: () => {
                  onUserInteraction == null ? void 0 : onUserInteraction();
                  setSelectedColor(color);
                  if (selectedModel) {
                    const validModelsForNewColor = baseHeads.filter((head) => {
                      var _a, _b;
                      return ((_b = (_a = head.meta) == null ? void 0 : _a.find((m) => (m == null ? void 0 : m.key) === "head_color")) == null ? void 0 : _b.value) === color;
                    }).map((head) => {
                      var _a, _b;
                      return (_b = (_a = head.meta) == null ? void 0 : _a.find((m) => (m == null ? void 0 : m.key) === "head_model")) == null ? void 0 : _b.value;
                    }).filter(Boolean);
                    if (!validModelsForNewColor.includes(selectedModel)) {
                      setSelectedModel(validModelsForNewColor[0] || "");
                    }
                  }
                },
                disabled: !isAvailable,
                className: `px-4 py-2 transition-colors border rounded-lg border-shadow-black ${selectedColor === color ? "bg-shadow-black text-fairway" : "bg-transparent text-shadow-black hover:bg-grey-v3"} ${!isAvailable && "cursor-not-allowed border-grey-v3 bg-system-disabled text-grey-v3 opacity-50"}`,
                children: displayText
              },
              color
            );
          }) })
        ] })
      ]
    }
  );
};
const LoftSelector = ({
  clubType,
  selectedBaseHead,
  selectedLofts,
  setSelectedLofts,
  selectedDexterity,
  isLoftAvailable,
  getCompositionLofts,
  formatLoftDisplay,
  handleLoftSelection,
  selectedComposition,
  setSelectedComposition,
  className = "",
  labelClubSelection,
  labelIncludedItems,
  onUserInteraction
}) => {
  var _a, _b, _c;
  const canDeselectLoft = (loft) => {
    return !(selectedLofts.length === 1 && selectedLofts.includes(loft));
  };
  const getAllPossibleLofts = () => {
    var _a2, _b2;
    if (!selectedBaseHead) return [];
    try {
      const headComponent = (_a2 = selectedBaseHead == null ? void 0 : selectedBaseHead.headComponents) == null ? void 0 : _a2.find((component) => (component == null ? void 0 : component.key) === "list_of_heads");
      const heads = ((_b2 = headComponent == null ? void 0 : headComponent.references) == null ? void 0 : _b2.nodes) || [];
      const allLofts = heads.filter((head) => {
        var _a3, _b3;
        return ((_b3 = (_a3 = head == null ? void 0 : head.meta) == null ? void 0 : _a3.find((m) => (m == null ? void 0 : m.key) === "dexterity")) == null ? void 0 : _b3.value) === selectedDexterity;
      }).map((head) => {
        var _a3, _b3;
        return (_b3 = (_a3 = head == null ? void 0 : head.meta) == null ? void 0 : _a3.find((m) => (m == null ? void 0 : m.key) === "loft")) == null ? void 0 : _b3.value;
      }).filter(Boolean).sort((a, b) => {
        if (!isNaN(parseFloat(a)) && !isNaN(parseFloat(b))) {
          return parseFloat(a) - parseFloat(b);
        }
        const wedgeOrder = {
          "Pitching Wedge": 1,
          "Gap Wedge": 2,
          "Sand Wedge": 3,
          "Lob Wedge": 4
        };
        if (wedgeOrder[a] && wedgeOrder[b]) {
          return wedgeOrder[a] - wedgeOrder[b];
        }
        if (a.includes("Iron") && b.includes("Iron")) {
          return parseInt(a.split(" ")[0]) - parseInt(b.split(" ")[0]);
        }
        return a.localeCompare(b);
      });
      return [...new Set(allLofts)];
    } catch (error) {
      console.error("Error getting all possible lofts:", error);
      return [];
    }
  };
  const getAllLoftsInCompositionRange = (composition) => {
    try {
      const allPossibleLofts = getAllPossibleLofts();
      const loftSortOrder = (loft) => {
        const wedgeOrder = {
          "Pitching Wedge": 9.5,
          "Gap Wedge": 9.6,
          "Sand Wedge": 9.7,
          "Lob Wedge": 9.8
        };
        if (wedgeOrder[loft]) return wedgeOrder[loft];
        return parseInt(loft.split(" ")[0]);
      };
      const loftOrder = allPossibleLofts.sort((a, b) => loftSortOrder(a) - loftSortOrder(b));
      const [start, end] = composition.split(" - ");
      const startLoft = start.includes("Iron") ? start : `${start} Iron`;
      const endLoft = {
        W: "Pitching Wedge",
        G: "Gap Wedge",
        S: "Sand Wedge",
        L: "Lob Wedge"
      }[end] || `${end} Iron`;
      const allPossibleLoftValues = {};
      for (let i = 3; i <= 9; i++) {
        allPossibleLoftValues[`${i} Iron`] = i;
      }
      allPossibleLoftValues["Pitching Wedge"] = 9.5;
      allPossibleLoftValues["Gap Wedge"] = 9.6;
      allPossibleLoftValues["Sand Wedge"] = 9.7;
      allPossibleLoftValues["Lob Wedge"] = 9.8;
      const startValue = allPossibleLoftValues[startLoft];
      const endValue = allPossibleLoftValues[endLoft];
      if (startValue === void 0 || endValue === void 0) {
        console.error("Invalid loft range:", startLoft, endLoft);
        return [];
      }
      return loftOrder.filter((loft) => {
        const loftValue = loftSortOrder(loft);
        return loftValue >= startValue && loftValue <= endValue;
      });
    } catch (error) {
      console.error("Error getting all lofts in composition range:", error);
      return [];
    }
  };
  const areAllLoftsInCompositionAvailable = (composition) => {
    const compositionLofts = getAllLoftsInCompositionRange(composition);
    return compositionLofts.length > 0 && compositionLofts.every((loft) => isLoftAvailable(loft, selectedBaseHead, selectedDexterity));
  };
  const findMatchingComposition = (currentLofts) => {
    var _a2, _b2, _c2, _d;
    if (!((_b2 = (_a2 = selectedBaseHead == null ? void 0 : selectedBaseHead.meta) == null ? void 0 : _a2.find((m) => (m == null ? void 0 : m.key) === "list_of_set_compositions")) == null ? void 0 : _b2.value)) {
      return null;
    }
    try {
      const setCompositions = JSON.parse(
        (_d = (_c2 = selectedBaseHead == null ? void 0 : selectedBaseHead.meta) == null ? void 0 : _c2.find((m) => (m == null ? void 0 : m.key) === "list_of_set_compositions")) == null ? void 0 : _d.value
      );
      if (!Array.isArray(setCompositions)) return null;
      const sortedCurrentLofts = [...currentLofts].sort();
      for (const composition of setCompositions) {
        if (!areAllLoftsInCompositionAvailable(composition)) continue;
        const compositionLofts = getCompositionLofts(composition);
        const sortedCompositionLofts = [...compositionLofts].sort();
        if (sortedCurrentLofts.length === sortedCompositionLofts.length && sortedCurrentLofts.every((loft, index) => loft === sortedCompositionLofts[index])) {
          return composition;
        }
      }
      return "";
    } catch (error) {
      console.error("Error finding matching composition:", error);
      return "";
    }
  };
  const handleLoftSelectionWithComposition = (loft) => {
    onUserInteraction == null ? void 0 : onUserInteraction();
    handleLoftSelection(loft);
    let newSelectedLofts;
    if (clubType === "irons") {
      if (selectedLofts.includes(loft) && selectedLofts.length > 1) {
        newSelectedLofts = selectedLofts.filter((l) => l !== loft);
      } else if (!selectedLofts.includes(loft)) {
        newSelectedLofts = [...selectedLofts, loft];
      } else {
        return;
      }
    } else {
      newSelectedLofts = [loft];
    }
    const matchingComposition = findMatchingComposition(newSelectedLofts);
    setSelectedComposition(matchingComposition);
  };
  reactExports.useEffect(() => {
    if (selectedLofts.length > 0 && clubType === "irons" && !selectedComposition) {
      const matchingComposition = findMatchingComposition(selectedLofts);
      if (matchingComposition) {
        setSelectedComposition(matchingComposition);
      }
    }
  }, [selectedLofts, selectedBaseHead]);
  return /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: `flex flex-col ${className} ${clubType === "putters" && "hidden"}`, children: [
    clubType === "irons" && ((_b = (_a = selectedBaseHead == null ? void 0 : selectedBaseHead.meta) == null ? void 0 : _a.find((m) => (m == null ? void 0 : m.key) === "list_of_set_compositions")) == null ? void 0 : _b.value) && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mb-4", children: [
      /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "p4 mb-2 text-shadow-black", children: labelClubSelection || "Club Selection" }),
      /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex flex-wrap gap-2", children: (() => {
        var _a2, _b2;
        try {
          const setCompositions = JSON.parse(
            (_b2 = (_a2 = selectedBaseHead == null ? void 0 : selectedBaseHead.meta) == null ? void 0 : _a2.find((m) => (m == null ? void 0 : m.key) === "list_of_set_compositions")) == null ? void 0 : _b2.value
          );
          if (!Array.isArray(setCompositions)) {
            console.error("Set compositions is not an array:", setCompositions);
            return null;
          }
          return setCompositions.map((composition, index) => {
            const isAvailable = areAllLoftsInCompositionAvailable(composition);
            return /* @__PURE__ */ jsxRuntimeExports.jsx(
              "button",
              {
                onClick: () => {
                  if (isAvailable) {
                    onUserInteraction == null ? void 0 : onUserInteraction();
                    const lofts = getCompositionLofts(composition);
                    setSelectedLofts(lofts);
                    setSelectedComposition(composition);
                  }
                },
                className: `px-4 py-2 transition-colors border rounded-lg border-shadow-black ${selectedComposition === composition ? "bg-shadow-black text-white" : " hover:bg-grey-v3 border-shadow-black text-shadow-black"} ${!isAvailable ? "cursor-not-allowed !border-grey-v3  hover:bg-system-disabled bg-system-disabled text-grey-v3 opacity-50" : ""}`,
                disabled: !isAvailable,
                title: !isAvailable ? "Some lofts in this composition are not available" : "",
                children: composition
              },
              index
            );
          });
        } catch (error) {
          console.error("Error parsing set compositions:", error);
          return null;
        }
      })() })
    ] }),
    clubType !== "putters" && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mb-4 flex flex-col flex-wrap", children: [
      /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "p4 mb-2 text-shadow-black", children: clubType === "irons" && ((_c = selectedBaseHead == null ? void 0 : selectedBaseHead.list_of_set_compositions) == null ? void 0 : _c.value) ? labelIncludedItems || "Included" : labelClubSelection || "Club Selection" }),
      /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex flex-wrap gap-2", children: (() => {
        if (!selectedBaseHead) return null;
        const allPossibleLofts = getAllPossibleLofts();
        return allPossibleLofts.map((loft) => {
          const isAvailable = isLoftAvailable(loft, selectedBaseHead, selectedDexterity);
          const isSelected = selectedLofts.includes(loft);
          const canDeselect = canDeselectLoft(loft);
          const isDisabled = !isAvailable || isSelected && !canDeselect;
          return /* @__PURE__ */ jsxRuntimeExports.jsx(
            "button",
            {
              onClick: () => isAvailable && !isDisabled && handleLoftSelectionWithComposition(loft),
              disabled: isDisabled,
              className: `px-4 py-2 transition-colors border rounded-lg border-shadow-black ${isSelected ? "border-shadow-black bg-shadow-black text-fairway" : isAvailable ? "border-shadow-black bg-transparent text-shadow-black hover:bg-grey-v3" : "cursor-not-allowed border-grey-v3 bg-system-disabled text-grey-v3 opacity-50"}`,
              title: !isAvailable ? "This loft is not available" : "",
              children: formatLoftDisplay(loft)
            },
            loft
          );
        });
      })() })
    ] })
  ] });
};
/*! @license DOMPurify 3.2.4 | (c) Cure53 and other contributors | Released under the Apache license 2.0 and Mozilla Public License 2.0 | github.com/cure53/DOMPurify/blob/3.2.4/LICENSE */
const {
  entries,
  setPrototypeOf,
  isFrozen,
  getPrototypeOf,
  getOwnPropertyDescriptor
} = Object;
let {
  freeze,
  seal,
  create
} = Object;
let {
  apply,
  construct
} = typeof Reflect !== "undefined" && Reflect;
if (!freeze) {
  freeze = function freeze2(x) {
    return x;
  };
}
if (!seal) {
  seal = function seal2(x) {
    return x;
  };
}
if (!apply) {
  apply = function apply2(fun, thisValue, args) {
    return fun.apply(thisValue, args);
  };
}
if (!construct) {
  construct = function construct2(Func, args) {
    return new Func(...args);
  };
}
const arrayForEach = unapply(Array.prototype.forEach);
const arrayLastIndexOf = unapply(Array.prototype.lastIndexOf);
const arrayPop = unapply(Array.prototype.pop);
const arrayPush = unapply(Array.prototype.push);
const arraySplice = unapply(Array.prototype.splice);
const stringToLowerCase = unapply(String.prototype.toLowerCase);
const stringToString = unapply(String.prototype.toString);
const stringMatch = unapply(String.prototype.match);
const stringReplace = unapply(String.prototype.replace);
const stringIndexOf = unapply(String.prototype.indexOf);
const stringTrim = unapply(String.prototype.trim);
const objectHasOwnProperty = unapply(Object.prototype.hasOwnProperty);
const regExpTest = unapply(RegExp.prototype.test);
const typeErrorCreate = unconstruct(TypeError);
function unapply(func) {
  return function(thisArg) {
    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }
    return apply(func, thisArg, args);
  };
}
function unconstruct(func) {
  return function() {
    for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      args[_key2] = arguments[_key2];
    }
    return construct(func, args);
  };
}
function addToSet(set, array) {
  let transformCaseFunc = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : stringToLowerCase;
  if (setPrototypeOf) {
    setPrototypeOf(set, null);
  }
  let l = array.length;
  while (l--) {
    let element = array[l];
    if (typeof element === "string") {
      const lcElement = transformCaseFunc(element);
      if (lcElement !== element) {
        if (!isFrozen(array)) {
          array[l] = lcElement;
        }
        element = lcElement;
      }
    }
    set[element] = true;
  }
  return set;
}
function cleanArray(array) {
  for (let index = 0; index < array.length; index++) {
    const isPropertyExist = objectHasOwnProperty(array, index);
    if (!isPropertyExist) {
      array[index] = null;
    }
  }
  return array;
}
function clone(object) {
  const newObject = create(null);
  for (const [property, value] of entries(object)) {
    const isPropertyExist = objectHasOwnProperty(object, property);
    if (isPropertyExist) {
      if (Array.isArray(value)) {
        newObject[property] = cleanArray(value);
      } else if (value && typeof value === "object" && value.constructor === Object) {
        newObject[property] = clone(value);
      } else {
        newObject[property] = value;
      }
    }
  }
  return newObject;
}
function lookupGetter(object, prop) {
  while (object !== null) {
    const desc = getOwnPropertyDescriptor(object, prop);
    if (desc) {
      if (desc.get) {
        return unapply(desc.get);
      }
      if (typeof desc.value === "function") {
        return unapply(desc.value);
      }
    }
    object = getPrototypeOf(object);
  }
  function fallbackValue() {
    return null;
  }
  return fallbackValue;
}
const html$1 = freeze(["a", "abbr", "acronym", "address", "area", "article", "aside", "audio", "b", "bdi", "bdo", "big", "blink", "blockquote", "body", "br", "button", "canvas", "caption", "center", "cite", "code", "col", "colgroup", "content", "data", "datalist", "dd", "decorator", "del", "details", "dfn", "dialog", "dir", "div", "dl", "dt", "element", "em", "fieldset", "figcaption", "figure", "font", "footer", "form", "h1", "h2", "h3", "h4", "h5", "h6", "head", "header", "hgroup", "hr", "html", "i", "img", "input", "ins", "kbd", "label", "legend", "li", "main", "map", "mark", "marquee", "menu", "menuitem", "meter", "nav", "nobr", "ol", "optgroup", "option", "output", "p", "picture", "pre", "progress", "q", "rp", "rt", "ruby", "s", "samp", "section", "select", "shadow", "small", "source", "spacer", "span", "strike", "strong", "style", "sub", "summary", "sup", "table", "tbody", "td", "template", "textarea", "tfoot", "th", "thead", "time", "tr", "track", "tt", "u", "ul", "var", "video", "wbr"]);
const svg$1 = freeze(["svg", "a", "altglyph", "altglyphdef", "altglyphitem", "animatecolor", "animatemotion", "animatetransform", "circle", "clippath", "defs", "desc", "ellipse", "filter", "font", "g", "glyph", "glyphref", "hkern", "image", "line", "lineargradient", "marker", "mask", "metadata", "mpath", "path", "pattern", "polygon", "polyline", "radialgradient", "rect", "stop", "style", "switch", "symbol", "text", "textpath", "title", "tref", "tspan", "view", "vkern"]);
const svgFilters = freeze(["feBlend", "feColorMatrix", "feComponentTransfer", "feComposite", "feConvolveMatrix", "feDiffuseLighting", "feDisplacementMap", "feDistantLight", "feDropShadow", "feFlood", "feFuncA", "feFuncB", "feFuncG", "feFuncR", "feGaussianBlur", "feImage", "feMerge", "feMergeNode", "feMorphology", "feOffset", "fePointLight", "feSpecularLighting", "feSpotLight", "feTile", "feTurbulence"]);
const svgDisallowed = freeze(["animate", "color-profile", "cursor", "discard", "font-face", "font-face-format", "font-face-name", "font-face-src", "font-face-uri", "foreignobject", "hatch", "hatchpath", "mesh", "meshgradient", "meshpatch", "meshrow", "missing-glyph", "script", "set", "solidcolor", "unknown", "use"]);
const mathMl$1 = freeze(["math", "menclose", "merror", "mfenced", "mfrac", "mglyph", "mi", "mlabeledtr", "mmultiscripts", "mn", "mo", "mover", "mpadded", "mphantom", "mroot", "mrow", "ms", "mspace", "msqrt", "mstyle", "msub", "msup", "msubsup", "mtable", "mtd", "mtext", "mtr", "munder", "munderover", "mprescripts"]);
const mathMlDisallowed = freeze(["maction", "maligngroup", "malignmark", "mlongdiv", "mscarries", "mscarry", "msgroup", "mstack", "msline", "msrow", "semantics", "annotation", "annotation-xml", "mprescripts", "none"]);
const text = freeze(["#text"]);
const html = freeze(["accept", "action", "align", "alt", "autocapitalize", "autocomplete", "autopictureinpicture", "autoplay", "background", "bgcolor", "border", "capture", "cellpadding", "cellspacing", "checked", "cite", "class", "clear", "color", "cols", "colspan", "controls", "controlslist", "coords", "crossorigin", "datetime", "decoding", "default", "dir", "disabled", "disablepictureinpicture", "disableremoteplayback", "download", "draggable", "enctype", "enterkeyhint", "face", "for", "headers", "height", "hidden", "high", "href", "hreflang", "id", "inputmode", "integrity", "ismap", "kind", "label", "lang", "list", "loading", "loop", "low", "max", "maxlength", "media", "method", "min", "minlength", "multiple", "muted", "name", "nonce", "noshade", "novalidate", "nowrap", "open", "optimum", "pattern", "placeholder", "playsinline", "popover", "popovertarget", "popovertargetaction", "poster", "preload", "pubdate", "radiogroup", "readonly", "rel", "required", "rev", "reversed", "role", "rows", "rowspan", "spellcheck", "scope", "selected", "shape", "size", "sizes", "span", "srclang", "start", "src", "srcset", "step", "style", "summary", "tabindex", "title", "translate", "type", "usemap", "valign", "value", "width", "wrap", "xmlns", "slot"]);
const svg = freeze(["accent-height", "accumulate", "additive", "alignment-baseline", "amplitude", "ascent", "attributename", "attributetype", "azimuth", "basefrequency", "baseline-shift", "begin", "bias", "by", "class", "clip", "clippathunits", "clip-path", "clip-rule", "color", "color-interpolation", "color-interpolation-filters", "color-profile", "color-rendering", "cx", "cy", "d", "dx", "dy", "diffuseconstant", "direction", "display", "divisor", "dur", "edgemode", "elevation", "end", "exponent", "fill", "fill-opacity", "fill-rule", "filter", "filterunits", "flood-color", "flood-opacity", "font-family", "font-size", "font-size-adjust", "font-stretch", "font-style", "font-variant", "font-weight", "fx", "fy", "g1", "g2", "glyph-name", "glyphref", "gradientunits", "gradienttransform", "height", "href", "id", "image-rendering", "in", "in2", "intercept", "k", "k1", "k2", "k3", "k4", "kerning", "keypoints", "keysplines", "keytimes", "lang", "lengthadjust", "letter-spacing", "kernelmatrix", "kernelunitlength", "lighting-color", "local", "marker-end", "marker-mid", "marker-start", "markerheight", "markerunits", "markerwidth", "maskcontentunits", "maskunits", "max", "mask", "media", "method", "mode", "min", "name", "numoctaves", "offset", "operator", "opacity", "order", "orient", "orientation", "origin", "overflow", "paint-order", "path", "pathlength", "patterncontentunits", "patterntransform", "patternunits", "points", "preservealpha", "preserveaspectratio", "primitiveunits", "r", "rx", "ry", "radius", "refx", "refy", "repeatcount", "repeatdur", "restart", "result", "rotate", "scale", "seed", "shape-rendering", "slope", "specularconstant", "specularexponent", "spreadmethod", "startoffset", "stddeviation", "stitchtiles", "stop-color", "stop-opacity", "stroke-dasharray", "stroke-dashoffset", "stroke-linecap", "stroke-linejoin", "stroke-miterlimit", "stroke-opacity", "stroke", "stroke-width", "style", "surfacescale", "systemlanguage", "tabindex", "tablevalues", "targetx", "targety", "transform", "transform-origin", "text-anchor", "text-decoration", "text-rendering", "textlength", "type", "u1", "u2", "unicode", "values", "viewbox", "visibility", "version", "vert-adv-y", "vert-origin-x", "vert-origin-y", "width", "word-spacing", "wrap", "writing-mode", "xchannelselector", "ychannelselector", "x", "x1", "x2", "xmlns", "y", "y1", "y2", "z", "zoomandpan"]);
const mathMl = freeze(["accent", "accentunder", "align", "bevelled", "close", "columnsalign", "columnlines", "columnspan", "denomalign", "depth", "dir", "display", "displaystyle", "encoding", "fence", "frame", "height", "href", "id", "largeop", "length", "linethickness", "lspace", "lquote", "mathbackground", "mathcolor", "mathsize", "mathvariant", "maxsize", "minsize", "movablelimits", "notation", "numalign", "open", "rowalign", "rowlines", "rowspacing", "rowspan", "rspace", "rquote", "scriptlevel", "scriptminsize", "scriptsizemultiplier", "selection", "separator", "separators", "stretchy", "subscriptshift", "supscriptshift", "symmetric", "voffset", "width", "xmlns"]);
const xml = freeze(["xlink:href", "xml:id", "xlink:title", "xml:space", "xmlns:xlink"]);
const MUSTACHE_EXPR = seal(/\{\{[\w\W]*|[\w\W]*\}\}/gm);
const ERB_EXPR = seal(/<%[\w\W]*|[\w\W]*%>/gm);
const TMPLIT_EXPR = seal(/\$\{[\w\W]*/gm);
const DATA_ATTR = seal(/^data-[\-\w.\u00B7-\uFFFF]+$/);
const ARIA_ATTR = seal(/^aria-[\-\w]+$/);
const IS_ALLOWED_URI = seal(
  /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i
  // eslint-disable-line no-useless-escape
);
const IS_SCRIPT_OR_DATA = seal(/^(?:\w+script|data):/i);
const ATTR_WHITESPACE = seal(
  /[\u0000-\u0020\u00A0\u1680\u180E\u2000-\u2029\u205F\u3000]/g
  // eslint-disable-line no-control-regex
);
const DOCTYPE_NAME = seal(/^html$/i);
const CUSTOM_ELEMENT = seal(/^[a-z][.\w]*(-[.\w]+)+$/i);
var EXPRESSIONS = /* @__PURE__ */ Object.freeze({
  __proto__: null,
  ARIA_ATTR,
  ATTR_WHITESPACE,
  CUSTOM_ELEMENT,
  DATA_ATTR,
  DOCTYPE_NAME,
  ERB_EXPR,
  IS_ALLOWED_URI,
  IS_SCRIPT_OR_DATA,
  MUSTACHE_EXPR,
  TMPLIT_EXPR
});
const NODE_TYPE = {
  element: 1,
  text: 3,
  // Deprecated
  progressingInstruction: 7,
  comment: 8,
  document: 9
};
const getGlobal = function getGlobal2() {
  return typeof window === "undefined" ? null : window;
};
const _createTrustedTypesPolicy = function _createTrustedTypesPolicy2(trustedTypes, purifyHostElement) {
  if (typeof trustedTypes !== "object" || typeof trustedTypes.createPolicy !== "function") {
    return null;
  }
  let suffix = null;
  const ATTR_NAME = "data-tt-policy-suffix";
  if (purifyHostElement && purifyHostElement.hasAttribute(ATTR_NAME)) {
    suffix = purifyHostElement.getAttribute(ATTR_NAME);
  }
  const policyName = "dompurify" + (suffix ? "#" + suffix : "");
  try {
    return trustedTypes.createPolicy(policyName, {
      createHTML(html2) {
        return html2;
      },
      createScriptURL(scriptUrl) {
        return scriptUrl;
      }
    });
  } catch (_) {
    console.warn("TrustedTypes policy " + policyName + " could not be created.");
    return null;
  }
};
const _createHooksMap = function _createHooksMap2() {
  return {
    afterSanitizeAttributes: [],
    afterSanitizeElements: [],
    afterSanitizeShadowDOM: [],
    beforeSanitizeAttributes: [],
    beforeSanitizeElements: [],
    beforeSanitizeShadowDOM: [],
    uponSanitizeAttribute: [],
    uponSanitizeElement: [],
    uponSanitizeShadowNode: []
  };
};
function createDOMPurify() {
  let window2 = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : getGlobal();
  const DOMPurify = (root) => createDOMPurify(root);
  DOMPurify.version = "3.2.4";
  DOMPurify.removed = [];
  if (!window2 || !window2.document || window2.document.nodeType !== NODE_TYPE.document || !window2.Element) {
    DOMPurify.isSupported = false;
    return DOMPurify;
  }
  let {
    document: document2
  } = window2;
  const originalDocument = document2;
  const currentScript = originalDocument.currentScript;
  const {
    DocumentFragment,
    HTMLTemplateElement,
    Node,
    Element,
    NodeFilter,
    NamedNodeMap = window2.NamedNodeMap || window2.MozNamedAttrMap,
    HTMLFormElement,
    DOMParser,
    trustedTypes
  } = window2;
  const ElementPrototype = Element.prototype;
  const cloneNode = lookupGetter(ElementPrototype, "cloneNode");
  const remove = lookupGetter(ElementPrototype, "remove");
  const getNextSibling = lookupGetter(ElementPrototype, "nextSibling");
  const getChildNodes = lookupGetter(ElementPrototype, "childNodes");
  const getParentNode = lookupGetter(ElementPrototype, "parentNode");
  if (typeof HTMLTemplateElement === "function") {
    const template = document2.createElement("template");
    if (template.content && template.content.ownerDocument) {
      document2 = template.content.ownerDocument;
    }
  }
  let trustedTypesPolicy;
  let emptyHTML = "";
  const {
    implementation,
    createNodeIterator,
    createDocumentFragment,
    getElementsByTagName
  } = document2;
  const {
    importNode
  } = originalDocument;
  let hooks = _createHooksMap();
  DOMPurify.isSupported = typeof entries === "function" && typeof getParentNode === "function" && implementation && implementation.createHTMLDocument !== void 0;
  const {
    MUSTACHE_EXPR: MUSTACHE_EXPR2,
    ERB_EXPR: ERB_EXPR2,
    TMPLIT_EXPR: TMPLIT_EXPR2,
    DATA_ATTR: DATA_ATTR2,
    ARIA_ATTR: ARIA_ATTR2,
    IS_SCRIPT_OR_DATA: IS_SCRIPT_OR_DATA2,
    ATTR_WHITESPACE: ATTR_WHITESPACE2,
    CUSTOM_ELEMENT: CUSTOM_ELEMENT2
  } = EXPRESSIONS;
  let {
    IS_ALLOWED_URI: IS_ALLOWED_URI$1
  } = EXPRESSIONS;
  let ALLOWED_TAGS = null;
  const DEFAULT_ALLOWED_TAGS = addToSet({}, [...html$1, ...svg$1, ...svgFilters, ...mathMl$1, ...text]);
  let ALLOWED_ATTR = null;
  const DEFAULT_ALLOWED_ATTR = addToSet({}, [...html, ...svg, ...mathMl, ...xml]);
  let CUSTOM_ELEMENT_HANDLING = Object.seal(create(null, {
    tagNameCheck: {
      writable: true,
      configurable: false,
      enumerable: true,
      value: null
    },
    attributeNameCheck: {
      writable: true,
      configurable: false,
      enumerable: true,
      value: null
    },
    allowCustomizedBuiltInElements: {
      writable: true,
      configurable: false,
      enumerable: true,
      value: false
    }
  }));
  let FORBID_TAGS = null;
  let FORBID_ATTR = null;
  let ALLOW_ARIA_ATTR = true;
  let ALLOW_DATA_ATTR = true;
  let ALLOW_UNKNOWN_PROTOCOLS = false;
  let ALLOW_SELF_CLOSE_IN_ATTR = true;
  let SAFE_FOR_TEMPLATES = false;
  let SAFE_FOR_XML = true;
  let WHOLE_DOCUMENT = false;
  let SET_CONFIG = false;
  let FORCE_BODY = false;
  let RETURN_DOM = false;
  let RETURN_DOM_FRAGMENT = false;
  let RETURN_TRUSTED_TYPE = false;
  let SANITIZE_DOM = true;
  let SANITIZE_NAMED_PROPS = false;
  const SANITIZE_NAMED_PROPS_PREFIX = "user-content-";
  let KEEP_CONTENT = true;
  let IN_PLACE = false;
  let USE_PROFILES = {};
  let FORBID_CONTENTS = null;
  const DEFAULT_FORBID_CONTENTS = addToSet({}, ["annotation-xml", "audio", "colgroup", "desc", "foreignobject", "head", "iframe", "math", "mi", "mn", "mo", "ms", "mtext", "noembed", "noframes", "noscript", "plaintext", "script", "style", "svg", "template", "thead", "title", "video", "xmp"]);
  let DATA_URI_TAGS = null;
  const DEFAULT_DATA_URI_TAGS = addToSet({}, ["audio", "video", "img", "source", "image", "track"]);
  let URI_SAFE_ATTRIBUTES = null;
  const DEFAULT_URI_SAFE_ATTRIBUTES = addToSet({}, ["alt", "class", "for", "id", "label", "name", "pattern", "placeholder", "role", "summary", "title", "value", "style", "xmlns"]);
  const MATHML_NAMESPACE = "http://www.w3.org/1998/Math/MathML";
  const SVG_NAMESPACE = "http://www.w3.org/2000/svg";
  const HTML_NAMESPACE = "http://www.w3.org/1999/xhtml";
  let NAMESPACE = HTML_NAMESPACE;
  let IS_EMPTY_INPUT = false;
  let ALLOWED_NAMESPACES = null;
  const DEFAULT_ALLOWED_NAMESPACES = addToSet({}, [MATHML_NAMESPACE, SVG_NAMESPACE, HTML_NAMESPACE], stringToString);
  let MATHML_TEXT_INTEGRATION_POINTS = addToSet({}, ["mi", "mo", "mn", "ms", "mtext"]);
  let HTML_INTEGRATION_POINTS = addToSet({}, ["annotation-xml"]);
  const COMMON_SVG_AND_HTML_ELEMENTS = addToSet({}, ["title", "style", "font", "a", "script"]);
  let PARSER_MEDIA_TYPE = null;
  const SUPPORTED_PARSER_MEDIA_TYPES = ["application/xhtml+xml", "text/html"];
  const DEFAULT_PARSER_MEDIA_TYPE = "text/html";
  let transformCaseFunc = null;
  let CONFIG = null;
  const formElement = document2.createElement("form");
  const isRegexOrFunction = function isRegexOrFunction2(testValue) {
    return testValue instanceof RegExp || testValue instanceof Function;
  };
  const _parseConfig = function _parseConfig2() {
    let cfg = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
    if (CONFIG && CONFIG === cfg) {
      return;
    }
    if (!cfg || typeof cfg !== "object") {
      cfg = {};
    }
    cfg = clone(cfg);
    PARSER_MEDIA_TYPE = // eslint-disable-next-line unicorn/prefer-includes
    SUPPORTED_PARSER_MEDIA_TYPES.indexOf(cfg.PARSER_MEDIA_TYPE) === -1 ? DEFAULT_PARSER_MEDIA_TYPE : cfg.PARSER_MEDIA_TYPE;
    transformCaseFunc = PARSER_MEDIA_TYPE === "application/xhtml+xml" ? stringToString : stringToLowerCase;
    ALLOWED_TAGS = objectHasOwnProperty(cfg, "ALLOWED_TAGS") ? addToSet({}, cfg.ALLOWED_TAGS, transformCaseFunc) : DEFAULT_ALLOWED_TAGS;
    ALLOWED_ATTR = objectHasOwnProperty(cfg, "ALLOWED_ATTR") ? addToSet({}, cfg.ALLOWED_ATTR, transformCaseFunc) : DEFAULT_ALLOWED_ATTR;
    ALLOWED_NAMESPACES = objectHasOwnProperty(cfg, "ALLOWED_NAMESPACES") ? addToSet({}, cfg.ALLOWED_NAMESPACES, stringToString) : DEFAULT_ALLOWED_NAMESPACES;
    URI_SAFE_ATTRIBUTES = objectHasOwnProperty(cfg, "ADD_URI_SAFE_ATTR") ? addToSet(clone(DEFAULT_URI_SAFE_ATTRIBUTES), cfg.ADD_URI_SAFE_ATTR, transformCaseFunc) : DEFAULT_URI_SAFE_ATTRIBUTES;
    DATA_URI_TAGS = objectHasOwnProperty(cfg, "ADD_DATA_URI_TAGS") ? addToSet(clone(DEFAULT_DATA_URI_TAGS), cfg.ADD_DATA_URI_TAGS, transformCaseFunc) : DEFAULT_DATA_URI_TAGS;
    FORBID_CONTENTS = objectHasOwnProperty(cfg, "FORBID_CONTENTS") ? addToSet({}, cfg.FORBID_CONTENTS, transformCaseFunc) : DEFAULT_FORBID_CONTENTS;
    FORBID_TAGS = objectHasOwnProperty(cfg, "FORBID_TAGS") ? addToSet({}, cfg.FORBID_TAGS, transformCaseFunc) : {};
    FORBID_ATTR = objectHasOwnProperty(cfg, "FORBID_ATTR") ? addToSet({}, cfg.FORBID_ATTR, transformCaseFunc) : {};
    USE_PROFILES = objectHasOwnProperty(cfg, "USE_PROFILES") ? cfg.USE_PROFILES : false;
    ALLOW_ARIA_ATTR = cfg.ALLOW_ARIA_ATTR !== false;
    ALLOW_DATA_ATTR = cfg.ALLOW_DATA_ATTR !== false;
    ALLOW_UNKNOWN_PROTOCOLS = cfg.ALLOW_UNKNOWN_PROTOCOLS || false;
    ALLOW_SELF_CLOSE_IN_ATTR = cfg.ALLOW_SELF_CLOSE_IN_ATTR !== false;
    SAFE_FOR_TEMPLATES = cfg.SAFE_FOR_TEMPLATES || false;
    SAFE_FOR_XML = cfg.SAFE_FOR_XML !== false;
    WHOLE_DOCUMENT = cfg.WHOLE_DOCUMENT || false;
    RETURN_DOM = cfg.RETURN_DOM || false;
    RETURN_DOM_FRAGMENT = cfg.RETURN_DOM_FRAGMENT || false;
    RETURN_TRUSTED_TYPE = cfg.RETURN_TRUSTED_TYPE || false;
    FORCE_BODY = cfg.FORCE_BODY || false;
    SANITIZE_DOM = cfg.SANITIZE_DOM !== false;
    SANITIZE_NAMED_PROPS = cfg.SANITIZE_NAMED_PROPS || false;
    KEEP_CONTENT = cfg.KEEP_CONTENT !== false;
    IN_PLACE = cfg.IN_PLACE || false;
    IS_ALLOWED_URI$1 = cfg.ALLOWED_URI_REGEXP || IS_ALLOWED_URI;
    NAMESPACE = cfg.NAMESPACE || HTML_NAMESPACE;
    MATHML_TEXT_INTEGRATION_POINTS = cfg.MATHML_TEXT_INTEGRATION_POINTS || MATHML_TEXT_INTEGRATION_POINTS;
    HTML_INTEGRATION_POINTS = cfg.HTML_INTEGRATION_POINTS || HTML_INTEGRATION_POINTS;
    CUSTOM_ELEMENT_HANDLING = cfg.CUSTOM_ELEMENT_HANDLING || {};
    if (cfg.CUSTOM_ELEMENT_HANDLING && isRegexOrFunction(cfg.CUSTOM_ELEMENT_HANDLING.tagNameCheck)) {
      CUSTOM_ELEMENT_HANDLING.tagNameCheck = cfg.CUSTOM_ELEMENT_HANDLING.tagNameCheck;
    }
    if (cfg.CUSTOM_ELEMENT_HANDLING && isRegexOrFunction(cfg.CUSTOM_ELEMENT_HANDLING.attributeNameCheck)) {
      CUSTOM_ELEMENT_HANDLING.attributeNameCheck = cfg.CUSTOM_ELEMENT_HANDLING.attributeNameCheck;
    }
    if (cfg.CUSTOM_ELEMENT_HANDLING && typeof cfg.CUSTOM_ELEMENT_HANDLING.allowCustomizedBuiltInElements === "boolean") {
      CUSTOM_ELEMENT_HANDLING.allowCustomizedBuiltInElements = cfg.CUSTOM_ELEMENT_HANDLING.allowCustomizedBuiltInElements;
    }
    if (SAFE_FOR_TEMPLATES) {
      ALLOW_DATA_ATTR = false;
    }
    if (RETURN_DOM_FRAGMENT) {
      RETURN_DOM = true;
    }
    if (USE_PROFILES) {
      ALLOWED_TAGS = addToSet({}, text);
      ALLOWED_ATTR = [];
      if (USE_PROFILES.html === true) {
        addToSet(ALLOWED_TAGS, html$1);
        addToSet(ALLOWED_ATTR, html);
      }
      if (USE_PROFILES.svg === true) {
        addToSet(ALLOWED_TAGS, svg$1);
        addToSet(ALLOWED_ATTR, svg);
        addToSet(ALLOWED_ATTR, xml);
      }
      if (USE_PROFILES.svgFilters === true) {
        addToSet(ALLOWED_TAGS, svgFilters);
        addToSet(ALLOWED_ATTR, svg);
        addToSet(ALLOWED_ATTR, xml);
      }
      if (USE_PROFILES.mathMl === true) {
        addToSet(ALLOWED_TAGS, mathMl$1);
        addToSet(ALLOWED_ATTR, mathMl);
        addToSet(ALLOWED_ATTR, xml);
      }
    }
    if (cfg.ADD_TAGS) {
      if (ALLOWED_TAGS === DEFAULT_ALLOWED_TAGS) {
        ALLOWED_TAGS = clone(ALLOWED_TAGS);
      }
      addToSet(ALLOWED_TAGS, cfg.ADD_TAGS, transformCaseFunc);
    }
    if (cfg.ADD_ATTR) {
      if (ALLOWED_ATTR === DEFAULT_ALLOWED_ATTR) {
        ALLOWED_ATTR = clone(ALLOWED_ATTR);
      }
      addToSet(ALLOWED_ATTR, cfg.ADD_ATTR, transformCaseFunc);
    }
    if (cfg.ADD_URI_SAFE_ATTR) {
      addToSet(URI_SAFE_ATTRIBUTES, cfg.ADD_URI_SAFE_ATTR, transformCaseFunc);
    }
    if (cfg.FORBID_CONTENTS) {
      if (FORBID_CONTENTS === DEFAULT_FORBID_CONTENTS) {
        FORBID_CONTENTS = clone(FORBID_CONTENTS);
      }
      addToSet(FORBID_CONTENTS, cfg.FORBID_CONTENTS, transformCaseFunc);
    }
    if (KEEP_CONTENT) {
      ALLOWED_TAGS["#text"] = true;
    }
    if (WHOLE_DOCUMENT) {
      addToSet(ALLOWED_TAGS, ["html", "head", "body"]);
    }
    if (ALLOWED_TAGS.table) {
      addToSet(ALLOWED_TAGS, ["tbody"]);
      delete FORBID_TAGS.tbody;
    }
    if (cfg.TRUSTED_TYPES_POLICY) {
      if (typeof cfg.TRUSTED_TYPES_POLICY.createHTML !== "function") {
        throw typeErrorCreate('TRUSTED_TYPES_POLICY configuration option must provide a "createHTML" hook.');
      }
      if (typeof cfg.TRUSTED_TYPES_POLICY.createScriptURL !== "function") {
        throw typeErrorCreate('TRUSTED_TYPES_POLICY configuration option must provide a "createScriptURL" hook.');
      }
      trustedTypesPolicy = cfg.TRUSTED_TYPES_POLICY;
      emptyHTML = trustedTypesPolicy.createHTML("");
    } else {
      if (trustedTypesPolicy === void 0) {
        trustedTypesPolicy = _createTrustedTypesPolicy(trustedTypes, currentScript);
      }
      if (trustedTypesPolicy !== null && typeof emptyHTML === "string") {
        emptyHTML = trustedTypesPolicy.createHTML("");
      }
    }
    if (freeze) {
      freeze(cfg);
    }
    CONFIG = cfg;
  };
  const ALL_SVG_TAGS = addToSet({}, [...svg$1, ...svgFilters, ...svgDisallowed]);
  const ALL_MATHML_TAGS = addToSet({}, [...mathMl$1, ...mathMlDisallowed]);
  const _checkValidNamespace = function _checkValidNamespace2(element) {
    let parent = getParentNode(element);
    if (!parent || !parent.tagName) {
      parent = {
        namespaceURI: NAMESPACE,
        tagName: "template"
      };
    }
    const tagName = stringToLowerCase(element.tagName);
    const parentTagName = stringToLowerCase(parent.tagName);
    if (!ALLOWED_NAMESPACES[element.namespaceURI]) {
      return false;
    }
    if (element.namespaceURI === SVG_NAMESPACE) {
      if (parent.namespaceURI === HTML_NAMESPACE) {
        return tagName === "svg";
      }
      if (parent.namespaceURI === MATHML_NAMESPACE) {
        return tagName === "svg" && (parentTagName === "annotation-xml" || MATHML_TEXT_INTEGRATION_POINTS[parentTagName]);
      }
      return Boolean(ALL_SVG_TAGS[tagName]);
    }
    if (element.namespaceURI === MATHML_NAMESPACE) {
      if (parent.namespaceURI === HTML_NAMESPACE) {
        return tagName === "math";
      }
      if (parent.namespaceURI === SVG_NAMESPACE) {
        return tagName === "math" && HTML_INTEGRATION_POINTS[parentTagName];
      }
      return Boolean(ALL_MATHML_TAGS[tagName]);
    }
    if (element.namespaceURI === HTML_NAMESPACE) {
      if (parent.namespaceURI === SVG_NAMESPACE && !HTML_INTEGRATION_POINTS[parentTagName]) {
        return false;
      }
      if (parent.namespaceURI === MATHML_NAMESPACE && !MATHML_TEXT_INTEGRATION_POINTS[parentTagName]) {
        return false;
      }
      return !ALL_MATHML_TAGS[tagName] && (COMMON_SVG_AND_HTML_ELEMENTS[tagName] || !ALL_SVG_TAGS[tagName]);
    }
    if (PARSER_MEDIA_TYPE === "application/xhtml+xml" && ALLOWED_NAMESPACES[element.namespaceURI]) {
      return true;
    }
    return false;
  };
  const _forceRemove = function _forceRemove2(node) {
    arrayPush(DOMPurify.removed, {
      element: node
    });
    try {
      getParentNode(node).removeChild(node);
    } catch (_) {
      remove(node);
    }
  };
  const _removeAttribute = function _removeAttribute2(name, element) {
    try {
      arrayPush(DOMPurify.removed, {
        attribute: element.getAttributeNode(name),
        from: element
      });
    } catch (_) {
      arrayPush(DOMPurify.removed, {
        attribute: null,
        from: element
      });
    }
    element.removeAttribute(name);
    if (name === "is") {
      if (RETURN_DOM || RETURN_DOM_FRAGMENT) {
        try {
          _forceRemove(element);
        } catch (_) {
        }
      } else {
        try {
          element.setAttribute(name, "");
        } catch (_) {
        }
      }
    }
  };
  const _initDocument = function _initDocument2(dirty) {
    let doc = null;
    let leadingWhitespace = null;
    if (FORCE_BODY) {
      dirty = "<remove></remove>" + dirty;
    } else {
      const matches = stringMatch(dirty, /^[\r\n\t ]+/);
      leadingWhitespace = matches && matches[0];
    }
    if (PARSER_MEDIA_TYPE === "application/xhtml+xml" && NAMESPACE === HTML_NAMESPACE) {
      dirty = '<html xmlns="http://www.w3.org/1999/xhtml"><head></head><body>' + dirty + "</body></html>";
    }
    const dirtyPayload = trustedTypesPolicy ? trustedTypesPolicy.createHTML(dirty) : dirty;
    if (NAMESPACE === HTML_NAMESPACE) {
      try {
        doc = new DOMParser().parseFromString(dirtyPayload, PARSER_MEDIA_TYPE);
      } catch (_) {
      }
    }
    if (!doc || !doc.documentElement) {
      doc = implementation.createDocument(NAMESPACE, "template", null);
      try {
        doc.documentElement.innerHTML = IS_EMPTY_INPUT ? emptyHTML : dirtyPayload;
      } catch (_) {
      }
    }
    const body = doc.body || doc.documentElement;
    if (dirty && leadingWhitespace) {
      body.insertBefore(document2.createTextNode(leadingWhitespace), body.childNodes[0] || null);
    }
    if (NAMESPACE === HTML_NAMESPACE) {
      return getElementsByTagName.call(doc, WHOLE_DOCUMENT ? "html" : "body")[0];
    }
    return WHOLE_DOCUMENT ? doc.documentElement : body;
  };
  const _createNodeIterator = function _createNodeIterator2(root) {
    return createNodeIterator.call(
      root.ownerDocument || root,
      root,
      // eslint-disable-next-line no-bitwise
      NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_COMMENT | NodeFilter.SHOW_TEXT | NodeFilter.SHOW_PROCESSING_INSTRUCTION | NodeFilter.SHOW_CDATA_SECTION,
      null
    );
  };
  const _isClobbered = function _isClobbered2(element) {
    return element instanceof HTMLFormElement && (typeof element.nodeName !== "string" || typeof element.textContent !== "string" || typeof element.removeChild !== "function" || !(element.attributes instanceof NamedNodeMap) || typeof element.removeAttribute !== "function" || typeof element.setAttribute !== "function" || typeof element.namespaceURI !== "string" || typeof element.insertBefore !== "function" || typeof element.hasChildNodes !== "function");
  };
  const _isNode = function _isNode2(value) {
    return typeof Node === "function" && value instanceof Node;
  };
  function _executeHooks(hooks2, currentNode, data) {
    arrayForEach(hooks2, (hook) => {
      hook.call(DOMPurify, currentNode, data, CONFIG);
    });
  }
  const _sanitizeElements = function _sanitizeElements2(currentNode) {
    let content = null;
    _executeHooks(hooks.beforeSanitizeElements, currentNode, null);
    if (_isClobbered(currentNode)) {
      _forceRemove(currentNode);
      return true;
    }
    const tagName = transformCaseFunc(currentNode.nodeName);
    _executeHooks(hooks.uponSanitizeElement, currentNode, {
      tagName,
      allowedTags: ALLOWED_TAGS
    });
    if (currentNode.hasChildNodes() && !_isNode(currentNode.firstElementChild) && regExpTest(/<[/\w]/g, currentNode.innerHTML) && regExpTest(/<[/\w]/g, currentNode.textContent)) {
      _forceRemove(currentNode);
      return true;
    }
    if (currentNode.nodeType === NODE_TYPE.progressingInstruction) {
      _forceRemove(currentNode);
      return true;
    }
    if (SAFE_FOR_XML && currentNode.nodeType === NODE_TYPE.comment && regExpTest(/<[/\w]/g, currentNode.data)) {
      _forceRemove(currentNode);
      return true;
    }
    if (!ALLOWED_TAGS[tagName] || FORBID_TAGS[tagName]) {
      if (!FORBID_TAGS[tagName] && _isBasicCustomElement(tagName)) {
        if (CUSTOM_ELEMENT_HANDLING.tagNameCheck instanceof RegExp && regExpTest(CUSTOM_ELEMENT_HANDLING.tagNameCheck, tagName)) {
          return false;
        }
        if (CUSTOM_ELEMENT_HANDLING.tagNameCheck instanceof Function && CUSTOM_ELEMENT_HANDLING.tagNameCheck(tagName)) {
          return false;
        }
      }
      if (KEEP_CONTENT && !FORBID_CONTENTS[tagName]) {
        const parentNode = getParentNode(currentNode) || currentNode.parentNode;
        const childNodes = getChildNodes(currentNode) || currentNode.childNodes;
        if (childNodes && parentNode) {
          const childCount = childNodes.length;
          for (let i = childCount - 1; i >= 0; --i) {
            const childClone = cloneNode(childNodes[i], true);
            childClone.__removalCount = (currentNode.__removalCount || 0) + 1;
            parentNode.insertBefore(childClone, getNextSibling(currentNode));
          }
        }
      }
      _forceRemove(currentNode);
      return true;
    }
    if (currentNode instanceof Element && !_checkValidNamespace(currentNode)) {
      _forceRemove(currentNode);
      return true;
    }
    if ((tagName === "noscript" || tagName === "noembed" || tagName === "noframes") && regExpTest(/<\/no(script|embed|frames)/i, currentNode.innerHTML)) {
      _forceRemove(currentNode);
      return true;
    }
    if (SAFE_FOR_TEMPLATES && currentNode.nodeType === NODE_TYPE.text) {
      content = currentNode.textContent;
      arrayForEach([MUSTACHE_EXPR2, ERB_EXPR2, TMPLIT_EXPR2], (expr) => {
        content = stringReplace(content, expr, " ");
      });
      if (currentNode.textContent !== content) {
        arrayPush(DOMPurify.removed, {
          element: currentNode.cloneNode()
        });
        currentNode.textContent = content;
      }
    }
    _executeHooks(hooks.afterSanitizeElements, currentNode, null);
    return false;
  };
  const _isValidAttribute = function _isValidAttribute2(lcTag, lcName, value) {
    if (SANITIZE_DOM && (lcName === "id" || lcName === "name") && (value in document2 || value in formElement)) {
      return false;
    }
    if (ALLOW_DATA_ATTR && !FORBID_ATTR[lcName] && regExpTest(DATA_ATTR2, lcName)) ;
    else if (ALLOW_ARIA_ATTR && regExpTest(ARIA_ATTR2, lcName)) ;
    else if (!ALLOWED_ATTR[lcName] || FORBID_ATTR[lcName]) {
      if (
        // First condition does a very basic check if a) it's basically a valid custom element tagname AND
        // b) if the tagName passes whatever the user has configured for CUSTOM_ELEMENT_HANDLING.tagNameCheck
        // and c) if the attribute name passes whatever the user has configured for CUSTOM_ELEMENT_HANDLING.attributeNameCheck
        _isBasicCustomElement(lcTag) && (CUSTOM_ELEMENT_HANDLING.tagNameCheck instanceof RegExp && regExpTest(CUSTOM_ELEMENT_HANDLING.tagNameCheck, lcTag) || CUSTOM_ELEMENT_HANDLING.tagNameCheck instanceof Function && CUSTOM_ELEMENT_HANDLING.tagNameCheck(lcTag)) && (CUSTOM_ELEMENT_HANDLING.attributeNameCheck instanceof RegExp && regExpTest(CUSTOM_ELEMENT_HANDLING.attributeNameCheck, lcName) || CUSTOM_ELEMENT_HANDLING.attributeNameCheck instanceof Function && CUSTOM_ELEMENT_HANDLING.attributeNameCheck(lcName)) || // Alternative, second condition checks if it's an `is`-attribute, AND
        // the value passes whatever the user has configured for CUSTOM_ELEMENT_HANDLING.tagNameCheck
        lcName === "is" && CUSTOM_ELEMENT_HANDLING.allowCustomizedBuiltInElements && (CUSTOM_ELEMENT_HANDLING.tagNameCheck instanceof RegExp && regExpTest(CUSTOM_ELEMENT_HANDLING.tagNameCheck, value) || CUSTOM_ELEMENT_HANDLING.tagNameCheck instanceof Function && CUSTOM_ELEMENT_HANDLING.tagNameCheck(value))
      ) ;
      else {
        return false;
      }
    } else if (URI_SAFE_ATTRIBUTES[lcName]) ;
    else if (regExpTest(IS_ALLOWED_URI$1, stringReplace(value, ATTR_WHITESPACE2, ""))) ;
    else if ((lcName === "src" || lcName === "xlink:href" || lcName === "href") && lcTag !== "script" && stringIndexOf(value, "data:") === 0 && DATA_URI_TAGS[lcTag]) ;
    else if (ALLOW_UNKNOWN_PROTOCOLS && !regExpTest(IS_SCRIPT_OR_DATA2, stringReplace(value, ATTR_WHITESPACE2, ""))) ;
    else if (value) {
      return false;
    } else ;
    return true;
  };
  const _isBasicCustomElement = function _isBasicCustomElement2(tagName) {
    return tagName !== "annotation-xml" && stringMatch(tagName, CUSTOM_ELEMENT2);
  };
  const _sanitizeAttributes = function _sanitizeAttributes2(currentNode) {
    _executeHooks(hooks.beforeSanitizeAttributes, currentNode, null);
    const {
      attributes
    } = currentNode;
    if (!attributes || _isClobbered(currentNode)) {
      return;
    }
    const hookEvent = {
      attrName: "",
      attrValue: "",
      keepAttr: true,
      allowedAttributes: ALLOWED_ATTR,
      forceKeepAttr: void 0
    };
    let l = attributes.length;
    while (l--) {
      const attr = attributes[l];
      const {
        name,
        namespaceURI,
        value: attrValue
      } = attr;
      const lcName = transformCaseFunc(name);
      let value = name === "value" ? attrValue : stringTrim(attrValue);
      hookEvent.attrName = lcName;
      hookEvent.attrValue = value;
      hookEvent.keepAttr = true;
      hookEvent.forceKeepAttr = void 0;
      _executeHooks(hooks.uponSanitizeAttribute, currentNode, hookEvent);
      value = hookEvent.attrValue;
      if (SANITIZE_NAMED_PROPS && (lcName === "id" || lcName === "name")) {
        _removeAttribute(name, currentNode);
        value = SANITIZE_NAMED_PROPS_PREFIX + value;
      }
      if (SAFE_FOR_XML && regExpTest(/((--!?|])>)|<\/(style|title)/i, value)) {
        _removeAttribute(name, currentNode);
        continue;
      }
      if (hookEvent.forceKeepAttr) {
        continue;
      }
      _removeAttribute(name, currentNode);
      if (!hookEvent.keepAttr) {
        continue;
      }
      if (!ALLOW_SELF_CLOSE_IN_ATTR && regExpTest(/\/>/i, value)) {
        _removeAttribute(name, currentNode);
        continue;
      }
      if (SAFE_FOR_TEMPLATES) {
        arrayForEach([MUSTACHE_EXPR2, ERB_EXPR2, TMPLIT_EXPR2], (expr) => {
          value = stringReplace(value, expr, " ");
        });
      }
      const lcTag = transformCaseFunc(currentNode.nodeName);
      if (!_isValidAttribute(lcTag, lcName, value)) {
        continue;
      }
      if (trustedTypesPolicy && typeof trustedTypes === "object" && typeof trustedTypes.getAttributeType === "function") {
        if (namespaceURI) ;
        else {
          switch (trustedTypes.getAttributeType(lcTag, lcName)) {
            case "TrustedHTML": {
              value = trustedTypesPolicy.createHTML(value);
              break;
            }
            case "TrustedScriptURL": {
              value = trustedTypesPolicy.createScriptURL(value);
              break;
            }
          }
        }
      }
      try {
        if (namespaceURI) {
          currentNode.setAttributeNS(namespaceURI, name, value);
        } else {
          currentNode.setAttribute(name, value);
        }
        if (_isClobbered(currentNode)) {
          _forceRemove(currentNode);
        } else {
          arrayPop(DOMPurify.removed);
        }
      } catch (_) {
      }
    }
    _executeHooks(hooks.afterSanitizeAttributes, currentNode, null);
  };
  const _sanitizeShadowDOM = function _sanitizeShadowDOM2(fragment) {
    let shadowNode = null;
    const shadowIterator = _createNodeIterator(fragment);
    _executeHooks(hooks.beforeSanitizeShadowDOM, fragment, null);
    while (shadowNode = shadowIterator.nextNode()) {
      _executeHooks(hooks.uponSanitizeShadowNode, shadowNode, null);
      _sanitizeElements(shadowNode);
      _sanitizeAttributes(shadowNode);
      if (shadowNode.content instanceof DocumentFragment) {
        _sanitizeShadowDOM2(shadowNode.content);
      }
    }
    _executeHooks(hooks.afterSanitizeShadowDOM, fragment, null);
  };
  DOMPurify.sanitize = function(dirty) {
    let cfg = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
    let body = null;
    let importedNode = null;
    let currentNode = null;
    let returnNode = null;
    IS_EMPTY_INPUT = !dirty;
    if (IS_EMPTY_INPUT) {
      dirty = "<!-->";
    }
    if (typeof dirty !== "string" && !_isNode(dirty)) {
      if (typeof dirty.toString === "function") {
        dirty = dirty.toString();
        if (typeof dirty !== "string") {
          throw typeErrorCreate("dirty is not a string, aborting");
        }
      } else {
        throw typeErrorCreate("toString is not a function");
      }
    }
    if (!DOMPurify.isSupported) {
      return dirty;
    }
    if (!SET_CONFIG) {
      _parseConfig(cfg);
    }
    DOMPurify.removed = [];
    if (typeof dirty === "string") {
      IN_PLACE = false;
    }
    if (IN_PLACE) {
      if (dirty.nodeName) {
        const tagName = transformCaseFunc(dirty.nodeName);
        if (!ALLOWED_TAGS[tagName] || FORBID_TAGS[tagName]) {
          throw typeErrorCreate("root node is forbidden and cannot be sanitized in-place");
        }
      }
    } else if (dirty instanceof Node) {
      body = _initDocument("<!---->");
      importedNode = body.ownerDocument.importNode(dirty, true);
      if (importedNode.nodeType === NODE_TYPE.element && importedNode.nodeName === "BODY") {
        body = importedNode;
      } else if (importedNode.nodeName === "HTML") {
        body = importedNode;
      } else {
        body.appendChild(importedNode);
      }
    } else {
      if (!RETURN_DOM && !SAFE_FOR_TEMPLATES && !WHOLE_DOCUMENT && // eslint-disable-next-line unicorn/prefer-includes
      dirty.indexOf("<") === -1) {
        return trustedTypesPolicy && RETURN_TRUSTED_TYPE ? trustedTypesPolicy.createHTML(dirty) : dirty;
      }
      body = _initDocument(dirty);
      if (!body) {
        return RETURN_DOM ? null : RETURN_TRUSTED_TYPE ? emptyHTML : "";
      }
    }
    if (body && FORCE_BODY) {
      _forceRemove(body.firstChild);
    }
    const nodeIterator = _createNodeIterator(IN_PLACE ? dirty : body);
    while (currentNode = nodeIterator.nextNode()) {
      _sanitizeElements(currentNode);
      _sanitizeAttributes(currentNode);
      if (currentNode.content instanceof DocumentFragment) {
        _sanitizeShadowDOM(currentNode.content);
      }
    }
    if (IN_PLACE) {
      return dirty;
    }
    if (RETURN_DOM) {
      if (RETURN_DOM_FRAGMENT) {
        returnNode = createDocumentFragment.call(body.ownerDocument);
        while (body.firstChild) {
          returnNode.appendChild(body.firstChild);
        }
      } else {
        returnNode = body;
      }
      if (ALLOWED_ATTR.shadowroot || ALLOWED_ATTR.shadowrootmode) {
        returnNode = importNode.call(originalDocument, returnNode, true);
      }
      return returnNode;
    }
    let serializedHTML = WHOLE_DOCUMENT ? body.outerHTML : body.innerHTML;
    if (WHOLE_DOCUMENT && ALLOWED_TAGS["!doctype"] && body.ownerDocument && body.ownerDocument.doctype && body.ownerDocument.doctype.name && regExpTest(DOCTYPE_NAME, body.ownerDocument.doctype.name)) {
      serializedHTML = "<!DOCTYPE " + body.ownerDocument.doctype.name + ">\n" + serializedHTML;
    }
    if (SAFE_FOR_TEMPLATES) {
      arrayForEach([MUSTACHE_EXPR2, ERB_EXPR2, TMPLIT_EXPR2], (expr) => {
        serializedHTML = stringReplace(serializedHTML, expr, " ");
      });
    }
    return trustedTypesPolicy && RETURN_TRUSTED_TYPE ? trustedTypesPolicy.createHTML(serializedHTML) : serializedHTML;
  };
  DOMPurify.setConfig = function() {
    let cfg = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
    _parseConfig(cfg);
    SET_CONFIG = true;
  };
  DOMPurify.clearConfig = function() {
    CONFIG = null;
    SET_CONFIG = false;
  };
  DOMPurify.isValidAttribute = function(tag, attr, value) {
    if (!CONFIG) {
      _parseConfig({});
    }
    const lcTag = transformCaseFunc(tag);
    const lcName = transformCaseFunc(attr);
    return _isValidAttribute(lcTag, lcName, value);
  };
  DOMPurify.addHook = function(entryPoint, hookFunction) {
    if (typeof hookFunction !== "function") {
      return;
    }
    arrayPush(hooks[entryPoint], hookFunction);
  };
  DOMPurify.removeHook = function(entryPoint, hookFunction) {
    if (hookFunction !== void 0) {
      const index = arrayLastIndexOf(hooks[entryPoint], hookFunction);
      return index === -1 ? void 0 : arraySplice(hooks[entryPoint], index, 1)[0];
    }
    return arrayPop(hooks[entryPoint]);
  };
  DOMPurify.removeHooks = function(entryPoint) {
    hooks[entryPoint] = [];
  };
  DOMPurify.removeAllHooks = function() {
    hooks = _createHooksMap();
  };
  return DOMPurify;
}
var purify = createDOMPurify();
const TooltipPopover = ({ isOpen, onClose, title, content, image }) => {
  const sanitizeConfig = {
    ALLOWED_TAGS: ["p", "h3", "strong", "ul", "li", "br"],
    ALLOWED_ATTR: []
    // No attributes needed for our use case
  };
  const sanitizedContent = purify.sanitize(content, sanitizeConfig);
  return /* @__PURE__ */ jsxRuntimeExports.jsxs(yt, { as: "div", className: "relative z-50", open: isOpen, onClose, children: [
    /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "fixed inset-0 bg-black/30", "aria-hidden": "true" }),
    /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "fixed inset-0 overflow-y-auto", children: /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex min-h-full items-center justify-center p-4", children: /* @__PURE__ */ jsxRuntimeExports.jsxs(yt.Panel, { className: "mx-auto max-w-[90%] rounded-lg bg-white p-6", children: [
      /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "relative mb-4", children: [
        /* @__PURE__ */ jsxRuntimeExports.jsx(yt.Title, { className: "h4 pr-8 text-shadow-black", children: title }),
        /* @__PURE__ */ jsxRuntimeExports.jsx(
          "button",
          {
            type: "button",
            onClick: onClose,
            className: "absolute right-0 top-0 rounded-full p-2 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-shadow-black focus:ring-offset-2",
            "aria-label": "Close dialog",
            children: /* @__PURE__ */ jsxRuntimeExports.jsx(
              "svg",
              {
                xmlns: "http://www.w3.org/2000/svg",
                width: "14",
                height: "14",
                viewBox: "0 0 14 14",
                fill: "none",
                "aria-hidden": "true",
                children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                  "path",
                  {
                    d: "M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z",
                    fill: "currentColor"
                  }
                )
              }
            )
          }
        )
      ] }),
      /* @__PURE__ */ jsxRuntimeExports.jsx(
        yt.Description,
        {
          className: "rte",
          dangerouslySetInnerHTML: { __html: sanitizedContent }
        }
      ),
      image && /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "mt-4 flex justify-center", children: /* @__PURE__ */ jsxRuntimeExports.jsx("img", { src: image, alt: title, className: "max-w-full" }) })
    ] }) }) })
  ] });
};
const LoftBuildSpecSelector = ({
  selectedBaseHead,
  loftBuildSpecs,
  clubLoftTranslationsValues,
  selectedLoftBuildSpec,
  setSelectedLoftBuildSpec,
  className = "",
  label,
  tooltipTitle,
  tooltipText,
  tooltipImage
}) => {
  const [isDialogOpen, setIsDialogOpen] = reactExports.useState(false);
  if (!selectedBaseHead || !loftBuildSpecs) return null;
  if (!(loftBuildSpecs == null ? void 0 : loftBuildSpecs.length)) return null;
  const displayText = (loftBuildSpec) => {
    return clubLoftTranslationsValues && clubLoftTranslationsValues[loftBuildSpec] ? clubLoftTranslationsValues[loftBuildSpec] : loftBuildSpec;
  };
  return /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: `mb-4 flex flex-col flex-wrap ${className}`, children: [
    /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "justify-left mb-2 flex items-center gap-2", children: [
      /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "p4 text-shadow-black", children: label || "Club Loft" }),
      (tooltipText || tooltipImage) && /* @__PURE__ */ jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [
        /* @__PURE__ */ jsxRuntimeExports.jsx(
          "button",
          {
            type: "button",
            onClick: () => setIsDialogOpen(true),
            className: "flex h-5 w-5 cursor-pointer items-center justify-center rounded-full border border-shadow-black text-shadow-black hover:bg-shadow-black hover:text-fairway focus:outline-none focus:ring-2 focus:ring-shadow-black focus:ring-offset-2",
            "aria-label": "About Club Loft",
            children: /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "p4", children: "i" })
          }
        ),
        /* @__PURE__ */ jsxRuntimeExports.jsx(
          TooltipPopover,
          {
            isOpen: isDialogOpen,
            onClose: () => setIsDialogOpen(false),
            title: tooltipTitle,
            content: tooltipText,
            image: tooltipImage
          }
        )
      ] })
    ] }),
    /* @__PURE__ */ jsxRuntimeExports.jsx(Mo, { value: selectedLoftBuildSpec, onChange: setSelectedLoftBuildSpec, children: /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "relative", children: [
      /* @__PURE__ */ jsxRuntimeExports.jsxs(Mo.Button, { className: "relative w-full cursor-pointer rounded border bg-white py-2 pl-3 pr-10 text-left focus:outline-none focus:ring-2 focus:ring-shadow-black sm:text-sm", children: [
        /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "block truncate", children: selectedLoftBuildSpec ? displayText(selectedLoftBuildSpec) : "Select Loft" }),
        /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
          "svg",
          {
            className: "h-5 w-5 text-gray-400",
            xmlns: "http://www.w3.org/2000/svg",
            viewBox: "0 0 20 20",
            fill: "currentColor",
            "aria-hidden": "true",
            children: /* @__PURE__ */ jsxRuntimeExports.jsx(
              "path",
              {
                fillRule: "evenodd",
                d: "M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z",
                clipRule: "evenodd"
              }
            )
          }
        ) })
      ] }),
      /* @__PURE__ */ jsxRuntimeExports.jsx(Mo.Options, { className: "absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm", children: loftBuildSpecs.map((loftBuildSpec) => /* @__PURE__ */ jsxRuntimeExports.jsx(
        Mo.Option,
        {
          className: ({ active }) => `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active ? "bg-shadow-black text-fairway" : "text-gray-900"}`,
          value: loftBuildSpec,
          children: ({ selected, active }) => /* @__PURE__ */ jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [
            /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: `block truncate ${selected ? "font-medium" : "font-normal"}`, children: displayText(loftBuildSpec) }),
            selected ? /* @__PURE__ */ jsxRuntimeExports.jsx(
              "span",
              {
                className: `absolute inset-y-0 left-0 flex items-center pl-3 ${active ? "text-fairway" : "text-shadow-black"}`,
                children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                  "svg",
                  {
                    xmlns: "http://www.w3.org/2000/svg",
                    className: "h-5 w-5",
                    viewBox: "0 0 20 20",
                    fill: "currentColor",
                    children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                      "path",
                      {
                        fillRule: "evenodd",
                        d: "M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z",
                        clipRule: "evenodd"
                      }
                    )
                  }
                )
              }
            ) : null
          ] })
        },
        loftBuildSpec
      )) })
    ] }) })
  ] });
};
const LieBuildSpecSelector = ({
  selectedBaseHead,
  lieBuildSpecs = [],
  clubLieTranslationsValues,
  selectedLieBuildSpec,
  setSelectedLieBuildSpec,
  className = "",
  label,
  tooltipTitle,
  tooltipText,
  tooltipImage
}) => {
  const [isDialogOpen, setIsDialogOpen] = reactExports.useState(false);
  if (!selectedBaseHead || !lieBuildSpecs) return null;
  if (!(lieBuildSpecs == null ? void 0 : lieBuildSpecs.length)) return null;
  const displayText = (lieBuildSpec) => {
    return clubLieTranslationsValues && clubLieTranslationsValues[lieBuildSpec] ? clubLieTranslationsValues[lieBuildSpec] : lieBuildSpec;
  };
  return /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: `mb-4 flex flex-col flex-wrap ${className}`, children: [
    /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "justify-left mb-2 flex items-center gap-2", children: [
      /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "p4 text-shadow-black", children: label || "Club Lie" }),
      (tooltipText || tooltipImage) && /* @__PURE__ */ jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [
        /* @__PURE__ */ jsxRuntimeExports.jsx(
          "button",
          {
            type: "button",
            onClick: () => setIsDialogOpen(true),
            className: "flex h-5 w-5 cursor-pointer items-center justify-center rounded-full border border-shadow-black text-shadow-black hover:bg-shadow-black hover:text-fairway focus:outline-none focus:ring-2 focus:ring-shadow-black focus:ring-offset-2",
            "aria-label": "About Club Lie",
            children: /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "p4", children: "i" })
          }
        ),
        /* @__PURE__ */ jsxRuntimeExports.jsx(
          TooltipPopover,
          {
            isOpen: isDialogOpen,
            onClose: () => setIsDialogOpen(false),
            title: tooltipTitle,
            content: tooltipText,
            image: tooltipImage
          }
        )
      ] })
    ] }),
    /* @__PURE__ */ jsxRuntimeExports.jsx(Mo, { value: selectedLieBuildSpec, onChange: setSelectedLieBuildSpec, children: /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "relative", children: [
      /* @__PURE__ */ jsxRuntimeExports.jsxs(Mo.Button, { className: "relative w-full cursor-pointer rounded border bg-white py-2 pl-3 pr-10 text-left focus:outline-none focus:ring-2 focus:ring-shadow-black sm:text-sm", children: [
        /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "block truncate", children: selectedLieBuildSpec ? displayText(selectedLieBuildSpec) : "Select Lie" }),
        /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
          "svg",
          {
            className: "h-5 w-5 text-gray-400",
            xmlns: "http://www.w3.org/2000/svg",
            viewBox: "0 0 20 20",
            fill: "currentColor",
            "aria-hidden": "true",
            children: /* @__PURE__ */ jsxRuntimeExports.jsx(
              "path",
              {
                fillRule: "evenodd",
                d: "M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z",
                clipRule: "evenodd"
              }
            )
          }
        ) })
      ] }),
      /* @__PURE__ */ jsxRuntimeExports.jsx(Mo.Options, { className: "absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm", children: lieBuildSpecs.map((lieBuildSpec) => /* @__PURE__ */ jsxRuntimeExports.jsx(
        Mo.Option,
        {
          className: ({ active }) => `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active ? "bg-shadow-black text-fairway" : "text-gray-900"}`,
          value: lieBuildSpec,
          children: ({ selected, active }) => /* @__PURE__ */ jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [
            /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: `block truncate ${selected ? "font-medium" : "font-normal"}`, children: displayText(lieBuildSpec) }),
            selected ? /* @__PURE__ */ jsxRuntimeExports.jsx(
              "span",
              {
                className: `absolute inset-y-0 left-0 flex items-center pl-3 ${active ? "text-fairway" : "text-shadow-black"}`,
                children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                  "svg",
                  {
                    xmlns: "http://www.w3.org/2000/svg",
                    className: "h-5 w-5",
                    viewBox: "0 0 20 20",
                    fill: "currentColor",
                    children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                      "path",
                      {
                        fillRule: "evenodd",
                        d: "M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z",
                        clipRule: "evenodd"
                      }
                    )
                  }
                )
              }
            ) : null
          ] })
        },
        lieBuildSpec
      )) })
    ] }) })
  ] });
};
const ShaftMaterialSelector = ({
  clubType,
  selectedBaseHead,
  selectedShaftFlex,
  selectedShaftMaterial,
  setSelectedShaftMaterial,
  shaftMaterials,
  getAvailableShafts,
  className = "",
  label,
  tooltipTitle,
  tooltipText,
  tooltipImage,
  materialTranslationsValues = {}
}) => {
  const [isDialogOpen, setIsDialogOpen] = reactExports.useState(false);
  if (!shaftMaterials.length || clubType === "putters") return null;
  const handleMaterialChange = (material) => {
    if (material !== selectedShaftMaterial) {
      setSelectedShaftMaterial(material);
    }
  };
  return shaftMaterials.length >= 2 && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: `mb-4 ${className}`, children: [
    /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "justify-left mb-2 flex items-center gap-2", children: [
      /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "p4 text-shadow-black", children: label || "Shaft Material" }),
      (tooltipText || tooltipImage) && /* @__PURE__ */ jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [
        /* @__PURE__ */ jsxRuntimeExports.jsx(
          "button",
          {
            type: "button",
            onClick: () => setIsDialogOpen(true),
            className: "flex h-5 w-5 cursor-pointer items-center justify-center rounded-full border border-shadow-black text-shadow-black hover:bg-shadow-black hover:text-fairway focus:outline-none focus:ring-2 focus:ring-shadow-black focus:ring-offset-2",
            "aria-label": "About Shaft Material",
            children: /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "p4", children: "i" })
          }
        ),
        /* @__PURE__ */ jsxRuntimeExports.jsx(
          TooltipPopover,
          {
            isOpen: isDialogOpen,
            onClose: () => setIsDialogOpen(false),
            title: tooltipTitle,
            content: tooltipText,
            image: tooltipImage
          }
        )
      ] })
    ] }),
    /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex flex-wrap gap-2", children: shaftMaterials.map((material) => {
      const isAvailable = getAvailableShafts(selectedBaseHead, selectedShaftFlex, false).some((shaft) => {
        var _a, _b;
        return ((_b = (_a = shaft == null ? void 0 : shaft.meta) == null ? void 0 : _a.find((m) => (m == null ? void 0 : m.key) === "material")) == null ? void 0 : _b.value) === material;
      });
      const displayText = materialTranslationsValues && materialTranslationsValues[material] || material;
      return /* @__PURE__ */ jsxRuntimeExports.jsx(
        "button",
        {
          onClick: () => handleMaterialChange(material),
          disabled: !isAvailable,
          className: `px-4 py-2 transition-colors border rounded-lg border-shadow-black ${selectedShaftMaterial === material ? "bg-shadow-black text-fairway" : "bg-transparent text-shadow-black hover:bg-grey-v3"} ${!isAvailable && "cursor-not-allowed border-grey-v3 bg-system-disabled text-grey-v3 opacity-50"}`,
          children: displayText
        },
        material
      );
    }) })
  ] });
};
const ShaftFlexSelector = ({
  clubType,
  selectedBaseHead,
  selectedShaftFlex,
  setSelectedShaftFlex,
  className = "",
  selectedShaftMaterial,
  setSelectedShaftMaterial,
  getAvailableShafts,
  productDetails,
  label,
  tooltipTitle,
  tooltipText,
  tooltipImage,
  shaftFlexTranslationsValues
}) => {
  const [isDialogOpen, setIsDialogOpen] = reactExports.useState(false);
  if (clubType === "putters") return null;
  const flexOptions = [
    { value: "ladies", label: (shaftFlexTranslationsValues == null ? void 0 : shaftFlexTranslationsValues.Ladies) || "Ladies" },
    { value: "senior", label: (shaftFlexTranslationsValues == null ? void 0 : shaftFlexTranslationsValues.Senior) || "Senior" },
    { value: "regular", label: (shaftFlexTranslationsValues == null ? void 0 : shaftFlexTranslationsValues.Regular) || "Regular" },
    { value: "stiff", label: (shaftFlexTranslationsValues == null ? void 0 : shaftFlexTranslationsValues.Stiff) || "Stiff" },
    { value: "xstiff", label: (shaftFlexTranslationsValues == null ? void 0 : shaftFlexTranslationsValues.XStiff) || "X-Stiff" }
  ];
  const handleFlexChange = (value) => {
    try {
      setSelectedShaftFlex(value);
      const availableShafts = getAvailableShafts(selectedBaseHead, value);
      if (availableShafts.length === 0) {
        const allShafts = getAvailableShafts(selectedBaseHead, value, false);
        const availableMaterials = /* @__PURE__ */ new Set();
        allShafts.forEach((shaftId) => {
          var _a, _b;
          const material = (_b = (_a = productDetails[shaftId]) == null ? void 0 : _a.shaft_component_material) == null ? void 0 : _b.value;
          if (material) availableMaterials.add(material);
        });
        const firstAvailableMaterial = Array.from(availableMaterials)[0];
        if (firstAvailableMaterial) {
          setSelectedShaftMaterial(firstAvailableMaterial);
        }
      }
    } catch (error) {
      console.error("Error setting shaft flex:", error);
    }
  };
  const selectedFlexOption = flexOptions.find((option) => option.value === selectedShaftFlex) || flexOptions[0];
  return /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: `mb-4 ${className}`, children: [
    /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "justify-left mb-2 flex items-center gap-2", children: [
      /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "p4 text-shadow-black", children: label || "Shaft Flex" }),
      (tooltipText || tooltipImage) && /* @__PURE__ */ jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [
        /* @__PURE__ */ jsxRuntimeExports.jsx(
          "button",
          {
            type: "button",
            onClick: () => setIsDialogOpen(true),
            className: "flex h-5 w-5 cursor-pointer items-center justify-center rounded-full border border-shadow-black text-shadow-black hover:bg-shadow-black hover:text-fairway focus:outline-none focus:ring-2 focus:ring-shadow-black focus:ring-offset-2",
            "aria-label": "About Shaft Flex",
            children: /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "p4", children: "i" })
          }
        ),
        /* @__PURE__ */ jsxRuntimeExports.jsx(
          TooltipPopover,
          {
            isOpen: isDialogOpen,
            onClose: () => setIsDialogOpen(false),
            title: tooltipTitle,
            content: tooltipText,
            image: tooltipImage
          }
        )
      ] })
    ] }),
    /* @__PURE__ */ jsxRuntimeExports.jsx(Mo, { value: selectedShaftFlex, onChange: handleFlexChange, children: /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "relative", children: [
      /* @__PURE__ */ jsxRuntimeExports.jsxs(Mo.Button, { className: "relative w-full cursor-pointer rounded border bg-white py-2 pl-3 pr-10 text-left focus:outline-none focus:ring-2 focus:ring-shadow-black sm:text-sm", children: [
        /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "block truncate", children: selectedFlexOption.label }),
        /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
          "svg",
          {
            className: "h-5 w-5 text-gray-400",
            xmlns: "http://www.w3.org/2000/svg",
            viewBox: "0 0 20 20",
            fill: "currentColor",
            "aria-hidden": "true",
            children: /* @__PURE__ */ jsxRuntimeExports.jsx(
              "path",
              {
                fillRule: "evenodd",
                d: "M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z",
                clipRule: "evenodd"
              }
            )
          }
        ) })
      ] }),
      /* @__PURE__ */ jsxRuntimeExports.jsx(Mo.Options, { className: "absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm", children: flexOptions.map((option) => {
        const availableShafts = getAvailableShafts(selectedBaseHead, option.value);
        const hasShafts = selectedShaftMaterial ? availableShafts.some((shaft) => {
          var _a, _b, _c, _d;
          const shaftMaterial = (_b = (_a = shaft == null ? void 0 : shaft.meta) == null ? void 0 : _a.find((m) => (m == null ? void 0 : m.key) === "material")) == null ? void 0 : _b.value;
          const inventory = (_d = (_c = shaft == null ? void 0 : shaft.variants) == null ? void 0 : _c.nodes) == null ? void 0 : _d[0];
          return shaftMaterial === selectedShaftMaterial && shaft.availableForSale && shaft.totalInventory > 0 && (inventory == null ? void 0 : inventory.availableForSale);
        }) : availableShafts.some(
          (shaft) => {
            var _a, _b, _c;
            return shaft.availableForSale && shaft.totalInventory > 0 && ((_c = (_b = (_a = shaft == null ? void 0 : shaft.variants) == null ? void 0 : _a.nodes) == null ? void 0 : _b[0]) == null ? void 0 : _c.availableForSale);
          }
        );
        return /* @__PURE__ */ jsxRuntimeExports.jsx(
          Mo.Option,
          {
            value: option.value,
            disabled: !hasShafts,
            className: ({ active }) => `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active ? "bg-shadow-black text-fairway" : "text-gray-900"} ${!hasShafts ? "cursor-not-allowed bg-system-disabled opacity-50" : ""}`,
            children: ({ selected, active }) => /* @__PURE__ */ jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: `block truncate ${selected ? "font-medium" : "font-normal"}`, children: option.label }),
              selected && /* @__PURE__ */ jsxRuntimeExports.jsx(
                "span",
                {
                  className: `absolute inset-y-0 left-0 flex items-center pl-3 ${active ? "text-fairway" : "text-shadow-black"}`,
                  children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                    "svg",
                    {
                      xmlns: "http://www.w3.org/2000/svg",
                      className: "h-5 w-5",
                      viewBox: "0 0 20 20",
                      fill: "currentColor",
                      children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                        "path",
                        {
                          fillRule: "evenodd",
                          d: "M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z",
                          clipRule: "evenodd"
                        }
                      )
                    }
                  )
                }
              )
            ] })
          },
          option.value
        );
      }) })
    ] }) })
  ] });
};
const LengthBuildSpecSelector = ({
  selectedBaseHead,
  lengthBuildSpecs,
  selectedLengthBuildSpec,
  setSelectedLengthBuildSpec,
  className = "",
  label,
  tooltipTitle,
  tooltipText,
  tooltipImage,
  shaftLengthTranslationsValues
}) => {
  const [isDialogOpen, setIsDialogOpen] = reactExports.useState(false);
  if (!selectedBaseHead || !lengthBuildSpecs) return null;
  const handleLengthBuildSpecChange = (newLengthBuildSpec) => {
    if (newLengthBuildSpec && (!selectedLengthBuildSpec || newLengthBuildSpec !== selectedLengthBuildSpec)) {
      setSelectedLengthBuildSpec(newLengthBuildSpec);
    }
  };
  return /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: `mb-4 flex flex-col flex-wrap ${className}`, children: [
    /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "justify-left mb-2 flex items-center gap-2", children: [
      /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "p4 text-shadow-black", children: label || "Shaft Length" }),
      (tooltipText || tooltipImage) && /* @__PURE__ */ jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [
        /* @__PURE__ */ jsxRuntimeExports.jsx(
          "button",
          {
            type: "button",
            onClick: () => setIsDialogOpen(true),
            className: "flex h-5 w-5 cursor-pointer items-center justify-center rounded-full border border-shadow-black text-shadow-black hover:bg-shadow-black hover:text-fairway focus:outline-none focus:ring-2 focus:ring-shadow-black focus:ring-offset-2",
            "aria-label": "About Shaft Length",
            children: /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "p4", children: "i" })
          }
        ),
        /* @__PURE__ */ jsxRuntimeExports.jsx(
          TooltipPopover,
          {
            isOpen: isDialogOpen,
            onClose: () => setIsDialogOpen(false),
            title: tooltipTitle,
            content: tooltipText,
            image: tooltipImage
          }
        )
      ] })
    ] }),
    /* @__PURE__ */ jsxRuntimeExports.jsx(Mo, { value: selectedLengthBuildSpec, onChange: handleLengthBuildSpecChange, children: /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "relative", children: [
      /* @__PURE__ */ jsxRuntimeExports.jsxs(Mo.Button, { className: "relative w-full cursor-pointer rounded border bg-white py-2 pl-3 pr-10 text-left focus:outline-none focus:ring-2 focus:ring-shadow-black sm:text-sm", children: [
        /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "block truncate", children: selectedLengthBuildSpec || "Select Length" }),
        /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
          "svg",
          {
            className: "h-5 w-5 text-gray-400",
            xmlns: "http://www.w3.org/2000/svg",
            viewBox: "0 0 20 20",
            fill: "currentColor",
            "aria-hidden": "true",
            children: /* @__PURE__ */ jsxRuntimeExports.jsx(
              "path",
              {
                fillRule: "evenodd",
                d: "M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z",
                clipRule: "evenodd"
              }
            )
          }
        ) })
      ] }),
      /* @__PURE__ */ jsxRuntimeExports.jsx(Mo.Options, { className: "absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm", children: lengthBuildSpecs.map((lengthBuildSpec) => /* @__PURE__ */ jsxRuntimeExports.jsx(
        Mo.Option,
        {
          className: ({ active }) => `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active ? "bg-shadow-black text-fairway" : "text-gray-900"}`,
          value: lengthBuildSpec,
          children: ({ selected, active }) => /* @__PURE__ */ jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [
            /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: `block truncate ${selected ? "font-medium" : "font-normal"}`, children: lengthBuildSpec }),
            selected ? /* @__PURE__ */ jsxRuntimeExports.jsx(
              "span",
              {
                className: `absolute inset-y-0 left-0 flex items-center pl-3 ${active ? "text-fairway" : "text-shadow-black"}`,
                children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                  "svg",
                  {
                    xmlns: "http://www.w3.org/2000/svg",
                    className: "h-5 w-5",
                    viewBox: "0 0 20 20",
                    fill: "currentColor",
                    children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                      "path",
                      {
                        fillRule: "evenodd",
                        d: "M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z",
                        clipRule: "evenodd"
                      }
                    )
                  }
                )
              }
            ) : null
          ] })
        },
        lengthBuildSpec
      )) })
    ] }) })
  ] });
};
const ShaftSelector = ({
  selectedShaft,
  setSelectedShaft,
  selectedBaseHead,
  selectedShaftFlex,
  selectedShaftMaterial,
  getAvailableShafts,
  productDetails,
  isShaftCompatible,
  getInventoryInfo,
  selectedHeadComponents,
  className = "",
  label,
  clubType
}) => {
  var _a, _b, _c, _d, _e, _f, _g, _h;
  if (!selectedBaseHead || !selectedShaftFlex) {
    return null;
  }
  const availableShafts = getAvailableShafts(selectedBaseHead, selectedShaftFlex);
  const shaftOptions = availableShafts.map((shaft) => {
    var _a2, _b2, _c2, _d2, _e2, _f2, _g2, _h2, _i, _j, _k, _l, _m, _n, _o;
    const isTapered = ((_b2 = (_a2 = shaft == null ? void 0 : shaft.meta) == null ? void 0 : _a2.find((m) => (m == null ? void 0 : m.key) === "tapered_shaft")) == null ? void 0 : _b2.value.toLowerCase()) === "true";
    const price = ((_f2 = (_e2 = (_d2 = (_c2 = shaft == null ? void 0 : shaft.variants) == null ? void 0 : _c2.nodes) == null ? void 0 : _d2[0]) == null ? void 0 : _e2.price) == null ? void 0 : _f2.amount) ? parseFloat(shaft.variants.nodes[0].price.amount) : 0;
    const currencyCode = (_j = (_i = (_h2 = (_g2 = shaft == null ? void 0 : shaft.variants) == null ? void 0 : _g2.nodes) == null ? void 0 : _h2[0]) == null ? void 0 : _i.price) == null ? void 0 : _j.currencyCode;
    const formattedPrice = formatPrice(price, currencyCode);
    let isCompatible = isShaftCompatible(shaft, selectedHeadComponents, productDetails);
    let isInStock = false;
    if (isTapered) {
      const taperedShafts = ((_m = (_l = (_k = shaft == null ? void 0 : shaft.taperedShafts) == null ? void 0 : _k.find((metafield) => {
        var _a3, _b3;
        return ((_b3 = (_a3 = metafield == null ? void 0 : metafield.references) == null ? void 0 : _a3.nodes) == null ? void 0 : _b3.length) > 0;
      })) == null ? void 0 : _l.references) == null ? void 0 : _m.nodes) || [];
      isInStock = taperedShafts.every((taperedShaft) => {
        const inventory = getInventoryInfo(taperedShaft);
        return inventory.available;
      });
    } else {
      const inventory = getInventoryInfo(shaft);
      if (clubType === "irons" && !isTapered) {
        isInStock = inventory.available && inventory.quantity >= 12;
      } else {
        isInStock = inventory.available;
      }
    }
    const isAvailable = isInStock && isCompatible;
    return {
      id: shaft.id,
      title: ((_o = (_n = shaft == null ? void 0 : shaft.meta) == null ? void 0 : _n.find((m) => (m == null ? void 0 : m.key) === "display_title")) == null ? void 0 : _o.value) || (shaft == null ? void 0 : shaft.title) || "Loading...",
      price: formattedPrice,
      isAvailable,
      isInStock,
      isCompatible,
      isTapered,
      shaft
    };
  }).filter((option) => option.isAvailable);
  const handleShaftChange = (newShaft) => {
    if ((newShaft == null ? void 0 : newShaft.title) && (!selectedShaft || (newShaft == null ? void 0 : newShaft.title) !== (selectedShaft == null ? void 0 : selectedShaft.title))) {
      setSelectedShaft(newShaft);
    }
  };
  return /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: `mb-4 ${className}`, children: [
    /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "p4 mb-2 text-shadow-black", children: label || "Shaft" }),
    /* @__PURE__ */ jsxRuntimeExports.jsx(Mo, { value: selectedShaft, onChange: handleShaftChange, children: /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "relative", children: [
      /* @__PURE__ */ jsxRuntimeExports.jsxs(Mo.Button, { className: "relative w-full cursor-pointer rounded border bg-white py-2 pl-3 pr-10 text-left focus:outline-none focus:ring-2 focus:ring-shadow-black sm:text-sm", children: [
        /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex flex-wrap pr-8", children: /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "whitespace-normal break-words", children: selectedShaft ? `${((_b = (_a = selectedShaft == null ? void 0 : selectedShaft.meta) == null ? void 0 : _a.find((m) => (m == null ? void 0 : m.key) === "display_title")) == null ? void 0 : _b.value) || (selectedShaft == null ? void 0 : selectedShaft.title) || "Loading..."} ${((_f = (_e = (_d = (_c = selectedShaft == null ? void 0 : selectedShaft.variants) == null ? void 0 : _c.nodes) == null ? void 0 : _d[0]) == null ? void 0 : _e.price) == null ? void 0 : _f.amount) && parseFloat(selectedShaft.variants.nodes[0].price.amount) > 0 ? `(+${formatPrice(
          parseFloat(selectedShaft.variants.nodes[0].price.amount),
          selectedShaft.variants.nodes[0].price.currencyCode
        )})` : ""}` : "Loading..." }) }),
        /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
          "svg",
          {
            className: "h-5 w-5 text-gray-400",
            xmlns: "http://www.w3.org/2000/svg",
            viewBox: "0 0 20 20",
            fill: "currentColor",
            "aria-hidden": "true",
            children: /* @__PURE__ */ jsxRuntimeExports.jsx(
              "path",
              {
                fillRule: "evenodd",
                d: "M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z",
                clipRule: "evenodd"
              }
            )
          }
        ) })
      ] }),
      /* @__PURE__ */ jsxRuntimeExports.jsx(Mo.Options, { className: "absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm", children: shaftOptions.length === 0 ? /* @__PURE__ */ jsxRuntimeExports.jsx(
        Mo.Option,
        {
          value: null,
          disabled: true,
          className: "relative cursor-not-allowed py-2 pl-10 pr-4 text-gray-400",
          children: /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "block truncate", children: selectedShaftMaterial ? `No ${selectedShaftMaterial.toLowerCase()} shafts available with ${selectedShaftFlex.toLowerCase()} flex.` : `No shafts available for ${selectedShaftFlex.toLowerCase()} flex.` })
        }
      ) : shaftOptions.map(({ shaft, title, price }) => /* @__PURE__ */ jsxRuntimeExports.jsx(
        Mo.Option,
        {
          value: shaft,
          className: ({ active }) => `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active ? "bg-shadow-black text-fairway" : "text-gray-900"}`,
          children: ({ selected, active }) => /* @__PURE__ */ jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [
            /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex flex-wrap pr-8", children: /* @__PURE__ */ jsxRuntimeExports.jsxs("span", { className: `whitespace-normal break-words ${selected ? "font-medium" : "font-normal"}`, children: [
              title,
              price ? ` (+${price})` : ""
            ] }) }),
            selected && /* @__PURE__ */ jsxRuntimeExports.jsx(
              "span",
              {
                className: `absolute inset-y-0 left-0 flex items-center pl-3 ${active ? "text-fairway" : "text-shadow-black"}`,
                children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                  "svg",
                  {
                    xmlns: "http://www.w3.org/2000/svg",
                    className: "h-5 w-5",
                    viewBox: "0 0 20 20",
                    fill: "currentColor",
                    children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                      "path",
                      {
                        fillRule: "evenodd",
                        d: "M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z",
                        clipRule: "evenodd"
                      }
                    )
                  }
                )
              }
            )
          ] })
        },
        shaft.id
      )) })
    ] }) }),
    selectedShaft && ((_h = (_g = selectedShaft == null ? void 0 : selectedShaft.meta) == null ? void 0 : _g.find((m) => (m == null ? void 0 : m.key) === "product_details")) == null ? void 0 : _h.value) && /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "mx-4 mb-8 mt-4", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
      "div",
      {
        className: "rte",
        dangerouslySetInnerHTML: {
          __html: purify.sanitize(selectedShaft.meta.find((m) => (m == null ? void 0 : m.key) === "product_details").value)
        }
      }
    ) })
  ] });
};
const BounceSelector = ({
  clubType,
  selectedLofts,
  selectedBaseHead,
  selectedDexterity,
  selectedBounce,
  setSelectedBounce,
  getAvailableBounces,
  label
}) => {
  if (clubType !== "wedges" || !selectedBaseHead) {
    return null;
  }
  const availableBounces = getAvailableBounces(selectedBaseHead, selectedDexterity, selectedLofts);
  return /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mb-6", children: [
    /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "p4 mb-2 text-shadow-black", children: label || "Bounce" }),
    /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex flex-wrap gap-2", children: availableBounces.map((bounce) => /* @__PURE__ */ jsxRuntimeExports.jsx(
      "button",
      {
        onClick: () => setSelectedBounce(bounce),
        className: `p3 rounded-[10px] border border-shadow-black px-4 py-[10px] transition-colors ${selectedBounce === bounce ? "bg-shadow-black text-fairway" : "bg-transparent text-shadow-black hover:bg-grey-v3"}`,
        children: bounce
      },
      bounce
    )) })
  ] });
};
const GripSizeSelector = ({
  clubType,
  selectedGripSize,
  onGripSizeChange,
  label,
  tooltipTitle,
  tooltipText,
  tooltipImage,
  gripSizeTranslationsValues
}) => {
  const [isDialogOpen, setIsDialogOpen] = reactExports.useState(false);
  if (clubType.toLowerCase() === "putters") return null;
  const displayText = (gripSize) => {
    return gripSizeTranslationsValues && gripSizeTranslationsValues[gripSize] ? gripSizeTranslationsValues[gripSize] : gripSize;
  };
  const handleSizeChange = (size) => {
    if (size && (!size || size != selectedGripSize)) {
      onGripSizeChange(size);
    }
  };
  return /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mb-6", children: [
    /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "justify-left mb-2 flex items-center gap-2", children: [
      /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "p4 text-shadow-black", children: label || "Grip Size" }),
      (tooltipText || tooltipImage) && /* @__PURE__ */ jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [
        /* @__PURE__ */ jsxRuntimeExports.jsx(
          "button",
          {
            type: "button",
            onClick: () => setIsDialogOpen(true),
            className: "flex h-5 w-5 cursor-pointer items-center justify-center rounded-full border border-shadow-black text-shadow-black hover:bg-shadow-black hover:text-fairway focus:outline-none focus:ring-2 focus:ring-shadow-black focus:ring-offset-2",
            "aria-label": "About Grip Size",
            children: /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "p4", children: "i" })
          }
        ),
        /* @__PURE__ */ jsxRuntimeExports.jsx(
          TooltipPopover,
          {
            isOpen: isDialogOpen,
            onClose: () => setIsDialogOpen(false),
            title: tooltipTitle,
            content: tooltipText,
            image: tooltipImage
          }
        )
      ] })
    ] }),
    /* @__PURE__ */ jsxRuntimeExports.jsx(Mo, { value: selectedGripSize, onChange: handleSizeChange, children: /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "relative", children: [
      /* @__PURE__ */ jsxRuntimeExports.jsxs(Mo.Button, { className: "relative w-full cursor-pointer rounded border bg-white py-2 pl-3 pr-10 text-left focus:outline-none focus:ring-2 focus:ring-shadow-black sm:text-sm", children: [
        /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "block truncate capitalize", children: displayText(selectedGripSize) }),
        /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
          "svg",
          {
            className: "h-5 w-5 text-gray-400",
            xmlns: "http://www.w3.org/2000/svg",
            viewBox: "0 0 20 20",
            fill: "currentColor",
            "aria-hidden": "true",
            children: /* @__PURE__ */ jsxRuntimeExports.jsx(
              "path",
              {
                fillRule: "evenodd",
                d: "M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z",
                clipRule: "evenodd"
              }
            )
          }
        ) })
      ] }),
      /* @__PURE__ */ jsxRuntimeExports.jsx(Mo.Options, { className: "absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm", children: ["Undersize", "Standard", "Midsize", "Oversize"].map((size) => /* @__PURE__ */ jsxRuntimeExports.jsx(
        Mo.Option,
        {
          className: ({ active }) => `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active ? "bg-shadow-black text-fairway" : "text-gray-900"}`,
          value: size,
          children: ({ selected, active }) => /* @__PURE__ */ jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [
            /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: `block truncate capitalize ${selected ? "font-medium" : "font-normal"}`, children: displayText(size) }),
            selected ? /* @__PURE__ */ jsxRuntimeExports.jsx(
              "span",
              {
                className: `absolute inset-y-0 left-0 flex items-center pl-3 ${active ? "text-fairway" : "text-shadow-black"}`,
                children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                  "svg",
                  {
                    xmlns: "http://www.w3.org/2000/svg",
                    className: "h-5 w-5",
                    viewBox: "0 0 20 20",
                    fill: "currentColor",
                    children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                      "path",
                      {
                        fillRule: "evenodd",
                        d: "M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z",
                        clipRule: "evenodd"
                      }
                    )
                  }
                )
              }
            ) : null
          ] })
        },
        size
      )) })
    ] }) })
  ] });
};
const ClubConfiguratorFooter = ({
  totalPrice,
  labels,
  formattedTotalPrice,
  formattedTotalCompareAtPrice,
  totalCompareAtPrice,
  handleResetToDefaults,
  onClose,
  handleAddToCart,
  addToCartButtonState
}) => {
  return /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "border-t bg-fairway-light p-4", children: [
    /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mb-1 flex w-full items-center justify-between", children: [
      /* @__PURE__ */ jsxRuntimeExports.jsx("p", { children: labels.estimated_total_title || "Estimated total:" }),
      /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex items-center gap-1", children: [
        formattedTotalCompareAtPrice && totalCompareAtPrice.compareAtPrice > totalPrice.price && /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "line-through", children: formattedTotalCompareAtPrice }),
        /* @__PURE__ */ jsxRuntimeExports.jsx("p", { children: formattedTotalPrice })
      ] })
    ] }),
    /* @__PURE__ */ jsxRuntimeExports.jsx("small", { className: "block opacity-75", children: "Taxes, discounts and shipping calculated at checkout." }),
    /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mt-4 grid w-full grid-cols-2 gap-4", children: [
      /* @__PURE__ */ jsxRuntimeExports.jsx(
        "button",
        {
          className: "btn btn-full btn-secondary btn-lg",
          onClick: () => {
            handleResetToDefaults();
            onClose();
          },
          children: "Reset"
        }
      ),
      /* @__PURE__ */ jsxRuntimeExports.jsx(
        "button",
        {
          className: `btn btn-primary btn-full capitalize ${addToCartButtonState.disabled ? "cursor-not-allowed opacity-50" : ""}`,
          onClick: () => handleAddToCart(false),
          disabled: addToCartButtonState.disabled,
          children: addToCartButtonState.text
        }
      )
    ] })
  ] });
};
const ChevronDown = ({ ...props }) => {
  return /* @__PURE__ */ jsxRuntimeExports.jsx(
    "svg",
    {
      ...props,
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 24 24",
      width: "24",
      height: "24",
      fill: "none",
      stroke: "currentColor",
      "stroke-linecap": "round",
      "stroke-linejoin": "round",
      "stroke-width": "2",
      children: /* @__PURE__ */ jsxRuntimeExports.jsx("polyline", { points: "6 9 12 15 18 9" })
    }
  );
};
const ClubConfiguratorAdvancedPanel = ({
  clubType,
  isOpen,
  onClose,
  labels,
  tooltips,
  includeTorqueWrench,
  handleTorqueWrenchChange,
  torqueWrenchProduct,
  selectedHeadcover,
  headModelTranslationsValues,
  headColorTranslationsValues,
  shaftFlexTranslationsValues,
  shaftLengthTranslationsValues,
  materialTranslationsValues,
  gripSizeTranslationsValues,
  clubLoftTranslationsValues,
  clubLieTranslationsValues,
  selectedGripSize,
  onGripSizeChange,
  selectedBaseHead,
  productDetails,
  selectedGrip,
  setSelectedGrip,
  selectedHeadComponents,
  hasTorqueWrench,
  selectedHosel,
  setSelectedHosel,
  selectedDexterity,
  setSelectedDexterity,
  getSelectedHeadComponents,
  selectedModel,
  setSelectedModel,
  selectedColor,
  setSelectedColor,
  availableModels,
  availableModelsForColor,
  availableColors,
  availableColorsForModel,
  baseHeads,
  selectedLofts,
  setSelectedLofts,
  getAvailableLofts,
  isLoftAvailable,
  getCompositionLofts,
  formatLoftDisplay,
  handleLoftSelection,
  selectedLoftBuildSpec,
  setSelectedLoftBuildSpec,
  loftBuildSpecs,
  selectedLieBuildSpec,
  setSelectedLieBuildSpec,
  lieBuildSpecs,
  lengthBuildSpecs,
  selectedLengthBuildSpec,
  setSelectedLengthBuildSpec,
  selectedShaftMaterial,
  setSelectedShaftMaterial,
  selectedShaftFlex,
  setSelectedShaftFlex,
  selectedShaft,
  setSelectedShaft,
  shaftMaterials,
  getAvailableShafts,
  isShaftCompatible,
  getInventoryInfo,
  selectedBounce,
  setSelectedBounce,
  getAvailableBounces,
  handleResetToDefaults,
  hasManualShaftChanges = { material: false, flex: false, shaft: false },
  setHasManualShaftChanges = () => {
  },
  hasManualGripChanges = { grip: false, size: false },
  setHasManualGripChanges = () => {
  },
  handleAddToCart,
  getHeadcoverInfo,
  cachedImages,
  cartQuantity = 0,
  isLoadingCart = false,
  maxBundledItems,
  totalSelectedItemsCount,
  addToCartButtonState,
  totalPrice,
  totalCompareAtPrice,
  formattedTotalPrice = "",
  formattedTotalCompareAtPrice = "",
  getAvailableHosels,
  handleMainConfigChange,
  activeDisclosurePanel,
  setActiveDisclosurePanel,
  selectedComposition,
  setSelectedComposition
}) => {
  const clubHeadPanelRef = reactExports.useRef(null);
  const shaftPanelRef = reactExports.useRef(null);
  const gripPanelRef = reactExports.useRef(null);
  const handlePanelChange = (panel) => {
    if (activeDisclosurePanel === panel) {
      setActiveDisclosurePanel(null);
      return;
    }
    if (panel !== "club-head" && clubHeadPanelRef.current) {
      clubHeadPanelRef.current();
    }
    if (panel !== "shaft" && shaftPanelRef.current) {
      shaftPanelRef.current();
    }
    if (panel !== "grip" && gripPanelRef.current) {
      gripPanelRef.current();
    }
    setActiveDisclosurePanel(panel);
  };
  const isGripCompatible = (grip, selectedHeadComponents2, productDetails2, selectedHosel2, clubType2) => {
    var _a, _b, _c, _d, _e, _f, _g;
    if (!grip) {
      return false;
    }
    const gripMapping = (_b = (_a = grip == null ? void 0 : grip.meta) == null ? void 0 : _a.find((m) => (m == null ? void 0 : m.key) === "club_component_mapping")) == null ? void 0 : _b.value;
    if (!gripMapping) {
      return true;
    }
    try {
      const gripCompatibleStrings = Object.values(JSON.parse(gripMapping));
      if (clubType2 === "putters" && selectedHosel2) {
        const isCompatible2 = gripCompatibleStrings.includes(selectedHosel2);
        return isCompatible2;
      }
      if (!((_c = selectedBaseHead == null ? void 0 : selectedBaseHead.headComponents) == null ? void 0 : _c.length)) {
        return true;
      }
      const headComponent = selectedBaseHead.headComponents[0];
      if (!((_e = (_d = headComponent == null ? void 0 : headComponent.references) == null ? void 0 : _d.nodes) == null ? void 0 : _e.length)) {
        return true;
      }
      const headNode = headComponent.references.nodes[0];
      const headMapping = (_g = (_f = headNode == null ? void 0 : headNode.meta) == null ? void 0 : _f.find((m) => (m == null ? void 0 : m.key) === "club_component_mapping")) == null ? void 0 : _g.value;
      if (!headMapping) {
        return true;
      }
      const headComponentStrings = Object.values(JSON.parse(headMapping));
      const isCompatible = headComponentStrings.some((str) => gripCompatibleStrings.includes(str));
      return isCompatible;
    } catch (error) {
      console.error("Error in grip compatibility check:", error);
      return true;
    }
  };
  const getGripListForSize = () => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j;
    if (!selectedBaseHead) {
      return [];
    }
    const gripComponents = selectedBaseHead.gripComponents || [];
    const gripListMap = {
      Undersize: ((_b = (_a = gripComponents.find((comp) => (comp == null ? void 0 : comp.key) === "list_of_gripsundersize")) == null ? void 0 : _a.references) == null ? void 0 : _b.nodes) || [],
      Standard: ((_d = (_c = gripComponents.find((comp) => (comp == null ? void 0 : comp.key) === "list_of_gripsstandard")) == null ? void 0 : _c.references) == null ? void 0 : _d.nodes) || [],
      Midsize: ((_f = (_e = gripComponents.find((comp) => (comp == null ? void 0 : comp.key) === "list_of_gripsmidsize")) == null ? void 0 : _e.references) == null ? void 0 : _f.nodes) || [],
      Oversize: ((_h = (_g = gripComponents.find((comp) => (comp == null ? void 0 : comp.key) === "list_of_gripsoversize")) == null ? void 0 : _g.references) == null ? void 0 : _h.nodes) || [],
      Putter: ((_j = (_i = gripComponents.find((comp) => (comp == null ? void 0 : comp.key) === "list_of_gripsputter")) == null ? void 0 : _i.references) == null ? void 0 : _j.nodes) || []
    };
    let gripsBysize = [];
    if (clubType === "putters") {
      gripsBysize = gripListMap.Putter;
    } else {
      gripsBysize = gripListMap[selectedGripSize] || [];
    }
    const compatibleGrips = gripsBysize.filter((grip) => {
      var _a2, _b2;
      const isHeadCompatible = isGripCompatible(grip, selectedHeadComponents, productDetails, selectedHosel, clubType);
      const gripDexterity = (_b2 = (_a2 = grip == null ? void 0 : grip.meta) == null ? void 0 : _a2.find((m) => (m == null ? void 0 : m.key) === "dexterity")) == null ? void 0 : _b2.value;
      const isDexterityCompatible = !gripDexterity || gripDexterity === selectedDexterity;
      return isHeadCompatible && isDexterityCompatible;
    });
    return compatibleGrips;
  };
  const availableGrips = getGripListForSize();
  reactExports.useEffect(() => {
    if (selectedGrip && availableGrips.length === 0) {
      setSelectedGrip(null);
    } else if (selectedGrip && !availableGrips.some((grip) => grip.id === selectedGrip.id)) {
      setSelectedGrip(null);
    }
  }, [availableGrips, selectedGrip, setSelectedGrip]);
  reactExports.useEffect(() => {
    var _a;
    if ((_a = selectedBaseHead == null ? void 0 : selectedBaseHead.list_of_gripsputter) == null ? void 0 : _a.value) {
      try {
        const putterGrips = JSON.parse(selectedBaseHead.list_of_gripsputter.value);
        if (putterGrips.length > 0) {
          onGripSizeChange("putter");
        }
      } catch (error) {
        console.error("Error parsing putter grips:", error);
      }
    }
  }, [selectedBaseHead, onGripSizeChange]);
  reactExports.useEffect(() => {
    var _a, _b;
    if (!selectedBaseHead || !availableGrips.length || selectedGrip) return;
    try {
      const defaultGrips = JSON.parse(
        ((_b = (_a = selectedBaseHead.meta) == null ? void 0 : _a.find((m) => (m == null ? void 0 : m.key) === "list_of_default_grip")) == null ? void 0 : _b.value) || "[]"
      );
      const defaultGripInCurrentType = availableGrips.find((grip) => defaultGrips.includes(grip.id));
      if (defaultGripInCurrentType) {
        setSelectedGrip(defaultGripInCurrentType);
      } else {
        setSelectedGrip(availableGrips[0]);
      }
    } catch (error) {
      console.error("Error setting default grip:", error);
      setSelectedGrip(availableGrips[0]);
    }
  }, [selectedGripSize, selectedBaseHead, availableGrips, selectedGrip]);
  reactExports.useEffect(() => {
    setSelectedGrip(null);
  }, [selectedGripSize]);
  reactExports.useEffect(() => {
    setSelectedGrip(null);
  }, [selectedDexterity]);
  const handleGripChange = (gripId) => {
    setSelectedGrip(gripId);
    setHasManualGripChanges((prev) => ({ ...prev, grip: true }));
    if (window.setUserSelectedGrip) {
      window.setUserSelectedGrip(true);
    }
  };
  const handleGripSizeChange = (size) => {
    onGripSizeChange(size);
    setHasManualGripChanges((prev) => ({ ...prev, size: true }));
  };
  const handleShaftMaterialChange = (material) => {
    setSelectedShaftMaterial(material);
    setHasManualShaftChanges((prev) => ({ ...prev, material: true }));
    if (window.setUserSelectedShaft) {
      window.setUserSelectedShaft(true);
    }
  };
  const handleShaftFlexChange = (flex) => {
    if (typeof setSelectedShaftFlex === "function") {
      setSelectedShaftFlex(flex);
      setHasManualShaftChanges((prev) => ({ ...prev, flex: true }));
      if (window.setUserSelectedShaft) {
        window.setUserSelectedShaft(true);
      }
    }
  };
  const handleShaftChange = (shaft) => {
    setSelectedShaft(shaft);
    setHasManualShaftChanges((prev) => ({ ...prev, shaft: true }));
    if (window.setUserSelectedShaft) {
      window.setUserSelectedShaft(true);
    }
  };
  const hasModifications = (changes) => {
    return changes && typeof changes === "object" && Object.values(changes).some(Boolean);
  };
  const disclosureButtonClasses = "flex w-full items-center justify-between bg-white py-3 text-left text-sm focus:outline-none focus-visible:ring focus-visible:ring-shadow-black focus-visible:ring-opacity-75";
  return /* @__PURE__ */ jsxRuntimeExports.jsx(ze.Root, { show: isOpen, as: reactExports.Fragment, children: /* @__PURE__ */ jsxRuntimeExports.jsxs(yt, { as: "div", className: "relative z-50", onClose, children: [
    /* @__PURE__ */ jsxRuntimeExports.jsx(
      ze.Child,
      {
        as: reactExports.Fragment,
        enter: "ease-in-out duration-300",
        enterFrom: "opacity-0",
        enterTo: "opacity-100",
        leave: "ease-in-out duration-300",
        leaveFrom: "opacity-100",
        leaveTo: "opacity-0",
        children: /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "fixed inset-0 bg-black/50 transition-opacity" })
      }
    ),
    /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "fixed inset-0 overflow-hidden", children: /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "absolute inset-0 overflow-hidden", children: /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "pointer-events-none fixed inset-y-0 right-0 flex w-full max-w-full pl-12 md:max-w-[472px] md:pl-0", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
      ze.Child,
      {
        as: reactExports.Fragment,
        enter: "transform transition ease-in-out duration-300",
        enterFrom: "translate-x-full",
        enterTo: "translate-x-0",
        leave: "transform transition ease-in-out duration-300",
        leaveFrom: "translate-x-0",
        leaveTo: "translate-x-full",
        children: /* @__PURE__ */ jsxRuntimeExports.jsx(yt.Panel, { className: "pointer-events-auto w-full bg-white shadow-xl md:w-[872px]", children: /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex h-full flex-col", children: [
          /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex w-full items-center justify-between border-b py-2 pl-4 pr-2", children: [
            /* @__PURE__ */ jsxRuntimeExports.jsx("h2", { className: "font-display text-lg font-semibold capitalize", children: "Club Customizer Tool" }),
            /* @__PURE__ */ jsxRuntimeExports.jsxs(
              "button",
              {
                onClick: onClose,
                className: "relative rounded-full p-2 transition-colors focus:outline-none focus:ring-2 focus:ring-shadow-black focus:ring-offset-2",
                children: [
                  /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "sr-only", children: "Close panel" }),
                  /* @__PURE__ */ jsxRuntimeExports.jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "14", height: "14", viewBox: "0 0 14 14", fill: "none", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                    "path",
                    {
                      d: "M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z",
                      fill: "currentColor"
                    }
                  ) })
                ]
              }
            )
          ] }),
          /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex-1 overflow-y-auto px-4 py-2", children: /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mx-auto w-full divide-y border-b", children: [
            /* @__PURE__ */ jsxRuntimeExports.jsx(je, { defaultOpen: activeDisclosurePanel === "club-head", children: ({ open, close }) => {
              var _a;
              clubHeadPanelRef.current = close;
              return /* @__PURE__ */ jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [
                /* @__PURE__ */ jsxRuntimeExports.jsxs(
                  je.Button,
                  {
                    className: disclosureButtonClasses,
                    onClick: () => handlePanelChange("club-head"),
                    children: [
                      /* @__PURE__ */ jsxRuntimeExports.jsx(
                        "span",
                        {
                          className: `h4 ${activeDisclosurePanel === "club-head" ? "text-swing-red" : "text-shadow-black group-hover:text-grey-v1"}`,
                          children: labels.club_head || "Club Head"
                        }
                      ),
                      /* @__PURE__ */ jsxRuntimeExports.jsx(ChevronDown, { className: `${open ? "rotate-180 transform" : ""} h-5 w-5 origin-center text-shadow-black transition-transform` })
                    ]
                  }
                ),
                /* @__PURE__ */ jsxRuntimeExports.jsx(je.Panel, { children: /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "py-4", children: [
                  /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mb-4 flex flex-row justify-center gap-4", children: [
                    selectedBaseHead && /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "h-[150px] w-[150px]", children: ((_a = cachedImages[selectedBaseHead.id]) == null ? void 0 : _a.length) > 0 ? /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex h-full w-full items-center justify-center", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                      "img",
                      {
                        src: cachedImages[selectedBaseHead.id][0].node.url,
                        srcSet: cachedImages[selectedBaseHead.id][0].node.small ? `${cachedImages[selectedBaseHead.id][0].node.small} 300w` : void 0,
                        alt: `${selectedModel} ${selectedColor}`,
                        className: "max-h-full max-w-full object-cover",
                        width: 150,
                        height: 150,
                        loading: "eager"
                      }
                    ) }) : /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex h-full w-full items-center justify-center bg-shadow-black p-1", children: /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "h5 text-center text-fairway", children: selectedBaseHead.title }) }) }),
                    selectedHeadcover && (() => {
                      var _a2, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n, _o, _p;
                      const inventory = getInventoryInfo(selectedHeadcover);
                      if (!inventory.available) return null;
                      return /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex flex-col items-center", children: [
                        /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "h-[150px] w-[150px]", children: ((_d = (_c = (_b = (_a2 = selectedHeadcover == null ? void 0 : selectedHeadcover.images) == null ? void 0 : _a2.edges) == null ? void 0 : _b[0]) == null ? void 0 : _c.node) == null ? void 0 : _d.url) ? /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex h-full w-full flex-col items-center", children: /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex h-[150px] w-[150px] items-center justify-center", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                          "img",
                          {
                            src: (_h = (_g = (_f = (_e = selectedHeadcover == null ? void 0 : selectedHeadcover.images) == null ? void 0 : _e.edges) == null ? void 0 : _f[0]) == null ? void 0 : _g.node) == null ? void 0 : _h.url,
                            srcSet: ((_l = (_k = (_j = (_i = selectedHeadcover == null ? void 0 : selectedHeadcover.images) == null ? void 0 : _i.edges) == null ? void 0 : _j[0]) == null ? void 0 : _k.node) == null ? void 0 : _l.small) ? `${(_p = (_o = (_n = (_m = selectedHeadcover == null ? void 0 : selectedHeadcover.images) == null ? void 0 : _m.edges) == null ? void 0 : _n[0]) == null ? void 0 : _o.node) == null ? void 0 : _p.small} 300w` : void 0,
                            alt: (selectedHeadcover == null ? void 0 : selectedHeadcover.title) || "Headcover image",
                            className: "max-h-full max-w-full object-contain",
                            width: 150,
                            height: 150,
                            loading: "lazy"
                          }
                        ) }) }) : /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex h-full w-full items-center justify-center bg-shadow-black", children: /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "h5 text-center text-fairway", children: (selectedHeadcover == null ? void 0 : selectedHeadcover.title) || "Includes Headcover" }) }) }),
                        /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "p4 font-sm mt-1 text-center text-xs text-shadow-black", children: "Includes Headcover" })
                      ] });
                    })()
                  ] }),
                  /* @__PURE__ */ jsxRuntimeExports.jsx(
                    DexteritySelector,
                    {
                      label: (labels == null ? void 0 : labels.dexterity) || "Dexterity",
                      labelRight: (labels == null ? void 0 : labels.dexterity_right) || "Right",
                      labelLeft: (labels == null ? void 0 : labels.dexterity_left) || "Left",
                      selectedDexterity,
                      setSelectedDexterity,
                      selectedBaseHead,
                      productDetails,
                      getSelectedHeadComponents,
                      selectedHosel
                    }
                  ),
                  /* @__PURE__ */ jsxRuntimeExports.jsx(
                    HeadModelColorSelector,
                    {
                      labelModel: (labels == null ? void 0 : labels.head_model) || "Head Model",
                      labelColor: (labels == null ? void 0 : labels.head_color) || "Head Color",
                      headModelTranslationsValues,
                      headColorTranslationsValues,
                      selectedModel,
                      setSelectedModel,
                      selectedColor,
                      setSelectedColor,
                      availableModels,
                      availableModelsForColor,
                      availableColors,
                      availableColorsForModel,
                      baseHeads
                    }
                  ),
                  clubType === "putters" && selectedBaseHead && getAvailableHosels(selectedBaseHead).length > 0 && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mb-4", children: [
                    /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "p4 mb-2 text-shadow-black", children: labels.select_hosel || "Hosel" }),
                    /* @__PURE__ */ jsxRuntimeExports.jsx(
                      Mo,
                      {
                        value: selectedHosel,
                        onChange: (hosel) => handleMainConfigChange(() => setSelectedHosel(hosel)),
                        children: /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "relative", children: [
                          /* @__PURE__ */ jsxRuntimeExports.jsxs(Mo.Button, { className: "relative w-full cursor-pointer rounded border bg-white py-2 pl-3 pr-10 text-left focus:outline-none focus:ring-2 focus:ring-shadow-black sm:text-sm", children: [
                            /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "block truncate", children: selectedHosel || "Select a Hosel" }),
                            /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                              "svg",
                              {
                                className: "h-5 w-5 text-gray-400",
                                xmlns: "http://www.w3.org/2000/svg",
                                viewBox: "0 0 20 20",
                                fill: "currentColor",
                                "aria-hidden": "true",
                                children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                                  "path",
                                  {
                                    fillRule: "evenodd",
                                    d: "M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z",
                                    clipRule: "evenodd"
                                  }
                                )
                              }
                            ) })
                          ] }),
                          /* @__PURE__ */ jsxRuntimeExports.jsx(Mo.Options, { className: "absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm", children: getAvailableHosels(selectedBaseHead).length === 0 ? /* @__PURE__ */ jsxRuntimeExports.jsx(
                            Mo.Option,
                            {
                              value: "",
                              disabled: true,
                              className: "relative cursor-not-allowed py-2 pl-10 pr-4 text-gray-400",
                              children: /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "block truncate", children: "No hosels available" })
                            }
                          ) : getAvailableHosels(selectedBaseHead).map((hosel) => {
                            const hasComponents = getSelectedHeadComponents(
                              selectedBaseHead,
                              selectedDexterity,
                              selectedBounce,
                              selectedHosel
                            ).length > 0;
                            return /* @__PURE__ */ jsxRuntimeExports.jsx(
                              Mo.Option,
                              {
                                value: hosel,
                                disabled: !hasComponents,
                                className: ({ active }) => `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active ? "bg-shadow-black text-fairway" : "text-gray-900"} ${!hasComponents ? "cursor-not-allowed bg-system-disabled opacity-50" : ""}`,
                                children: ({ selected, active }) => /* @__PURE__ */ jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [
                                  /* @__PURE__ */ jsxRuntimeExports.jsx(
                                    "span",
                                    {
                                      className: `block truncate ${selected ? "font-medium" : "font-normal"}`,
                                      children: hosel
                                    }
                                  ),
                                  selected && /* @__PURE__ */ jsxRuntimeExports.jsx(
                                    "span",
                                    {
                                      className: `absolute inset-y-0 left-0 flex items-center pl-3 ${active ? "text-fairway" : "text-shadow-black"}`,
                                      children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                                        "svg",
                                        {
                                          xmlns: "http://www.w3.org/2000/svg",
                                          className: "h-5 w-5",
                                          viewBox: "0 0 20 20",
                                          fill: "currentColor",
                                          children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                                            "path",
                                            {
                                              fillRule: "evenodd",
                                              d: "M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z",
                                              clipRule: "evenodd"
                                            }
                                          )
                                        }
                                      )
                                    }
                                  )
                                ] })
                              },
                              hosel
                            );
                          }) })
                        ] })
                      }
                    )
                  ] }),
                  /* @__PURE__ */ jsxRuntimeExports.jsx(
                    LoftSelector,
                    {
                      clubType,
                      labelClubSelection: labels.club_selection,
                      labelIncludedItems: labels.included_items,
                      selectedBaseHead,
                      selectedLofts,
                      setSelectedLofts,
                      selectedDexterity,
                      productDetails,
                      getAvailableLofts,
                      isLoftAvailable,
                      getCompositionLofts,
                      formatLoftDisplay,
                      handleLoftSelection,
                      selectedComposition,
                      setSelectedComposition
                    }
                  ),
                  clubType === "wedges" && /* @__PURE__ */ jsxRuntimeExports.jsx(
                    BounceSelector,
                    {
                      label: labels.select_bounce,
                      clubType,
                      selectedLofts,
                      selectedBaseHead,
                      selectedDexterity,
                      productDetails,
                      selectedBounce,
                      setSelectedBounce,
                      getAvailableBounces
                    }
                  ),
                  /* @__PURE__ */ jsxRuntimeExports.jsx(
                    LoftBuildSpecSelector,
                    {
                      label: labels.club_loft,
                      tooltipTitle: tooltips.club_loft.title,
                      tooltipText: tooltips.club_loft.text,
                      tooltipImage: tooltips.club_loft.image,
                      clubLoftTranslationsValues,
                      selectedBaseHead,
                      loftBuildSpecs,
                      selectedLoftBuildSpec,
                      setSelectedLoftBuildSpec
                    }
                  ),
                  /* @__PURE__ */ jsxRuntimeExports.jsx(
                    LieBuildSpecSelector,
                    {
                      label: labels.club_lie,
                      tooltipTitle: tooltips.club_lie.title,
                      tooltipText: tooltips.club_lie.text,
                      tooltipImage: tooltips.club_lie.image,
                      clubLieTranslationsValues,
                      selectedBaseHead,
                      lieBuildSpecs,
                      selectedLieBuildSpec,
                      setSelectedLieBuildSpec
                    }
                  )
                ] }) })
              ] });
            } }),
            /* @__PURE__ */ jsxRuntimeExports.jsx(je, { defaultOpen: activeDisclosurePanel === "shaft", children: ({ open, close }) => {
              var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _A, _B, _C, _D, _E, _F;
              shaftPanelRef.current = close;
              return /* @__PURE__ */ jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [
                /* @__PURE__ */ jsxRuntimeExports.jsxs(
                  je.Button,
                  {
                    className: disclosureButtonClasses,
                    onClick: () => handlePanelChange("shaft"),
                    children: [
                      /* @__PURE__ */ jsxRuntimeExports.jsxs(
                        "span",
                        {
                          className: `h4 ${activeDisclosurePanel === "shaft" ? "text-swing-red" : "text-shadow-black group-hover:text-grey-v1"}`,
                          children: [
                            labels.shaft,
                            " ",
                            hasModifications(hasManualShaftChanges) && "*"
                          ]
                        }
                      ),
                      /* @__PURE__ */ jsxRuntimeExports.jsx(ChevronDown, { className: `${open ? "rotate-180 transform" : ""} h-5 w-5 origin-center text-shadow-black transition-transform` })
                    ]
                  }
                ),
                /* @__PURE__ */ jsxRuntimeExports.jsxs(je.Panel, { children: [
                  ((_d = (_c = (_b = (_a = selectedShaft == null ? void 0 : selectedShaft.images) == null ? void 0 : _a.edges) == null ? void 0 : _b[0]) == null ? void 0 : _c.node) == null ? void 0 : _d.url) ? /* @__PURE__ */ jsxRuntimeExports.jsx(
                    "img",
                    {
                      src: (_h = (_g = (_f = (_e = selectedShaft == null ? void 0 : selectedShaft.images) == null ? void 0 : _e.edges) == null ? void 0 : _f[0]) == null ? void 0 : _g.node) == null ? void 0 : _h.url,
                      srcSet: ((_l = (_k = (_j = (_i = selectedShaft == null ? void 0 : selectedShaft.images) == null ? void 0 : _i.edges) == null ? void 0 : _j[0]) == null ? void 0 : _k.node) == null ? void 0 : _l.small) ? `${(_p = (_o = (_n = (_m = selectedShaft == null ? void 0 : selectedShaft.images) == null ? void 0 : _m.edges) == null ? void 0 : _n[0]) == null ? void 0 : _o.node) == null ? void 0 : _p.small} 300w,
                                         ${((_t = (_s = (_r = (_q = selectedShaft == null ? void 0 : selectedShaft.images) == null ? void 0 : _q.edges) == null ? void 0 : _r[0]) == null ? void 0 : _s.node) == null ? void 0 : _t.medium) || ((_x = (_w = (_v = (_u = selectedShaft == null ? void 0 : selectedShaft.images) == null ? void 0 : _u.edges) == null ? void 0 : _v[0]) == null ? void 0 : _w.node) == null ? void 0 : _x.url)} 600w,
                                         ${((_B = (_A = (_z = (_y = selectedShaft == null ? void 0 : selectedShaft.images) == null ? void 0 : _y.edges) == null ? void 0 : _z[0]) == null ? void 0 : _A.node) == null ? void 0 : _B.large) || ((_F = (_E = (_D = (_C = selectedShaft == null ? void 0 : selectedShaft.images) == null ? void 0 : _C.edges) == null ? void 0 : _D[0]) == null ? void 0 : _E.node) == null ? void 0 : _F.url)} 1200w` : void 0,
                      sizes: "(max-width: 768px) 100vw, 472px",
                      alt: (selectedShaft == null ? void 0 : selectedShaft.title) || "Shaft image",
                      className: "h-full max-h-[182px] w-full object-cover",
                      loading: "lazy"
                    }
                  ) : /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex h-[182px] w-full max-w-[472px] items-center justify-center bg-shadow-black", children: /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "h4 text-center text-fairway", children: (selectedShaft == null ? void 0 : selectedShaft.title) || "Select a Shaft" }) }),
                  /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "py-4", children: [
                    /* @__PURE__ */ jsxRuntimeExports.jsx(
                      ShaftMaterialSelector,
                      {
                        label: labels.shaft_material,
                        tooltipTitle: tooltips.shaft_material.title,
                        tooltipText: tooltips.shaft_material.text,
                        tooltipImage: tooltips.shaft_material.image,
                        materialTranslationsValues,
                        selectedBaseHead,
                        selectedShaftFlex,
                        selectedShaftMaterial,
                        setSelectedShaftMaterial: handleShaftMaterialChange,
                        shaftMaterials,
                        getAvailableShafts,
                        productDetails
                      }
                    ),
                    /* @__PURE__ */ jsxRuntimeExports.jsx(
                      ShaftFlexSelector,
                      {
                        clubType,
                        label: labels.shaft_flex,
                        shaftFlexTranslationsValues,
                        tooltipTitle: tooltips.shaft_flex.title,
                        tooltipText: tooltips.shaft_flex.text,
                        tooltipImage: tooltips.shaft_flex.image,
                        selectedBaseHead,
                        selectedShaftFlex,
                        setSelectedShaftFlex: handleShaftFlexChange,
                        selectedShaftMaterial,
                        setSelectedShaftMaterial: handleShaftMaterialChange,
                        getAvailableShafts,
                        productDetails
                      }
                    ),
                    /* @__PURE__ */ jsxRuntimeExports.jsx(
                      ShaftSelector,
                      {
                        label: labels.shaft,
                        selectedShaft,
                        setSelectedShaft: handleShaftChange,
                        selectedBaseHead,
                        selectedShaftFlex,
                        selectedShaftMaterial,
                        getAvailableShafts,
                        productDetails,
                        isShaftCompatible,
                        getInventoryInfo,
                        clubType,
                        selectedHeadComponents: getSelectedHeadComponents(
                          selectedBaseHead,
                          selectedDexterity,
                          selectedBounce,
                          selectedHosel
                        )
                      }
                    ),
                    /* @__PURE__ */ jsxRuntimeExports.jsx(
                      LengthBuildSpecSelector,
                      {
                        label: labels.shaft_length,
                        shaftLengthTranslationsValues,
                        tooltipTitle: tooltips.shaft_length.title,
                        tooltipText: tooltips.shaft_length.text,
                        tooltipImage: tooltips.shaft_length.image,
                        selectedBaseHead,
                        selectedLengthBuildSpec,
                        setSelectedLengthBuildSpec,
                        lengthBuildSpecs
                      }
                    )
                  ] })
                ] })
              ] });
            } }),
            /* @__PURE__ */ jsxRuntimeExports.jsx(je, { defaultOpen: activeDisclosurePanel === "grip", children: ({ open, close }) => {
              var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _A, _B, _C, _D, _E, _F, _G, _H, _I, _J, _K, _L, _M, _N, _O, _P, _Q, _R, _S;
              gripPanelRef.current = close;
              return /* @__PURE__ */ jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [
                /* @__PURE__ */ jsxRuntimeExports.jsxs(
                  je.Button,
                  {
                    className: disclosureButtonClasses,
                    onClick: () => handlePanelChange("grip"),
                    children: [
                      /* @__PURE__ */ jsxRuntimeExports.jsxs(
                        "span",
                        {
                          className: `h4 ${activeDisclosurePanel === "grip" ? "text-swing-red" : "text-shadow-black group-hover:text-grey-v1"}`,
                          children: [
                            labels.grip,
                            " ",
                            hasModifications(hasManualGripChanges) && "*"
                          ]
                        }
                      ),
                      /* @__PURE__ */ jsxRuntimeExports.jsx(ChevronDown, { className: `${open ? "rotate-180 transform" : ""} h-5 w-5 origin-center text-shadow-black transition-transform` })
                    ]
                  }
                ),
                /* @__PURE__ */ jsxRuntimeExports.jsxs(je.Panel, { children: [
                  ((_d = (_c = (_b = (_a = selectedGrip == null ? void 0 : selectedGrip.images) == null ? void 0 : _a.edges) == null ? void 0 : _b[0]) == null ? void 0 : _c.node) == null ? void 0 : _d.url) ? /* @__PURE__ */ jsxRuntimeExports.jsx(
                    "img",
                    {
                      src: (_h = (_g = (_f = (_e = selectedGrip == null ? void 0 : selectedGrip.images) == null ? void 0 : _e.edges) == null ? void 0 : _f[0]) == null ? void 0 : _g.node) == null ? void 0 : _h.url,
                      srcSet: ((_l = (_k = (_j = (_i = selectedGrip == null ? void 0 : selectedGrip.images) == null ? void 0 : _i.edges) == null ? void 0 : _j[0]) == null ? void 0 : _k.node) == null ? void 0 : _l.small) ? `${(_p = (_o = (_n = (_m = selectedGrip == null ? void 0 : selectedGrip.images) == null ? void 0 : _m.edges) == null ? void 0 : _n[0]) == null ? void 0 : _o.node) == null ? void 0 : _p.small} 300w,
                                         ${((_t = (_s = (_r = (_q = selectedGrip == null ? void 0 : selectedGrip.images) == null ? void 0 : _q.edges) == null ? void 0 : _r[0]) == null ? void 0 : _s.node) == null ? void 0 : _t.medium) || ((_x = (_w = (_v = (_u = selectedGrip == null ? void 0 : selectedGrip.images) == null ? void 0 : _u.edges) == null ? void 0 : _v[0]) == null ? void 0 : _w.node) == null ? void 0 : _x.url)} 600w,
                                         ${((_B = (_A = (_z = (_y = selectedGrip == null ? void 0 : selectedGrip.images) == null ? void 0 : _y.edges) == null ? void 0 : _z[0]) == null ? void 0 : _A.node) == null ? void 0 : _B.large) || ((_F = (_E = (_D = (_C = selectedGrip == null ? void 0 : selectedGrip.images) == null ? void 0 : _C.edges) == null ? void 0 : _D[0]) == null ? void 0 : _E.node) == null ? void 0 : _F.url)} 1200w` : void 0,
                      sizes: "(max-width: 768px) 100vw, 472px",
                      alt: ((_H = (_G = selectedGrip == null ? void 0 : selectedGrip.meta) == null ? void 0 : _G.find((m) => (m == null ? void 0 : m.key) === "display_title")) == null ? void 0 : _H.value) || (selectedGrip == null ? void 0 : selectedGrip.title) || "Grip image",
                      className: "h-full w-full object-contain",
                      loading: "lazy"
                    }
                  ) : /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex h-[182px] w-full max-w-[472px] items-center justify-center bg-shadow-black", children: /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "h4 text-center text-fairway", children: ((_J = (_I = selectedGrip == null ? void 0 : selectedGrip.meta) == null ? void 0 : _I.find((m) => (m == null ? void 0 : m.key) === "display_title")) == null ? void 0 : _J.value) || (selectedGrip == null ? void 0 : selectedGrip.title) || "Select a Grip" }) }),
                  /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "py-4", children: [
                    /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "mt-4", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                      GripSizeSelector,
                      {
                        clubType,
                        label: labels.grip_size,
                        selectedGripSize,
                        onGripSizeChange: handleGripSizeChange,
                        gripSizeTranslationsValues,
                        tooltipTitle: tooltips.grip_size.title,
                        tooltipText: tooltips.grip_size.text,
                        tooltipImage: tooltips.grip_size.image
                      }
                    ) }),
                    /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mb-6", children: [
                      /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "p4 mb-2 text-shadow-black", children: labels.grip || "Grip" }),
                      /* @__PURE__ */ jsxRuntimeExports.jsx(Mo, { value: selectedGrip, onChange: handleGripChange, children: /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "relative", children: [
                        /* @__PURE__ */ jsxRuntimeExports.jsxs(Mo.Button, { className: "relative w-full cursor-pointer rounded border bg-white py-2 pl-3 pr-10 text-left focus:outline-none focus:ring-2 focus:ring-shadow-black sm:text-sm", children: [
                          /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex flex-wrap pr-8", children: /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "whitespace-normal break-words", children: selectedGrip ? `${((_L = (_K = selectedGrip == null ? void 0 : selectedGrip.meta) == null ? void 0 : _K.find((m) => (m == null ? void 0 : m.key) === "display_title")) == null ? void 0 : _L.value) || ((_M = selectedGrip == null ? void 0 : selectedGrip.display_title) == null ? void 0 : _M.value) || (selectedGrip == null ? void 0 : selectedGrip.title) || "Loading..."} ${((_Q = (_P = (_O = (_N = selectedGrip == null ? void 0 : selectedGrip.variants) == null ? void 0 : _N.nodes) == null ? void 0 : _O[0]) == null ? void 0 : _P.price) == null ? void 0 : _Q.amount) && parseFloat(selectedGrip.variants.nodes[0].price.amount) > 0 ? `(+${formatPrice(
                            parseFloat(selectedGrip.variants.nodes[0].price.amount),
                            selectedGrip.variants.nodes[0].price.currencyCode
                          )})` : ""}` : "Select a Grip" }) }),
                          /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                            "svg",
                            {
                              className: "h-5 w-5 text-gray-400",
                              xmlns: "http://www.w3.org/2000/svg",
                              viewBox: "0 0 20 20",
                              fill: "currentColor",
                              "aria-hidden": "true",
                              children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                                "path",
                                {
                                  fillRule: "evenodd",
                                  d: "M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z",
                                  clipRule: "evenodd"
                                }
                              )
                            }
                          ) })
                        ] }),
                        /* @__PURE__ */ jsxRuntimeExports.jsx(Mo.Options, { className: "absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm", children: availableGrips.filter((grip) => {
                          var _a2, _b2;
                          const inventory = getInventoryInfo(grip);
                          const isInStock = inventory.available;
                          const gripDexterity = (_b2 = (_a2 = grip == null ? void 0 : grip.meta) == null ? void 0 : _a2.find(
                            (m) => (m == null ? void 0 : m.key) === "dexterity"
                          )) == null ? void 0 : _b2.value;
                          const isDexterityCompatible = !gripDexterity || gripDexterity === selectedDexterity;
                          return isInStock && isDexterityCompatible;
                        }).map((grip) => /* @__PURE__ */ jsxRuntimeExports.jsx(
                          Mo.Option,
                          {
                            className: ({ active }) => `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active ? "bg-shadow-black text-fairway" : "text-gray-900"}`,
                            value: grip,
                            children: ({ selected, active }) => {
                              var _a2, _b2, _c2, _d2, _e2, _f2;
                              return /* @__PURE__ */ jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [
                                /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex flex-wrap pr-8", children: /* @__PURE__ */ jsxRuntimeExports.jsxs(
                                  "span",
                                  {
                                    className: `whitespace-normal break-words ${selected ? "font-medium" : "font-normal"}`,
                                    children: [
                                      ((_b2 = (_a2 = grip == null ? void 0 : grip.meta) == null ? void 0 : _a2.find((m) => (m == null ? void 0 : m.key) === "display_title")) == null ? void 0 : _b2.value) || (grip == null ? void 0 : grip.title) || "Loading...",
                                      ((_f2 = (_e2 = (_d2 = (_c2 = grip == null ? void 0 : grip.variants) == null ? void 0 : _c2.nodes) == null ? void 0 : _d2[0]) == null ? void 0 : _e2.price) == null ? void 0 : _f2.amount) && parseFloat(grip.variants.nodes[0].price.amount) > 0 && ` (+${formatPrice(
                                        parseFloat(grip.variants.nodes[0].price.amount),
                                        grip.variants.nodes[0].price.currencyCode
                                      )})`
                                    ]
                                  }
                                ) }),
                                selected ? /* @__PURE__ */ jsxRuntimeExports.jsx(
                                  "span",
                                  {
                                    className: `absolute inset-y-0 left-0 flex items-center pl-3 ${active ? "text-fairway" : "text-shadow-black"}`,
                                    children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                                      "svg",
                                      {
                                        xmlns: "http://www.w3.org/2000/svg",
                                        className: "h-5 w-5",
                                        viewBox: "0 0 20 20",
                                        fill: "currentColor",
                                        children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                                          "path",
                                          {
                                            fillRule: "evenodd",
                                            d: "M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z",
                                            clipRule: "evenodd"
                                          }
                                        )
                                      }
                                    )
                                  }
                                ) : null
                              ] });
                            }
                          },
                          grip.id
                        )) })
                      ] }) }),
                      selectedGrip && ((_S = (_R = selectedGrip == null ? void 0 : selectedGrip.meta) == null ? void 0 : _R.find((m) => (m == null ? void 0 : m.key) === "product_details")) == null ? void 0 : _S.value) && /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "mx-4 mb-8 mt-4", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                        "div",
                        {
                          className: "rte",
                          dangerouslySetInnerHTML: {
                            __html: purify.sanitize(
                              selectedGrip.meta.find((m) => (m == null ? void 0 : m.key) === "product_details").value
                            )
                          }
                        }
                      ) })
                    ] }),
                    selectedBaseHead && hasTorqueWrench && /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "mt-4", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                      TorqueWrenchSelector,
                      {
                        label: labels.add_torque_wrench,
                        includeTorqueWrench,
                        handleTorqueWrenchChange,
                        torqueWrenchProduct
                      }
                    ) })
                  ] })
                ] })
              ] });
            } })
          ] }) }),
          /* @__PURE__ */ jsxRuntimeExports.jsx(
            ClubConfiguratorFooter,
            {
              totalPrice,
              labels,
              formattedTotalPrice,
              formattedTotalCompareAtPrice,
              totalCompareAtPrice,
              handleResetToDefaults,
              onClose,
              handleAddToCart,
              addToCartButtonState
            }
          )
        ] }) })
      }
    ) }) }) })
  ] }) });
};
const HoselList = ({ listValue, productDetails, getInventoryInfo, selectedHosel }) => {
  try {
    const hosels = Array.isArray(listValue) ? listValue : JSON.parse(listValue || "[]");
    if (!hosels.length) {
      return /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "text-gray-500", children: "No hosels available" });
    }
    return hosels.map((hoselOrGid) => {
      var _a, _b, _c, _d, _e;
      const hosel = typeof hoselOrGid === "string" ? productDetails[hoselOrGid] : hoselOrGid;
      const gid = typeof hoselOrGid === "string" ? hoselOrGid : hosel.id;
      const inventory = getInventoryInfo(hosel);
      const isOutOfStock = !inventory.available || inventory.quantity === 0;
      const isSelected = selectedHosel === ((_b = (_a = hosel == null ? void 0 : hosel.meta) == null ? void 0 : _a.find((m) => (m == null ? void 0 : m.key) === "club_component_mapping")) == null ? void 0 : _b.value);
      return /* @__PURE__ */ jsxRuntimeExports.jsxs(
        "div",
        {
          className: `mb-2 w-fit border-4 p-2 ${isSelected ? "border-pink-500" : ""} ${isOutOfStock ? "border-red-200 bg-red-50" : "border-green-500 bg-green-100"}`,
          children: [
            /* @__PURE__ */ jsxRuntimeExports.jsx("div", { children: /* @__PURE__ */ jsxRuntimeExports.jsx("span", { children: (hosel == null ? void 0 : hosel.title) || /* @__PURE__ */ jsxRuntimeExports.jsx("span", { children: "Loading..." }) }) }),
            /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "text-sm", children: gid }),
            /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mt-1 text-sm", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold text-blue-600", children: "Mapping: " }),
              (() => {
                var _a2, _b2;
                try {
                  const mapping = (_b2 = (_a2 = hosel == null ? void 0 : hosel.meta) == null ? void 0 : _a2.find((m) => (m == null ? void 0 : m.key) === "club_component_mapping")) == null ? void 0 : _b2.value;
                  if (!mapping) return "N/A";
                  return mapping;
                } catch (error) {
                  console.error("Error getting hosel mapping:", error);
                  return "N/A";
                }
              })()
            ] }),
            /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mt-1", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "Stock: " }),
              inventory.available ? /* @__PURE__ */ jsxRuntimeExports.jsxs("span", { className: `font-medium ${inventory.quantity > 0 ? "text-green-500" : "text-swing-red"}`, children: [
                inventory.quantity,
                " available"
              ] }) : /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-medium text-system-negative", children: "Out of stock" })
            ] }),
            ((_e = (_d = (_c = hosel == null ? void 0 : hosel.variants) == null ? void 0 : _c.nodes) == null ? void 0 : _d[0]) == null ? void 0 : _e.price) && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mt-1 text-sm", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "Price: " }),
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { children: new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: hosel.variants.nodes[0].price.currencyCode || "USD"
              }).format(hosel.variants.nodes[0].price.amount) })
            ] })
          ]
        },
        gid
      );
    });
  } catch (error) {
    console.error("Error loading hosels:", error);
    return /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "text-red-500", children: "Error loading hosels" });
  }
};
const ShaftList = ({ listValue, productDetails, getInventoryInfo }) => {
  try {
    let shafts = [];
    if (Array.isArray(listValue)) {
      shafts = listValue;
    } else if (typeof listValue === "string") {
      try {
        shafts = JSON.parse(listValue || "[]");
      } catch (parseError) {
        console.error("Error parsing listValue:", parseError);
        shafts = [];
      }
    } else if (listValue && typeof listValue === "object") {
      shafts = [listValue];
    } else {
      shafts = [];
    }
    if (shafts.length === 0) {
      return /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "text-gray-500", children: "No shafts available" });
    }
    return shafts.map((shaftOrGid) => {
      var _a, _b, _c, _d, _e;
      const shaft = typeof shaftOrGid === "string" ? productDetails[shaftOrGid] : shaftOrGid;
      const gid = typeof shaftOrGid === "string" ? shaftOrGid : shaft.id;
      const isTapered = ((_c = (_b = (_a = shaft == null ? void 0 : shaft.meta) == null ? void 0 : _a.find((m) => (m == null ? void 0 : m.key) === "tapered_shaft")) == null ? void 0 : _b.value) == null ? void 0 : _c.toLowerCase()) === "true";
      const inventory = getInventoryInfo(shaft);
      const isOutOfStock = !inventory.available;
      const material = (_e = (_d = shaft == null ? void 0 : shaft.meta) == null ? void 0 : _d.find((m) => (m == null ? void 0 : m.key) === "material")) == null ? void 0 : _e.value;
      return /* @__PURE__ */ jsxRuntimeExports.jsxs(
        "div",
        {
          className: `mb-2 w-fit border-4 p-2 ${isOutOfStock ? "border-red-200 bg-red-50" : "border-green-500 bg-green-100"}`,
          children: [
            /* @__PURE__ */ jsxRuntimeExports.jsx("div", { children: /* @__PURE__ */ jsxRuntimeExports.jsx("span", { children: (shaft == null ? void 0 : shaft.title) || /* @__PURE__ */ jsxRuntimeExports.jsx("span", { children: "Loading..." }) }) }),
            /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "text-sm", children: gid }),
            material && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mt-1 text-sm", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "Material: " }),
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { children: material })
            ] }),
            /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mt-1 text-sm", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold text-blue-600", children: "Mapping: " }),
              (() => {
                var _a2, _b2;
                try {
                  const mapping = (_b2 = (_a2 = shaft == null ? void 0 : shaft.meta) == null ? void 0 : _a2.find((m) => (m == null ? void 0 : m.key) === "club_component_mapping")) == null ? void 0 : _b2.value;
                  if (!mapping) return "N/A";
                  const parsedMapping = JSON.parse(mapping);
                  return Object.values(parsedMapping).map((length, index, array) => /* @__PURE__ */ jsxRuntimeExports.jsxs("span", { className: "text-blue-600", children: [
                    length,
                    '"',
                    index < array.length - 1 ? ", " : ""
                  ] }, length));
                } catch (error) {
                  console.error("Error parsing shaft mapping:", error);
                  return "N/A";
                }
              })()
            ] }),
            /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mt-1", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "Stock: " }),
              inventory.available ? /* @__PURE__ */ jsxRuntimeExports.jsxs("span", { className: `font-medium ${inventory.quantity > 0 ? "text-green-500" : "text-swing-red"}`, children: [
                inventory.quantity,
                " available"
              ] }) : /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-medium text-system-negative", children: "Out of stock" })
            ] }),
            isTapered && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mt-2 rounded border border-green-800 bg-white p-2", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "flex items-center justify-center border-none bg-shadow-black p-1 pb-2 text-center text-sm font-medium text-white", children: "Tapered Options:" }),
              renderTaperedOptions(shaft, productDetails, getInventoryInfo)
            ] })
          ]
        },
        gid
      );
    });
  } catch (error) {
    console.error("Error loading products:", error);
    return /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "text-red-500", children: "Error loading products" });
  }
};
const renderTaperedOptions = (shaft, productDetails, getInventoryInfo) => {
  var _a, _b, _c;
  try {
    const taperedShafts = ((_c = (_b = (_a = shaft == null ? void 0 : shaft.taperedShafts) == null ? void 0 : _a.find((metafield) => {
      var _a2, _b2;
      return ((_b2 = (_a2 = metafield == null ? void 0 : metafield.references) == null ? void 0 : _a2.nodes) == null ? void 0 : _b2.length) > 0;
    })) == null ? void 0 : _b.references) == null ? void 0 : _c.nodes) || [];
    if (taperedShafts.length > 0) {
      return /* @__PURE__ */ jsxRuntimeExports.jsx("ul", { className: "mt-1 space-y-2", children: taperedShafts.map((taperedShaft) => {
        var _a2, _b2, _c2;
        const gid = taperedShaft.id;
        const inventory = getInventoryInfo(taperedShaft);
        const isOutOfStock = !inventory.available;
        const material = (_b2 = (_a2 = taperedShaft == null ? void 0 : taperedShaft.meta) == null ? void 0 : _a2.find((m) => (m == null ? void 0 : m.key) === "material")) == null ? void 0 : _b2.value;
        let clubMapping = {};
        try {
          const mappingMeta = (_c2 = taperedShaft == null ? void 0 : taperedShaft.meta) == null ? void 0 : _c2.find((m) => (m == null ? void 0 : m.key) === "club_component_mapping");
          if (mappingMeta == null ? void 0 : mappingMeta.value) {
            clubMapping = JSON.parse(mappingMeta.value);
          }
        } catch (error) {
          console.error("Error parsing club mapping for GID:", gid, error);
        }
        return /* @__PURE__ */ jsxRuntimeExports.jsxs(
          "li",
          {
            className: `my-4 border border-green-800 p-1 pb-2 last:border-0 ${isOutOfStock ? "bg-red-50" : "bg-green-100"}`,
            children: [
              /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { children: [
                /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "font-medium", children: (taperedShaft == null ? void 0 : taperedShaft.title) || "Loading..." }),
                /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "text-sm text-gray-500", children: gid })
              ] }),
              material && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mt-1 text-sm", children: [
                /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "Material: " }),
                /* @__PURE__ */ jsxRuntimeExports.jsx("span", { children: material })
              ] }),
              Object.keys(clubMapping).length > 0 && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mt-1 text-sm", children: [
                /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold text-blue-600", children: "Mapping: " }),
                Object.values(clubMapping).map((length, index, array) => /* @__PURE__ */ jsxRuntimeExports.jsxs("span", { className: "text-blue-600", children: [
                  length,
                  '"',
                  index < array.length - 1 ? ", " : ""
                ] }, length))
              ] }),
              /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "mt-1", children: /* @__PURE__ */ jsxRuntimeExports.jsxs("span", { children: [
                "Stock:",
                " ",
                inventory.available ? /* @__PURE__ */ jsxRuntimeExports.jsxs("span", { className: `font-medium ${inventory.quantity > 0 ? "text-green-500" : "text-swing-red"}`, children: [
                  inventory.quantity,
                  " available"
                ] }) : /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-medium text-system-negative", children: "Out of stock" })
              ] }) })
            ]
          },
          gid
        );
      }) });
    }
  } catch (error) {
    console.error("Error rendering tapered options:", error);
    return /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "mt-2 text-sm text-red-500", children: "Error loading tapered options" });
  }
};
const GripList = ({ listValue, productDetails, getInventoryInfo }) => {
  try {
    let grips = [];
    if (Array.isArray(listValue)) {
      grips = listValue;
    } else if (typeof listValue === "string") {
      try {
        grips = JSON.parse(listValue || "[]");
      } catch (parseError) {
        console.error("Error parsing listValue:", parseError);
        grips = [];
      }
    } else if (listValue && typeof listValue === "object") {
      grips = [listValue];
    } else {
      grips = [];
    }
    if (grips.length === 0) {
      return /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "text-gray-500", children: "No grips available" });
    }
    return grips.map((gripOrGid) => {
      var _a, _b, _c, _d, _e, _f, _g, _h;
      const grip = typeof gripOrGid === "string" ? productDetails[gripOrGid] : gripOrGid;
      const gid = typeof gripOrGid === "string" ? gripOrGid : grip.id;
      const inventory = getInventoryInfo(grip);
      const isOutOfStock = !inventory.available || inventory.quantity === 0;
      const gripSize = (_b = (_a = grip == null ? void 0 : grip.meta) == null ? void 0 : _a.find((m) => (m == null ? void 0 : m.key) === "grip_size")) == null ? void 0 : _b.value;
      const dexterity = (_d = (_c = grip == null ? void 0 : grip.meta) == null ? void 0 : _c.find((m) => (m == null ? void 0 : m.key) === "dexterity")) == null ? void 0 : _d.value;
      return /* @__PURE__ */ jsxRuntimeExports.jsxs(
        "div",
        {
          className: `mb-2 w-fit border-4 p-2 ${isOutOfStock ? "border-red-200 bg-red-50" : "border-green-500 bg-green-100"}`,
          children: [
            /* @__PURE__ */ jsxRuntimeExports.jsx("div", { children: /* @__PURE__ */ jsxRuntimeExports.jsx("span", { children: (grip == null ? void 0 : grip.title) || /* @__PURE__ */ jsxRuntimeExports.jsx("span", { children: "Loading..." }) }) }),
            /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "text-sm", children: gid }),
            gripSize && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mt-1 text-sm", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "Size: " }),
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { children: gripSize })
            ] }),
            dexterity && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mt-1 text-sm", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "Dexterity: " }),
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { children: dexterity })
            ] }),
            /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mt-1 text-sm", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold text-blue-600", children: "Mapping: " }),
              (() => {
                var _a2, _b2;
                try {
                  const mapping = (_b2 = (_a2 = grip == null ? void 0 : grip.meta) == null ? void 0 : _a2.find((m) => (m == null ? void 0 : m.key) === "club_component_mapping")) == null ? void 0 : _b2.value;
                  if (!mapping) return "N/A";
                  const parsedMapping = JSON.parse(mapping);
                  return Object.entries(parsedMapping).map(([key, value], index, array) => /* @__PURE__ */ jsxRuntimeExports.jsxs("span", { className: "text-blue-600", children: [
                    key,
                    ": ",
                    value,
                    index < array.length - 1 ? ", " : ""
                  ] }, key));
                } catch (error) {
                  console.error("Error parsing grip mapping:", error);
                  return "N/A";
                }
              })()
            ] }),
            /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mt-1", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "Stock: " }),
              inventory.available ? /* @__PURE__ */ jsxRuntimeExports.jsxs("span", { className: `font-medium ${inventory.quantity > 0 ? "text-green-500" : "text-swing-red"}`, children: [
                inventory.quantity,
                " available"
              ] }) : /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-medium text-system-negative", children: "Out of stock" })
            ] }),
            ((_f = (_e = grip == null ? void 0 : grip.variants) == null ? void 0 : _e.nodes) == null ? void 0 : _f.length) > 0 && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mt-1 text-sm", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "Price: " }),
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { children: new Intl.NumberFormat(((_g = window.Shopify) == null ? void 0 : _g.locale) || "en-US", {
                style: "currency",
                currency: grip.variants.nodes[0].price.currencyCode || "USD",
                currencyDisplay: "narrowSymbol"
              }).format(parseFloat(grip.variants.nodes[0].price.amount)) })
            ] }),
            ((_h = grip == null ? void 0 : grip.component_product_details) == null ? void 0 : _h.value) && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mt-1 text-sm", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "Details: " }),
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "italic text-gray-600", children: "Has product details" })
            ] })
          ]
        },
        gid
      );
    });
  } catch (error) {
    console.error("Error loading grip products:", error);
    return /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "text-red-500", children: "Error loading grip products" });
  }
};
const HeadComponentList = ({ headComponents, selectedHeads, getInventoryInfo }) => {
  if (!headComponents || headComponents.length === 0) {
    return /* @__PURE__ */ jsxRuntimeExports.jsx("div", { children: "No heads available" });
  }
  return headComponents.map((headComponent, index) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l;
    const inventory = getInventoryInfo(headComponent);
    const isOutOfStock = !inventory.available || inventory.quantity === 0;
    return /* @__PURE__ */ jsxRuntimeExports.jsxs(
      "div",
      {
        className: `head-head mb-2 w-fit max-w-[360px] border-4 p-2 text-gray-600 ${"border-pink-500 text-black"} ${isOutOfStock ? "border-red-200 bg-red-50" : "border-green-500 bg-green-100"} ${""}`,
        children: [
          /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "p4 font-bold", children: headComponent.title }),
          /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "p4", children: headComponent.id }),
          /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "ml-2 text-sm", children: [
            /* @__PURE__ */ jsxRuntimeExports.jsxs("p", { children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "p4 font-bold", children: "Dexterity:" }),
              " ",
              (_b = (_a = headComponent.meta) == null ? void 0 : _a.find((m) => (m == null ? void 0 : m.key) === "dexterity")) == null ? void 0 : _b.value
            ] }),
            /* @__PURE__ */ jsxRuntimeExports.jsxs("p", { children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "p4 font-bold", children: "Loft:" }),
              " ",
              ((_d = (_c = headComponent.meta) == null ? void 0 : _c.find((m) => (m == null ? void 0 : m.key) === "loft")) == null ? void 0 : _d.value) || "N/A"
            ] }),
            ((_f = (_e = headComponent.meta) == null ? void 0 : _e.find((m) => (m == null ? void 0 : m.key) === "bounce")) == null ? void 0 : _f.value) && /* @__PURE__ */ jsxRuntimeExports.jsxs("p", { children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "p4 font-bold", children: "Bounce:" }),
              " ",
              (_h = (_g = headComponent.meta) == null ? void 0 : _g.find((m) => (m == null ? void 0 : m.key) === "bounce")) == null ? void 0 : _h.value
            ] }),
            /* @__PURE__ */ jsxRuntimeExports.jsxs("p", { className: "p4 font-bold", children: [
              "Base Head Mapping:  ",
              (() => {
                var _a2, _b2;
                try {
                  const mapping = JSON.parse(
                    ((_b2 = (_a2 = headComponent == null ? void 0 : headComponent.meta) == null ? void 0 : _a2.find((m) => (m == null ? void 0 : m.key) === "club_component_mapping")) == null ? void 0 : _b2.value) || "{}"
                  );
                  return Object.values(mapping).map((length, index2, array) => /* @__PURE__ */ jsxRuntimeExports.jsxs("span", { className: "text-blue-600", children: [
                    length,
                    '"',
                    index2 < array.length - 1 ? ", " : ""
                  ] }, length));
                } catch (error) {
                  console.error("Error parsing base head mapping:", error);
                  return "N/A";
                }
              })()
            ] }),
            /* @__PURE__ */ jsxRuntimeExports.jsxs("p", { children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "p4 font-bold", children: "Stock:" }),
              " ",
              inventory.available ? /* @__PURE__ */ jsxRuntimeExports.jsxs("span", { className: `font-medium ${inventory.quantity > 0 ? "text-green-500" : "text-swing-red"}`, children: [
                inventory.quantity,
                " available"
              ] }) : /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-medium text-system-negative", children: "Out of stock" })
            ] }),
            /* @__PURE__ */ jsxRuntimeExports.jsxs("p", { className: "", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "p4 font-bold", children: "Length:" }),
              " ",
              ((_j = (_i = headComponent.meta) == null ? void 0 : _i.find((m) => (m == null ? void 0 : m.key) === "standard_length")) == null ? void 0 : _j.value) || "N/A",
              '"'
            ] }),
            /* @__PURE__ */ jsxRuntimeExports.jsxs("p", { className: "", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "p4 font-bold", children: "Weight Config:" }),
              " ",
              ((_l = (_k = headComponent.meta) == null ? void 0 : _k.find((m) => (m == null ? void 0 : m.key) === "default_weight_configuration")) == null ? void 0 : _l.value) || "N/A"
            ] })
          ] })
        ]
      },
      index
    );
  });
};
const PatchList = ({ listValue, productDetails, getInventoryInfo }) => {
  try {
    const patches = Array.isArray(listValue) ? listValue : JSON.parse(listValue || "[]");
    return patches.map((patchOrGid) => {
      var _a, _b, _c;
      const patch = typeof patchOrGid === "string" ? productDetails[patchOrGid] : patchOrGid;
      const gid = typeof patchOrGid === "string" ? patchOrGid : patch.id;
      const inventory = getInventoryInfo(patch);
      const isOutOfStock = !inventory.available || inventory.quantity === 0;
      return /* @__PURE__ */ jsxRuntimeExports.jsxs(
        "div",
        {
          className: `mb-2 w-fit border-4 p-2 ${isOutOfStock ? "border-red-200 bg-red-50" : "border-green-500 bg-green-100"}`,
          children: [
            /* @__PURE__ */ jsxRuntimeExports.jsx("div", { children: /* @__PURE__ */ jsxRuntimeExports.jsx("span", { children: (patch == null ? void 0 : patch.title) || /* @__PURE__ */ jsxRuntimeExports.jsx("span", { children: "Loading..." }) }) }),
            /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "text-sm", children: gid }),
            /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mt-1", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "Stock: " }),
              inventory.available ? /* @__PURE__ */ jsxRuntimeExports.jsxs("span", { className: `font-medium ${inventory.quantity > 0 ? "text-green-500" : "text-swing-red"}`, children: [
                inventory.quantity,
                " available"
              ] }) : /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-medium text-system-negative", children: "Out of stock" })
            ] }),
            ((_c = (_b = (_a = patch == null ? void 0 : patch.variants) == null ? void 0 : _a.nodes) == null ? void 0 : _b[0]) == null ? void 0 : _c.price) && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mt-1 text-sm", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "Price: " }),
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { children: new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: patch.variants.nodes[0].price.currencyCode || "USD"
              }).format(patch.variants.nodes[0].price.amount) })
            ] })
          ]
        },
        gid
      );
    });
  } catch (error) {
    console.error("Error loading patches:", error);
    return /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "text-red-500", children: "Error loading patches" });
  }
};
const TorqueWrenchList = ({ listValue, productDetails, getInventoryInfo }) => {
  try {
    const torqueWrenches = Array.isArray(listValue) ? listValue : JSON.parse(listValue || "[]");
    return torqueWrenches.map((wrenchOrGid) => {
      var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j;
      const wrench = typeof wrenchOrGid === "string" ? productDetails[wrenchOrGid] : wrenchOrGid;
      const gid = typeof wrenchOrGid === "string" ? wrenchOrGid : wrench.id;
      const inventory = getInventoryInfo(wrench);
      const isOutOfStock = !inventory.available || inventory.quantity === 0;
      return /* @__PURE__ */ jsxRuntimeExports.jsxs(
        "div",
        {
          className: `mb-2 w-fit border-4 p-2 ${isOutOfStock ? "border-red-200 bg-red-50" : "border-green-500 bg-green-100"}`,
          children: [
            /* @__PURE__ */ jsxRuntimeExports.jsx("div", { children: /* @__PURE__ */ jsxRuntimeExports.jsx("span", { children: (wrench == null ? void 0 : wrench.title) || /* @__PURE__ */ jsxRuntimeExports.jsx("span", { children: "Loading..." }) }) }),
            /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "text-sm", children: gid }),
            /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mt-1", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "Stock: " }),
              inventory.available ? /* @__PURE__ */ jsxRuntimeExports.jsxs("span", { className: `font-medium ${inventory.quantity > 0 ? "text-green-500" : "text-swing-red"}`, children: [
                inventory.quantity,
                " available"
              ] }) : /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-medium text-system-negative", children: "Out of stock" })
            ] }),
            ((_c = (_b = (_a = wrench == null ? void 0 : wrench.variants) == null ? void 0 : _a.nodes) == null ? void 0 : _b[0]) == null ? void 0 : _c.price) && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mt-1 text-sm", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "Price: " }),
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { children: new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: wrench.variants.nodes[0].price.currencyCode || "USD"
              }).format(wrench.variants.nodes[0].price.amount) })
            ] }),
            ((_g = (_f = (_e = (_d = wrench == null ? void 0 : wrench.images) == null ? void 0 : _d.edges) == null ? void 0 : _e[0]) == null ? void 0 : _f.node) == null ? void 0 : _g.url) && /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "mt-2", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
              "img",
              {
                src: wrench.images.edges[0].node.url,
                alt: wrench.images.edges[0].node.altText || wrench.title,
                className: "h-20 w-20 object-contain"
              }
            ) }),
            ((_j = (_i = (_h = wrench == null ? void 0 : wrench.variants) == null ? void 0 : _h.nodes) == null ? void 0 : _i[0]) == null ? void 0 : _j.sku) && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mt-1 text-sm", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "SKU: " }),
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { children: wrench.variants.nodes[0].sku })
            ] })
          ]
        },
        gid
      );
    });
  } catch (error) {
    console.error("Error loading torque wrenches:", error);
    return /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "text-red-500", children: "Error loading torque wrenches" });
  }
};
const HeadcoverList = ({ listValue, productDetails, getInventoryInfo }) => {
  try {
    const headcovers = Array.isArray(listValue) ? listValue : JSON.parse(listValue || "[]");
    return headcovers.map((headcoverOrGid) => {
      var _a, _b, _c, _d, _e, _f, _g;
      const headcover = typeof headcoverOrGid === "string" ? productDetails[headcoverOrGid] : headcoverOrGid;
      const gid = typeof headcoverOrGid === "string" ? headcoverOrGid : headcover.id;
      const inventory = getInventoryInfo(headcover);
      const isOutOfStock = !inventory.available || inventory.quantity === 0;
      return /* @__PURE__ */ jsxRuntimeExports.jsxs(
        "div",
        {
          className: `mb-2 w-fit border-4 p-2 ${isOutOfStock ? "border-red-200 bg-red-50" : "border-green-500 bg-green-100"}`,
          children: [
            /* @__PURE__ */ jsxRuntimeExports.jsx("div", { children: /* @__PURE__ */ jsxRuntimeExports.jsx("span", { children: (headcover == null ? void 0 : headcover.title) || /* @__PURE__ */ jsxRuntimeExports.jsx("span", { children: "Loading..." }) }) }),
            /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "text-sm", children: gid }),
            /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mt-1", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "Stock: " }),
              inventory.available ? /* @__PURE__ */ jsxRuntimeExports.jsxs("span", { className: `font-medium ${inventory.quantity > 0 ? "text-green-500" : "text-swing-red"}`, children: [
                inventory.quantity,
                " available"
              ] }) : /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-medium text-system-negative", children: "Out of stock" })
            ] }),
            ((_c = (_b = (_a = headcover == null ? void 0 : headcover.variants) == null ? void 0 : _a.nodes) == null ? void 0 : _b[0]) == null ? void 0 : _c.price) && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mt-1 text-sm", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "Price: " }),
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { children: new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: headcover.variants.nodes[0].price.currencyCode || "USD"
              }).format(headcover.variants.nodes[0].price.amount) })
            ] }),
            /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mt-1", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold text-blue-600", children: "Mapping: " }),
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "text-blue-600", children: (() => {
                var _a2, _b2;
                try {
                  const mapping = (_b2 = (_a2 = headcover == null ? void 0 : headcover.meta) == null ? void 0 : _a2.find((m) => (m == null ? void 0 : m.key) === "club_component_mapping")) == null ? void 0 : _b2.value;
                  if (!mapping) return "N/A";
                  const parsedMapping = JSON.parse(mapping);
                  return Object.values(parsedMapping).map((length, index, array) => /* @__PURE__ */ jsxRuntimeExports.jsxs("span", { className: "text-blue-600", children: [
                    length,
                    '"',
                    index < array.length - 1 ? ", " : ""
                  ] }, length));
                } catch (error) {
                  console.error("Error parsing headcover mapping:", error);
                  return "N/A";
                }
              })() })
            ] }),
            ((_g = (_f = (_e = (_d = headcover == null ? void 0 : headcover.images) == null ? void 0 : _d.edges) == null ? void 0 : _e[0]) == null ? void 0 : _f.node) == null ? void 0 : _g.url) && /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "mt-2", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
              "img",
              {
                src: headcover.images.edges[0].node.url,
                alt: headcover.images.edges[0].node.altText || headcover.title,
                className: "h-20 w-20 object-contain"
              }
            ) })
          ]
        },
        gid
      );
    });
  } catch (error) {
    console.error("Error loading headcovers:", error);
    return /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "text-red-500", children: "Error loading headcovers" });
  }
};
const ValidationMessage = ({
  cartValidation,
  selectedForCartItems,
  type = "error",
  children,
  validationMessages = {}
}) => {
  const [showMessage, setShowMessage] = reactExports.useState(false);
  const [isClosed, setIsClosed] = reactExports.useState(false);
  reactExports.useEffect(() => {
    const timeoutId = setTimeout(() => {
      setShowMessage(cartValidation.hasIssue);
      setIsClosed(false);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [cartValidation.hasIssue]);
  if (!showMessage || isClosed || children === void 0 && cartValidation.items.length === 0 && (selectedForCartItems.length === 0 || cartValidation.isBalanced) && !cartValidation.exceedsMaxItems && !cartValidation.exceedsMaxConfigurations) {
    return null;
  }
  const messageTypeClasses = {
    error: "bg-system-negative text-white",
    warning: "bg-system-warning text-shadow-black"
  };
  const messageClass = messageTypeClasses[type] || messageTypeClasses.error;
  return /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: `p4 mb-4 px-4 py-1 font-medium ${messageClass} relative`, children: [
    /* @__PURE__ */ jsxRuntimeExports.jsx(
      "button",
      {
        onClick: () => setIsClosed(true),
        className: "absolute right-1 top-1 rounded-full p-1 hover:opacity-80 focus:outline-none focus:ring-1 focus:ring-white",
        "aria-label": "Close message",
        children: /* @__PURE__ */ jsxRuntimeExports.jsx("svg", { className: "h-4 w-4", fill: "currentColor", viewBox: "0 0 20 20", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
          "path",
          {
            fillRule: "evenodd",
            d: "M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z",
            clipRule: "evenodd"
          }
        ) })
      }
    ),
    children ? /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "pr-6 font-medium", children }) : /* @__PURE__ */ jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [
      cartValidation.exceedsMaxItems && /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "pr-6 font-medium", children: validationMessages.max_items || "Adding these items would exceed the maximum limit of bundled items in your cart." }),
      cartValidation.exceedsMaxConfigurations && /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "pr-6 font-medium", children: validationMessages.max_configurations || "You have reached the maximum limit of configurations in your cart." }),
      !cartValidation.isBalanced && selectedForCartItems.length !== 0 && /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "pr-6", children: validationMessages.not_balanced || "This configuration is not available. Please adjust your selection." }),
      cartValidation.items.map((item, index) => /* @__PURE__ */ jsxRuntimeExports.jsxs("p", { className: "pr-6 font-medium", children: [
        item.title,
        " ",
        validationMessages.insufficient_stock || item.title + " has insufficient stock."
      ] }, index)),
      cartValidation.items.length > 0 && /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "mt-1 pr-6 text-shadow-black", children: validationMessages.adjust_selection || "Please adjust your selection." })
    ] })
  ] });
};
const PromoMessaging = ({ productData }) => {
  var _a, _b, _c, _d, _e, _f;
  const isDiscountEligible = (_c = (_b = (_a = window.clubConfiguratorData) == null ? void 0 : _a.translations) == null ? void 0 : _b.pricing_info) == null ? void 0 : _c.is_discount_eligible;
  if (!isDiscountEligible) {
    return null;
  }
  const pricingInfo = (_e = (_d = window.clubConfiguratorData) == null ? void 0 : _d.translations) == null ? void 0 : _e.pricing_info;
  const isHeroesForEveryone = ((_f = productData == null ? void 0 : productData.tags) == null ? void 0 : _f.includes("heroes-for-everyone")) && (pricingInfo == null ? void 0 : pricingInfo.labels_pricing_hero_for_everyone_text);
  if (!(pricingInfo == null ? void 0 : pricingInfo.labels_pricing_hero_for_everyone_text) && !(pricingInfo == null ? void 0 : pricingInfo.labels_pricing_non_hero_best_offer_text)) {
    return null;
  }
  return /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mb-4 flex items-center", children: [
    /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "mr-1 size-[12px] text-swing-red", children: /* @__PURE__ */ jsxRuntimeExports.jsx("svg", { className: "icon icon-discount", viewBox: "0 0 12 12", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
      "path",
      {
        fill: "currentColor",
        fillRule: "evenodd",
        d: "M7 0h3a2 2 0 0 1 2 2v3a1 1 0 0 1-.3.7l-6 6a1 1 0 0 1-1.4 0l-4-4a1 1 0 0 1 0-1.4l6-6A1 1 0 0 1 7 0m2 2a1 1 0 1 0 2 0 1 1 0 0 0-2 0",
        clipRule: "evenodd"
      }
    ) }) }),
    isHeroesForEveryone ? /* @__PURE__ */ jsxRuntimeExports.jsx(
      "span",
      {
        className: "text-xs inline-richtext",
        dangerouslySetInnerHTML: { __html: pricingInfo == null ? void 0 : pricingInfo.labels_pricing_hero_for_everyone_text }
      }
    ) : (pricingInfo == null ? void 0 : pricingInfo.best_value_link) ? /* @__PURE__ */ jsxRuntimeExports.jsx(
      "a",
      {
        href: pricingInfo.best_value_link,
        className: "text-xs font-normal hover:underline focus:underline",
        children: pricingInfo == null ? void 0 : pricingInfo.labels_pricing_non_hero_best_offer_text
      }
    ) : /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "text-xs font-normal", children: pricingInfo == null ? void 0 : pricingInfo.labels_pricing_non_hero_best_offer_text })
  ] });
};
const MAX_BUNDLED_COMPONENTS_IN_CART = (() => {
  var _a;
  try {
    if ((_a = window.clubConfiguratorData) == null ? void 0 : _a.cartLimits) {
      return window.clubConfiguratorData.cartLimits.maxBundledComponentsInCart || 105;
    }
    return 105;
  } catch (error) {
    console.error("Error accessing cart limits:", error);
    return 105;
  }
})();
const MAX_BUNDLE_CONFIGURATIONS_IN_CART = (() => {
  var _a;
  try {
    if ((_a = window.clubConfiguratorData) == null ? void 0 : _a.cartLimits) {
      return window.clubConfiguratorData.cartLimits.maxBundleConfigurationInCart || 15;
    }
    return 15;
  } catch (error) {
    console.error("Error accessing cart limits:", error);
    return 15;
  }
})();
const ClubConfiguratorApp = () => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _A, _B, _C, _D, _E, _F, _G, _H, _I, _J, _K, _L, _M;
  const splideRef = reactExports.useRef(null);
  const [productData, setProductData] = reactExports.useState(null);
  const [hasManualShaftChanges, setHasManualShaftChanges] = reactExports.useState({
    material: false,
    flex: false,
    shaft: false
  });
  const [hasManualGripChanges, setHasManualGripChanges] = reactExports.useState({
    grip: false,
    size: false
  });
  const [hasManualBuildSpecChanges, setHasManualBuildSpecChanges] = reactExports.useState({
    loft: false,
    lie: false
  });
  const [isLoading, setIsLoading] = reactExports.useState(true);
  const [error, setError] = reactExports.useState(null);
  const [isLoadingOnAddToCartButton, setIsLoadingOnAddToCartButton] = reactExports.useState(false);
  const [isAdvancedPanelOpen, setIsAdvancedPanelOpen] = reactExports.useState(false);
  const [isDetailedViewOpen, setIsDetailedViewOpen] = reactExports.useState(false);
  const [isLoadingCart, setIsLoadingCart] = reactExports.useState(true);
  const [skeletonHidden, setSkeletonHidden] = reactExports.useState(false);
  const [activeDisclosurePanel, setActiveDisclosurePanel] = reactExports.useState("");
  const [showManualChangesMessage, setShowManualChangesMessage] = reactExports.useState(false);
  const [selectedDexterity, setSelectedDexterity] = reactExports.useState("R");
  const [productDetails, setProductDetails] = reactExports.useState({});
  const [selectedModel, setSelectedModel] = reactExports.useState("");
  const [selectedColor, setSelectedColor] = reactExports.useState("");
  const [selectedBounce, setSelectedBounce] = reactExports.useState("");
  const [selectedBaseHead, setSelectedBaseHead] = reactExports.useState(null);
  const [selectedLofts, setSelectedLofts] = reactExports.useState([]);
  const [selectedComposition, setSelectedComposition] = reactExports.useState("");
  const [selectedShaftFlex, setSelectedShaftFlex] = reactExports.useState("");
  const [selectedShaft, setSelectedShaft] = reactExports.useState("");
  const [selectedTorqueWrench, setSelectedTorqueWrench] = reactExports.useState(null);
  const [selectedForCartItems, setSelectedForCartItems] = reactExports.useState([]);
  const [selectedHeadcover, setSelectedHeadcover] = reactExports.useState("");
  const [selectedShaftMaterial, setSelectedShaftMaterial] = reactExports.useState("");
  const [selectedHosel, setSelectedHosel] = reactExports.useState("");
  const [selectedLoftBuildSpec, setSelectedLoftBuildSpec] = reactExports.useState("Standard");
  const [selectedLieBuildSpec, setSelectedLieBuildSpec] = reactExports.useState("Standard");
  const [selectedLengthBuildSpec, setSelectedLengthBuildSpec] = reactExports.useState("Standard");
  const [selectedDefaultWeightConfig, setSelectedDefaultWeightConfig] = reactExports.useState("");
  const [selectedGripSize, setSelectedGripSize] = reactExports.useState("standard");
  const [selectedGrip, setSelectedGrip] = reactExports.useState("");
  const [mainSlider, setMainSlider] = reactExports.useState(null);
  const [thumbsSlider, setThumbsSlider] = reactExports.useState(null);
  const [cachedImages, setCachedImages] = reactExports.useState({});
  const [includeTorqueWrench, setIncludeTorqueWrench] = reactExports.useState(false);
  const [selectedHeadComponents, setSelectedHeadComponents] = reactExports.useState([]);
  const [cartQuantity, setCartQuantity] = reactExports.useState(0);
  const [configurationInCartCount, setConfigurationInCartCount] = reactExports.useState(0);
  const [uniqueSelectedForCartItems, setUniqueSelectedForCartItems] = reactExports.useState([]);
  const [userSelectedBounce, setUserSelectedBounce] = reactExports.useState(false);
  const [userSelectedShaft, setUserSelectedShaft] = reactExports.useState(false);
  const [userSelectedGrip, setUserSelectedGrip] = reactExports.useState(false);
  const [userSelectedLoft, setUserSelectedLoft] = reactExports.useState(false);
  const params = new URLSearchParams(window.location.search);
  const shopLocale = reactExports.useMemo(() => {
    var _a2, _b2;
    try {
      if ((_b2 = (_a2 = window.clubConfiguratorData) == null ? void 0 : _a2.translations) == null ? void 0 : _b2.shop_locale) {
        return window.clubConfiguratorData.translations.shop_locale;
      }
      console.warn("shop_locale not found in clubConfiguratorData");
      return "en";
    } catch (error2) {
      console.error("Error accessing shop locale:", error2);
      return "en";
    }
  }, []);
  const labels = reactExports.useMemo(() => {
    var _a2, _b2;
    try {
      return ((_b2 = (_a2 = window.clubConfiguratorData) == null ? void 0 : _a2.translations) == null ? void 0 : _b2.labels) || {};
    } catch (error2) {
      console.error("Error accessing labels:", error2);
      return {};
    }
  }, []);
  const validationMessages = reactExports.useMemo(() => {
    var _a2, _b2;
    try {
      return ((_b2 = (_a2 = window.clubConfiguratorData) == null ? void 0 : _a2.translations) == null ? void 0 : _b2.validation_messages) || {};
    } catch (error2) {
      console.error("Error accessing validation messages:", error2);
      return {};
    }
  }, []);
  const cartNoteLabels = reactExports.useMemo(() => {
    var _a2, _b2;
    try {
      return ((_b2 = (_a2 = window.clubConfiguratorData) == null ? void 0 : _a2.translations) == null ? void 0 : _b2.cart_note_labels) || {};
    } catch (error2) {
      console.error("Error accessing cart note labels:", error2);
      return {};
    }
  }, []);
  const tooltips = reactExports.useMemo(() => {
    var _a2, _b2;
    try {
      return ((_b2 = (_a2 = window.clubConfiguratorData) == null ? void 0 : _a2.translations) == null ? void 0 : _b2.tooltips) || {};
    } catch (error2) {
      console.error("Error accessing tooltips:", error2);
      return {};
    }
  }, []);
  const headModelTranslationsValues = reactExports.useMemo(() => {
    var _a2, _b2, _c2;
    if (shopLocale === "en") return {};
    try {
      return ((_c2 = (_b2 = (_a2 = window.clubConfiguratorData) == null ? void 0 : _a2.translations) == null ? void 0 : _b2.values_translations) == null ? void 0 : _c2.head_model) || {};
    } catch (error2) {
      console.error("Error accessing head model translations:", error2);
      return {};
    }
  }, [shopLocale]);
  const headColorTranslationsValues = reactExports.useMemo(() => {
    var _a2, _b2, _c2;
    if (shopLocale === "en") return {};
    try {
      return ((_c2 = (_b2 = (_a2 = window.clubConfiguratorData) == null ? void 0 : _a2.translations) == null ? void 0 : _b2.values_translations) == null ? void 0 : _c2.head_color) || {};
    } catch (error2) {
      console.error("Error accessing head color translations:", error2);
      return {};
    }
  }, [shopLocale]);
  const materialTranslationsValues = reactExports.useMemo(() => {
    var _a2, _b2, _c2;
    if (shopLocale === "en") return {};
    try {
      return ((_c2 = (_b2 = (_a2 = window.clubConfiguratorData) == null ? void 0 : _a2.translations) == null ? void 0 : _b2.values_translations) == null ? void 0 : _c2.shaft_materials) || {};
    } catch (error2) {
      console.error("Error accessing material translations:", error2);
      return {};
    }
  }, [shopLocale]);
  const shaftFlexTranslationsValues = reactExports.useMemo(() => {
    var _a2, _b2, _c2;
    if (shopLocale === "en") return {};
    try {
      return ((_c2 = (_b2 = (_a2 = window.clubConfiguratorData) == null ? void 0 : _a2.translations) == null ? void 0 : _b2.values_translations) == null ? void 0 : _c2.shaft_flex) || {};
    } catch (error2) {
      console.error("Error accessing shaft flex translations:", error2);
      return {};
    }
  }, [shopLocale]);
  const getTranslatedShaftFlex = reactExports.useMemo(() => {
    return (flexValue) => {
      if (!flexValue) return "";
      const translations = {
        ladies: (shaftFlexTranslationsValues == null ? void 0 : shaftFlexTranslationsValues.Ladies) || "Ladies",
        senior: (shaftFlexTranslationsValues == null ? void 0 : shaftFlexTranslationsValues.Senior) || "Senior",
        regular: (shaftFlexTranslationsValues == null ? void 0 : shaftFlexTranslationsValues.Regular) || "Regular",
        stiff: (shaftFlexTranslationsValues == null ? void 0 : shaftFlexTranslationsValues.Stiff) || "Stiff",
        xstiff: (shaftFlexTranslationsValues == null ? void 0 : shaftFlexTranslationsValues.XStiff) || "X-Stiff"
      };
      return translations[flexValue] || flexValue;
    };
  }, [shaftFlexTranslationsValues]);
  const clubLoftTranslationsValues = reactExports.useMemo(() => {
    var _a2, _b2, _c2;
    if (shopLocale === "en") return {};
    try {
      return ((_c2 = (_b2 = (_a2 = window.clubConfiguratorData) == null ? void 0 : _a2.translations) == null ? void 0 : _b2.values_translations) == null ? void 0 : _c2.club_loft) || {};
    } catch (error2) {
      console.error("Error accessing club loft translations:", error2);
      return {};
    }
  }, [shopLocale]);
  const clubLieTranslationsValues = reactExports.useMemo(() => {
    var _a2, _b2, _c2;
    if (shopLocale === "en") return {};
    try {
      return ((_c2 = (_b2 = (_a2 = window.clubConfiguratorData) == null ? void 0 : _a2.translations) == null ? void 0 : _b2.values_translations) == null ? void 0 : _c2.club_lie) || {};
    } catch (error2) {
      console.error("Error accessing club lie translations:", error2);
      return {};
    }
  }, [shopLocale]);
  const gripSizeTranslationsValues = reactExports.useMemo(() => {
    var _a2, _b2, _c2;
    if (shopLocale === "en") return {};
    try {
      return ((_c2 = (_b2 = (_a2 = window.clubConfiguratorData) == null ? void 0 : _a2.translations) == null ? void 0 : _b2.values_translations) == null ? void 0 : _c2.grip_size) || {};
    } catch (error2) {
      console.error("Error accessing grip size translations:", error2);
      return {};
    }
  }, [shopLocale]);
  const shaftLengthTranslationsValues = reactExports.useMemo(() => {
    var _a2, _b2, _c2;
    if (shopLocale === "en") return {};
    try {
      return ((_c2 = (_b2 = (_a2 = window.clubConfiguratorData) == null ? void 0 : _a2.translations) == null ? void 0 : _b2.values_translations) == null ? void 0 : _c2.shaft_length) || {};
    } catch (error2) {
      console.error("Error accessing shaft length translations:", error2);
      return {};
    }
  }, [shopLocale]);
  const initialData = reactExports.useMemo(() => {
    var _a2;
    try {
      return ((_a2 = window.clubConfiguratorData) == null ? void 0 : _a2.productData) || null;
    } catch (error2) {
      console.error("Error accessing initial product data:", error2);
      return null;
    }
  }, []);
  const baseHeads = reactExports.useMemo(() => {
    var _a2, _b2, _c2;
    return (_c2 = (_b2 = (_a2 = productData == null ? void 0 : productData.baseHeads) == null ? void 0 : _a2[0]) == null ? void 0 : _b2.references) == null ? void 0 : _c2.nodes.filter(Boolean);
  }, [productData]);
  reactExports.useEffect(() => {
    window.setUserSelectedShaft = setUserSelectedShaft;
    window.setUserSelectedGrip = setUserSelectedGrip;
    return () => {
      delete window.setUserSelectedShaft;
      delete window.setUserSelectedGrip;
    };
  }, []);
  reactExports.useEffect(() => {
    if (!mainSlider) return;
    let targetSlide = null;
    if (userSelectedBounce && selectedBounce) {
      targetSlide = "head-slide";
    } else if (userSelectedLoft && selectedLofts.length > 0) {
      targetSlide = "head-slide";
    } else if (userSelectedShaft) {
      targetSlide = "shaft-slide";
    } else if (userSelectedGrip) {
      targetSlide = "grip-slide";
    }
    if (targetSlide && mainSlider) {
      setTimeout(() => {
        const headSlideIndex = Array.from(document.querySelector(".main-slider .splide__list").children).findIndex(
          (slide) => slide.classList.contains(targetSlide)
        );
        if (headSlideIndex !== -1) mainSlider.go(headSlideIndex);
      }, 250);
    }
    setUserSelectedBounce(false);
    setUserSelectedLoft(false);
    setUserSelectedShaft(false);
    setUserSelectedGrip(false);
  }, [
    userSelectedBounce,
    userSelectedLoft,
    userSelectedShaft,
    userSelectedGrip,
    selectedBounce,
    selectedLofts,
    mainSlider
  ]);
  reactExports.useEffect(() => {
    const uniqueSelectedForCartItems2 = selectedForCartItems.filter((item, index, self) => index === self.findIndex((t) => (t == null ? void 0 : t.type) === (item == null ? void 0 : item.type))).filter((item) => {
      var _a2, _b2, _c2, _d2;
      return (_d2 = (_c2 = (_b2 = (_a2 = item == null ? void 0 : item.product) == null ? void 0 : _a2.images) == null ? void 0 : _b2.edges[0]) == null ? void 0 : _c2.node) == null ? void 0 : _d2.url;
    });
    setUniqueSelectedForCartItems(uniqueSelectedForCartItems2);
  }, [selectedForCartItems]);
  const showDetailedView = reactExports.useMemo(() => {
    var _a2, _b2;
    try {
      return ((_b2 = (_a2 = window.clubConfiguratorData) == null ? void 0 : _a2.detailedView) == null ? void 0 : _b2.showDetailedView) || false;
    } catch (error2) {
      console.error("Error accessing detailed view config:", error2);
      return false;
    }
  }, []);
  reactExports.useEffect(() => {
    const fetchProductData = async () => {
      var _a2;
      setIsLoading(true);
      if (!((_a2 = initialData == null ? void 0 : initialData.product) == null ? void 0 : _a2.id)) {
        setError("Required product data is missing");
        setIsLoading(false);
        return;
      }
      try {
        const storefrontId = btoa(`gid://shopify/Product/${initialData.product.id}`);
        const languageCode = window.Shopify.locale.toUpperCase();
        const countryCode = window.Shopify.country.toUpperCase();
        const dataStorefrontApi = await executeStorefrontQuery(BASE_PRODUCT_QUERY, {
          id: storefrontId,
          country: countryCode,
          language: languageCode
        });
        setProductData(dataStorefrontApi.product);
        setIsLoading(false);
      } catch (error2) {
        console.error("Error fetching product data:", error2);
        setError("Error fetching product data");
        setIsLoading(false);
      }
    };
    fetchProductData();
  }, [initialData]);
  const clubType = reactExports.useMemo(() => {
    const tags = (productData == null ? void 0 : productData.tags) || [];
    const clubTypeTag = tags.find((tag) => {
      const lowercaseTag = tag.toLowerCase();
      return ["irons", "drivers", "fairways", "hybrids", "wedges", "putters"].includes(lowercaseTag);
    });
    return clubTypeTag ? clubTypeTag.toLowerCase() : "";
  }, [productData]);
  const loftBuildSpecs = reactExports.useMemo(() => {
    if (!baseHeads || baseHeads.length === 0) return [];
    const loftBuildSpecs2 = baseHeads.map((head) => {
      try {
        const meta = head.meta.find((m) => (m == null ? void 0 : m.key) === "list_of_loft");
        return JSON.parse((meta == null ? void 0 : meta.value) || "[]");
      } catch (error2) {
        console.error("Error parsing loft loftBuildSpecs:", error2);
        return [];
      }
    }).flat().filter(Boolean).filter((value, index, self) => self.indexOf(value) === index);
    return loftBuildSpecs2;
  }, [baseHeads]);
  const lieBuildSpecs = reactExports.useMemo(() => {
    if (!baseHeads || baseHeads.length === 0) return [];
    const lieBuildSpecs2 = baseHeads.map((head) => {
      try {
        const meta = head.meta.find((m) => (m == null ? void 0 : m.key) === "list_of_lie");
        return JSON.parse((meta == null ? void 0 : meta.value) || "[]");
      } catch (error2) {
        console.error("Error parsing lie lieBuildSpecs:", error2);
        return [];
      }
    }).flat().filter(Boolean).filter((value, index, self) => self.indexOf(value) === index);
    return lieBuildSpecs2;
  }, [baseHeads]);
  const lengthBuildSpecs = reactExports.useMemo(() => {
    if (!baseHeads || baseHeads.length === 0) return [];
    const lengthBuildSpecs2 = baseHeads.map((head) => {
      try {
        const meta = head.meta.find((m) => (m == null ? void 0 : m.key) === "list_of_length");
        return JSON.parse((meta == null ? void 0 : meta.value) || "[]");
      } catch (error2) {
        console.error("Error parsing length lengthBuildSpecs:", error2);
        return [];
      }
    }).flat().filter(Boolean).filter((value, index, self) => self.indexOf(value) === index);
    return lengthBuildSpecs2;
  }, [baseHeads]);
  const getParamFromUrl = reactExports.useCallback((key) => {
    return params.get(key);
  }, [params]);
  const getLoftsFromUrl = reactExports.useCallback(() => {
    const lofts = [];
    for (let i = 1; true; i++) {
      const loft = params.get(`clubNumber${i}`);
      if (loft) {
        lofts.push(loft);
      } else {
        break;
      }
    }
    return lofts;
  }, []);
  const defaultDexterity = reactExports.useMemo(() => {
    if (!(productData == null ? void 0 : productData.baseProductMeta)) return "R";
    const meta = productData.baseProductMeta.find((m) => (m == null ? void 0 : m.key) === "default_dexterity");
    return (meta == null ? void 0 : meta.value) || "R";
  }, [productData]);
  reactExports.useEffect(() => {
    if (defaultDexterity) {
      setSelectedDexterity(defaultDexterity);
    }
  }, [defaultDexterity]);
  const defaultModel = reactExports.useMemo(() => {
    if (!(productData == null ? void 0 : productData.baseProductMeta)) return "";
    const meta = productData.baseProductMeta.find((m) => (m == null ? void 0 : m.key) === "default_club_model");
    return (meta == null ? void 0 : meta.value) || "";
  }, [productData]);
  const defaultColor = reactExports.useMemo(() => {
    if (!(productData == null ? void 0 : productData.baseProductMeta)) return "";
    const meta = productData.baseProductMeta.find((m) => (m == null ? void 0 : m.key) === "default_club_finish");
    return (meta == null ? void 0 : meta.value) || "";
  }, [productData]);
  const availableModels = reactExports.useMemo(() => {
    if (!baseHeads || !selectedDexterity) return [];
    const models = baseHeads.filter((head) => {
      var _a2, _b2, _c2;
      const headComponents = ((_c2 = (_b2 = (_a2 = head.headComponents) == null ? void 0 : _a2.find((component) => (component == null ? void 0 : component.key) === "list_of_heads")) == null ? void 0 : _b2.references) == null ? void 0 : _c2.nodes) || [];
      return headComponents.some(
        (component) => {
          var _a3, _b3;
          return ((_b3 = (_a3 = component == null ? void 0 : component.meta) == null ? void 0 : _a3.find((m) => (m == null ? void 0 : m.key) === "dexterity")) == null ? void 0 : _b3.value) === selectedDexterity && component.availableForSale && component.totalInventory > 0;
        }
      );
    }).map((head) => {
      var _a2, _b2, _c2;
      return (_c2 = (_b2 = (_a2 = head.meta) == null ? void 0 : _a2.filter(Boolean)) == null ? void 0 : _b2.find((m) => (m == null ? void 0 : m.key) === "head_model")) == null ? void 0 : _c2.value;
    }).filter(Boolean).filter((value, index, self) => self.indexOf(value) === index);
    return models;
  }, [baseHeads, selectedDexterity]);
  const availableColors = reactExports.useMemo(() => {
    if (!baseHeads || !selectedDexterity) return [];
    const colors = baseHeads.filter((head) => {
      var _a2, _b2, _c2;
      const headComponents = ((_c2 = (_b2 = (_a2 = head.headComponents) == null ? void 0 : _a2.find((component) => (component == null ? void 0 : component.key) === "list_of_heads")) == null ? void 0 : _b2.references) == null ? void 0 : _c2.nodes) || [];
      return headComponents.some(
        (component) => {
          var _a3, _b3;
          return ((_b3 = (_a3 = component == null ? void 0 : component.meta) == null ? void 0 : _a3.find((m) => (m == null ? void 0 : m.key) === "dexterity")) == null ? void 0 : _b3.value) === selectedDexterity && component.availableForSale && component.totalInventory > 0;
        }
      );
    }).map((head) => {
      var _a2, _b2, _c2;
      return (_c2 = (_b2 = (_a2 = head.meta) == null ? void 0 : _a2.filter(Boolean)) == null ? void 0 : _b2.find((m) => (m == null ? void 0 : m.key) === "head_color")) == null ? void 0 : _c2.value;
    }).filter(Boolean).filter((value, index, self) => self.indexOf(value) === index);
    return colors;
  }, [baseHeads, selectedDexterity]);
  const availableColorsForModel = reactExports.useMemo(() => {
    if (!selectedModel || !selectedDexterity) return [];
    return baseHeads.filter((head) => {
      var _a2, _b2, _c2, _d2, _e2, _f2;
      const modelMatch = ((_c2 = (_b2 = (_a2 = head.meta) == null ? void 0 : _a2.filter(Boolean)) == null ? void 0 : _b2.find((m) => (m == null ? void 0 : m.key) === "head_model")) == null ? void 0 : _c2.value) === selectedModel;
      const headComponents = ((_f2 = (_e2 = (_d2 = head.headComponents) == null ? void 0 : _d2.find((component) => (component == null ? void 0 : component.key) === "list_of_heads")) == null ? void 0 : _e2.references) == null ? void 0 : _f2.nodes) || [];
      return modelMatch && headComponents.some(
        (component) => {
          var _a3, _b3;
          return ((_b3 = (_a3 = component == null ? void 0 : component.meta) == null ? void 0 : _a3.find((m) => (m == null ? void 0 : m.key) === "dexterity")) == null ? void 0 : _b3.value) === selectedDexterity && component.availableForSale && component.totalInventory > 0;
        }
      );
    }).map((head) => {
      var _a2, _b2, _c2;
      return (_c2 = (_b2 = (_a2 = head.meta) == null ? void 0 : _a2.filter(Boolean)) == null ? void 0 : _b2.find((m) => (m == null ? void 0 : m.key) === "head_color")) == null ? void 0 : _c2.value;
    }).filter(Boolean);
  }, [baseHeads, selectedModel, selectedDexterity]);
  const availableModelsForColor = reactExports.useMemo(() => {
    if (!selectedColor || !selectedDexterity) return [];
    return baseHeads.filter((head) => {
      var _a2, _b2, _c2, _d2, _e2, _f2;
      const colorMatch = ((_c2 = (_b2 = (_a2 = head.meta) == null ? void 0 : _a2.filter(Boolean)) == null ? void 0 : _b2.find((m) => (m == null ? void 0 : m.key) === "head_color")) == null ? void 0 : _c2.value) === selectedColor;
      const headComponents = ((_f2 = (_e2 = (_d2 = head.headComponents) == null ? void 0 : _d2.find((component) => (component == null ? void 0 : component.key) === "list_of_heads")) == null ? void 0 : _e2.references) == null ? void 0 : _f2.nodes) || [];
      return colorMatch && headComponents.some(
        (component) => {
          var _a3, _b3;
          return ((_b3 = (_a3 = component == null ? void 0 : component.meta) == null ? void 0 : _a3.find((m) => (m == null ? void 0 : m.key) === "dexterity")) == null ? void 0 : _b3.value) === selectedDexterity && component.availableForSale && component.totalInventory > 0;
        }
      );
    }).map((head) => {
      var _a2, _b2, _c2;
      return (_c2 = (_b2 = (_a2 = head.meta) == null ? void 0 : _a2.filter(Boolean)) == null ? void 0 : _b2.find((m) => (m == null ? void 0 : m.key) === "head_model")) == null ? void 0 : _c2.value;
    }).filter(Boolean);
  }, [baseHeads, selectedColor, selectedDexterity]);
  const defaultLengthAdjustment = reactExports.useMemo(() => {
    if (!(productData == null ? void 0 : productData.baseProductMeta)) return "";
    const meta = productData.baseProductMeta.find((m) => (m == null ? void 0 : m.key) === "default_length_adjustment");
    return (meta == null ? void 0 : meta.value) || "";
  }, [productData]);
  const defaultShaftMaterial = reactExports.useMemo(() => {
    if (!(productData == null ? void 0 : productData.baseProductMeta)) return "";
    const meta = productData.baseProductMeta.find((m) => (m == null ? void 0 : m.key) === "default_shaft_material");
    return (meta == null ? void 0 : meta.value) || "";
  }, [productData]);
  const defaultShaftFlex = reactExports.useMemo(() => {
    if (!(productData == null ? void 0 : productData.baseProductMeta)) return "";
    const meta = productData.baseProductMeta.find((m) => (m == null ? void 0 : m.key) === "default_shaft_flex");
    return (meta == null ? void 0 : meta.value) || "";
  }, [productData]);
  const defaultGripSize = reactExports.useMemo(() => {
    if (!(productData == null ? void 0 : productData.baseProductMeta)) return "";
    const meta = productData.baseProductMeta.find((m) => (m == null ? void 0 : m.key) === "default_grip_size");
    return (meta == null ? void 0 : meta.value) || "";
  }, [productData]);
  reactExports.useEffect(() => {
    var _a2, _b2, _c2, _d2, _e2, _f2;
    if (!productData) {
      setIsLoading(false);
      return;
    }
    if (defaultModel && !selectedModel) {
      const isValidModel = baseHeads.some((head) => {
        var _a3, _b3;
        return ((_b3 = (_a3 = head.meta) == null ? void 0 : _a3.filter(Boolean)) == null ? void 0 : _b3.find((m) => {
          return (m == null ? void 0 : m.key) === "head_model" && (m == null ? void 0 : m.value) === defaultModel;
        })) !== void 0;
      });
      if (isValidModel) {
        setSelectedModel(defaultModel);
        const validColorsForModel = baseHeads.filter((head) => {
          var _a3;
          return (_a3 = head.meta) == null ? void 0 : _a3.find((m) => (m == null ? void 0 : m.key) === "head_model" && (m == null ? void 0 : m.value) === defaultModel);
        }).map((head) => {
          var _a3, _b3;
          return (_b3 = (_a3 = head == null ? void 0 : head.meta) == null ? void 0 : _a3.find((m) => (m == null ? void 0 : m.key) === "head_color")) == null ? void 0 : _b3.value;
        });
        if (defaultColor && validColorsForModel.includes(defaultColor)) {
          setSelectedColor(defaultColor);
        } else {
          const firstColor = validColorsForModel[0];
          console.error("⚠️ Default color not valid, using first available:", firstColor);
          setSelectedColor(firstColor || "");
        }
      } else {
        const firstModel = (_c2 = (_b2 = (_a2 = baseHeads[0]) == null ? void 0 : _a2.meta) == null ? void 0 : _b2.find((m) => (m == null ? void 0 : m.key) === "head_model")) == null ? void 0 : _c2.value;
        const firstColor = (_f2 = (_e2 = (_d2 = baseHeads[0]) == null ? void 0 : _d2.meta) == null ? void 0 : _e2.find((m) => (m == null ? void 0 : m.key) === "head_color")) == null ? void 0 : _f2.value;
        if (firstModel) {
          setSelectedModel(firstModel);
          setSelectedColor(firstColor || "");
        }
      }
    }
  }, [defaultModel, defaultColor]);
  reactExports.useEffect(() => {
    if (splideRef.current) {
      splideRef.current.querySelectorAll("[aria-orientation]").forEach((el) => el.removeAttribute("aria-orientation"));
    }
  }, [splideRef.current, selectedBaseHead]);
  reactExports.useEffect(() => {
    const newCachedImages = { ...cachedImages };
    baseHeads == null ? void 0 : baseHeads.forEach((head) => {
      var _a2;
      if (!newCachedImages[head.id] && ((_a2 = head.images) == null ? void 0 : _a2.edges)) {
        newCachedImages[head.id] = head.images.edges;
      }
    });
    setCachedImages(newCachedImages);
  }, [baseHeads]);
  reactExports.useEffect(() => {
    if (!selectedModel && !selectedColor) {
      return;
    }
    if (selectedModel && selectedColor) {
      const newSelectedHead = baseHeads.find(
        (head) => {
          var _a2, _b2, _c2, _d2, _e2, _f2;
          return ((_c2 = (_b2 = (_a2 = head.meta) == null ? void 0 : _a2.filter(Boolean)) == null ? void 0 : _b2.find((m) => (m == null ? void 0 : m.key) === "head_model")) == null ? void 0 : _c2.value) === selectedModel && ((_f2 = (_e2 = (_d2 = head.meta) == null ? void 0 : _d2.filter(Boolean)) == null ? void 0 : _e2.find((m) => (m == null ? void 0 : m.key) === "head_color")) == null ? void 0 : _f2.value) === selectedColor;
        }
      );
      setSelectedBaseHead(newSelectedHead || null);
      if (newSelectedHead) {
        const availableLofts = getAvailableLofts(newSelectedHead, selectedDexterity);
        if (availableLofts.length > 0) {
          if (clubType === "irons") {
            setSelectedLofts(availableLofts);
          } else {
            setSelectedLofts([availableLofts[0]]);
          }
        } else {
          setSelectedLofts([]);
        }
      }
      setSelectedTorqueWrench("");
    } else {
      setSelectedBaseHead(null);
      setSelectedTorqueWrench("");
    }
  }, [selectedModel, selectedColor, baseHeads]);
  reactExports.useEffect(() => {
    var _a2, _b2, _c2, _d2, _e2, _f2, _g2, _h2;
    if (selectedBaseHead && selectedHeadComponents.length > 0) {
      try {
        const headcoverComponents = ((_c2 = (_b2 = (_a2 = selectedBaseHead.headcoverComponents) == null ? void 0 : _a2[0]) == null ? void 0 : _b2.references) == null ? void 0 : _c2.nodes) || [];
        let headComponent = selectedHeadComponents[0];
        if (!headComponent) {
          setSelectedHeadcover(null);
          return;
        }
        if (headcoverComponents.length > 1) {
          const selectedHeadItem = selectedForCartItems.find((item) => item.type === "head");
          const selectedHeadMapping = (_f2 = (_e2 = (_d2 = selectedHeadItem == null ? void 0 : selectedHeadItem.product) == null ? void 0 : _d2.meta) == null ? void 0 : _e2.find(
            (m) => (m == null ? void 0 : m.key) === "club_component_mapping"
          )) == null ? void 0 : _f2.value;
          if (!selectedHeadMapping) {
            setSelectedHeadcover(null);
            return;
          }
          const parsedMapping = JSON.parse(selectedHeadMapping);
          headComponent = selectedHeadComponents.find((component) => {
            var _a3, _b3;
            const componentMapping = (_b3 = (_a3 = component == null ? void 0 : component.meta) == null ? void 0 : _a3.find((m) => (m == null ? void 0 : m.key) === "club_component_mapping")) == null ? void 0 : _b3.value;
            return componentMapping && parsedMapping.some((mapping) => componentMapping.includes(selectedHosel));
          });
        }
        const headMapping = JSON.parse(
          ((_h2 = (_g2 = headComponent == null ? void 0 : headComponent.meta) == null ? void 0 : _g2.find((m) => (m == null ? void 0 : m.key) === "club_component_mapping")) == null ? void 0 : _h2.value) || "{}"
        );
        const headMappingValues = Object.values(headMapping);
        const compatibleHeadcover = headcoverComponents.find((headcover) => {
          var _a3, _b3;
          try {
            const headcoverMapping = JSON.parse(
              ((_b3 = (_a3 = headcover == null ? void 0 : headcover.meta) == null ? void 0 : _a3.find((m) => (m == null ? void 0 : m.key) === "club_component_mapping")) == null ? void 0 : _b3.value) || "{}"
            );
            const headcoverMappingValues = Object.values(headcoverMapping);
            return headcoverMappingValues.some((value) => headMappingValues.includes(value));
          } catch (error2) {
            console.error("Error parsing headcover mapping:", error2);
            return false;
          }
        });
        setSelectedHeadcover(compatibleHeadcover || null);
      } catch (error2) {
        console.error("Error selecting headcover:", error2);
        setSelectedHeadcover(null);
      }
    } else {
      setSelectedHeadcover(null);
    }
  }, [selectedBaseHead, selectedHeadComponents, clubType, selectedHosel]);
  const getSelectedHeadComponents = (baseHead, selectedDexterity2, selectedBounce2 = "", selectedHosel2 = "") => {
    var _a2, _b2;
    const headComponent = (_a2 = baseHead == null ? void 0 : baseHead.headComponents) == null ? void 0 : _a2.find((component) => (component == null ? void 0 : component.key) === "list_of_heads");
    const heads = ((_b2 = headComponent == null ? void 0 : headComponent.references) == null ? void 0 : _b2.nodes) || [];
    return heads.filter((head) => {
      var _a3, _b3, _c2, _d2, _e2, _f2;
      if (!head) return false;
      const inventory = getInventoryInfo(head);
      if (!inventory.available) return false;
      const dexterityMatch = ((_b3 = (_a3 = head == null ? void 0 : head.meta) == null ? void 0 : _a3.find((m) => (m == null ? void 0 : m.key) === "dexterity")) == null ? void 0 : _b3.value) === selectedDexterity2;
      if (clubType === "putters") {
        if (selectedHosel2) {
          try {
            const mapping = (_d2 = (_c2 = head == null ? void 0 : head.meta) == null ? void 0 : _c2.find((m) => (m == null ? void 0 : m.key) === "club_component_mapping")) == null ? void 0 : _d2.value;
            const mappingValues = mapping ? JSON.parse(mapping) : [];
            return dexterityMatch && mappingValues.includes(selectedHosel2);
          } catch (error2) {
            console.error("Error parsing head mapping:", error2);
            return false;
          }
        }
        return dexterityMatch;
      }
      if (clubType === "wedges" && selectedBounce2) {
        const bounceMatch = ((_f2 = (_e2 = head == null ? void 0 : head.meta) == null ? void 0 : _e2.find((m) => (m == null ? void 0 : m.key) === "bounce")) == null ? void 0 : _f2.value) === selectedBounce2;
        return dexterityMatch && bounceMatch;
      }
      return dexterityMatch;
    });
  };
  const getAvailableLofts = (baseHead, selectedDexterity2) => {
    var _a2, _b2;
    try {
      const headComponent = (_a2 = baseHead == null ? void 0 : baseHead.headComponents) == null ? void 0 : _a2.find((component) => (component == null ? void 0 : component.key) === "list_of_heads");
      const heads = ((_b2 = headComponent == null ? void 0 : headComponent.references) == null ? void 0 : _b2.nodes) || [];
      const availableHeads = heads.filter((head) => {
        var _a3, _b3;
        if (!head) return false;
        const inventory = getInventoryInfo(head);
        if (!inventory.available) return false;
        return ((_b3 = (_a3 = head == null ? void 0 : head.meta) == null ? void 0 : _a3.find((m) => (m == null ? void 0 : m.key) === "dexterity")) == null ? void 0 : _b3.value) === selectedDexterity2;
      });
      const lofts = availableHeads.map((component) => {
        var _a3, _b3;
        return (_b3 = (_a3 = component.meta) == null ? void 0 : _a3.find((m) => (m == null ? void 0 : m.key) === "loft")) == null ? void 0 : _b3.value;
      }).filter(Boolean).sort((a, b) => parseFloat(a) - parseFloat(b));
      return [...new Set(lofts)];
    } catch (error2) {
      console.error("Error getting available lofts:", error2);
      return [];
    }
  };
  const getCompatiblePatchForHead = (headComponent, baseHead) => {
    var _a2, _b2, _c2, _d2, _e2;
    try {
      const patchesComponents = ((_c2 = (_b2 = (_a2 = baseHead.patchesComponents) == null ? void 0 : _a2.find((comp) => (comp == null ? void 0 : comp.key) === "list_of_patches")) == null ? void 0 : _b2.references) == null ? void 0 : _c2.nodes) || [];
      const headMapping = JSON.parse(
        ((_e2 = (_d2 = headComponent == null ? void 0 : headComponent.meta) == null ? void 0 : _d2.find((m) => (m == null ? void 0 : m.key) === "club_component_mapping")) == null ? void 0 : _e2.value) || "{}"
      );
      const headComponentStrings = Object.values(headMapping);
      const compatiblePatch = patchesComponents.find((patch) => {
        var _a3, _b3;
        const patchMapping = JSON.parse(((_b3 = (_a3 = patch == null ? void 0 : patch.meta) == null ? void 0 : _a3.find((m) => (m == null ? void 0 : m.key) === "club_component_mapping")) == null ? void 0 : _b3.value) || "{}");
        const patchCompatibleStrings = Object.values(patchMapping);
        return headComponentStrings.some((headString) => patchCompatibleStrings.includes(headString));
      });
      return compatiblePatch || null;
    } catch (error2) {
      console.error("Error finding compatible patch:", error2);
      return null;
    }
  };
  const parseGidList = (value) => {
    try {
      return JSON.parse(value || "[]");
    } catch (error2) {
      console.error("Error parsing GID list:", error2);
      return [];
    }
  };
  const findTaperedShaftMatch = (baseShaftGid, headComponent, productDetails2) => {
    var _a2, _b2;
    try {
      const baseShaft = productDetails2[baseShaftGid];
      if (!baseShaft) return null;
      const headMapping = JSON.parse(
        ((_b2 = (_a2 = headComponent == null ? void 0 : headComponent.meta) == null ? void 0 : _a2.find((m) => (m == null ? void 0 : m.key) === "club_component_mapping")) == null ? void 0 : _b2.value) || "{}"
      );
      const headMappingValues = Object.values(headMapping);
      const taperedLists = [
        "list_of_tapered_shaft",
        "list_of_tapered_shaftregular",
        "list_of_tapered_shaftstiff",
        "list_of_tapered_shaftxtiff",
        "list_of_tapered_shaftsenior",
        "list_of_tapered_shaftladies"
      ].map((key) => {
        var _a3;
        return parseGidList((_a3 = baseShaft[key]) == null ? void 0 : _a3.value);
      }).flat();
      return taperedLists.find((taperedGid) => {
        var _a3, _b3;
        const taperedShaft = productDetails2[taperedGid];
        if (!taperedShaft) return false;
        const shaftMapping = JSON.parse(
          ((_b3 = (_a3 = taperedShaft == null ? void 0 : taperedShaft.meta) == null ? void 0 : _a3.find((m) => (m == null ? void 0 : m.key) === "club_component_mapping")) == null ? void 0 : _b3.value) || "{}"
        );
        const shaftMappingValues = Object.values(shaftMapping);
        const isCompatible = shaftMappingValues.some((shaftValue) => headMappingValues.includes(shaftValue));
        const inventory = getInventoryInfo(taperedShaft);
        return isCompatible && inventory.available;
      }) || null;
    } catch (error2) {
      console.error("Error in findTaperedShaftMatch:", error2);
      return null;
    }
  };
  const getAvailableShafts = reactExports.useCallback(
    (baseHead, flex, checkMaterial = true) => {
      var _a2, _b2;
      if (!baseHead) return [];
      const flexMap = {
        ladies: "list_of_shaftsladies",
        senior: "list_of_shaftssenior",
        regular: "list_of_shaftsregular",
        stiff: "list_of_shaftsstiff",
        xstiff: "list_of_shaftsxstiff",
        putter: "list_of_shaftsputter"
      };
      try {
        const flexKey = flexMap[flex];
        const shafts = ((_b2 = (_a2 = baseHead == null ? void 0 : baseHead.shaftComponents.filter(Boolean).find((shaft) => shaft.key === flexKey)) == null ? void 0 : _a2.references) == null ? void 0 : _b2.nodes) || [];
        return checkMaterial ? shafts.filter((shaft) => {
          var _a3, _b3;
          const shaftMaterial = (_b3 = (_a3 = shaft == null ? void 0 : shaft.meta) == null ? void 0 : _a3.find((m) => (m == null ? void 0 : m.key) === "material")) == null ? void 0 : _b3.value;
          return !selectedShaftMaterial || shaftMaterial === selectedShaftMaterial;
        }) : shafts;
      } catch (error2) {
        console.error("Error getting available shafts:", error2);
        return [];
      }
    },
    [selectedShaftMaterial]
  );
  const selectedShaftProduct = reactExports.useMemo(() => {
    if (!selectedShaft || !selectedBaseHead) return null;
    try {
      const availableShafts = getAvailableShafts(selectedBaseHead, selectedShaftFlex);
      return availableShafts.find((shaft) => shaft.id === selectedShaft) || null;
    } catch (error2) {
      console.error("Error finding selected shaft product:", error2);
      return null;
    }
  }, [selectedShaft, selectedBaseHead, selectedShaftFlex, getAvailableShafts]);
  const totalSelectedItemsCount = reactExports.useMemo(() => {
    return selectedForCartItems.reduce((acc, item) => acc + item.qty, 0);
  }, [selectedForCartItems]);
  reactExports.useEffect(() => {
    setSelectedForCartItems([]);
    if (!selectedLofts.length || !selectedBaseHead) return;
    const headComponents = getSelectedHeadComponents(
      selectedBaseHead,
      selectedDexterity,
      selectedBounce,
      selectedHosel
    );
    const clubs = selectedLofts.map((loft) => {
      var _a2, _b2, _c2, _d2, _e2, _f2, _g2, _h2;
      const headComponent = headComponents.find(
        (component) => {
          var _a3, _b3;
          return ((_b3 = (_a3 = component.meta) == null ? void 0 : _a3.find((m) => (m == null ? void 0 : m.key) === "loft")) == null ? void 0 : _b3.value) === loft;
        }
      );
      const headComponentMapping = (_b2 = (_a2 = headComponent == null ? void 0 : headComponent.meta) == null ? void 0 : _a2.find((m) => (m == null ? void 0 : m.key) === "club_component_mapping")) == null ? void 0 : _b2.value;
      if (!headComponent) return null;
      const clubUUID = crypto.randomUUID();
      const club = {
        uuid: clubUUID,
        components: [
          {
            gid: headComponent.id,
            product: headComponent,
            qty: 1,
            type: "head",
            clubUUID,
            loft: (_c2 = headComponent.head_component_loft) == null ? void 0 : _c2.value
          }
        ]
      };
      if (selectedHoselProduct && clubType === "putters") {
        const hoselInventory = getInventoryInfo(selectedHoselProduct);
        if (hoselInventory.available) {
          club.components.push({
            gid: selectedHoselProduct,
            product: selectedHoselProduct,
            qty: 1,
            type: "hosel",
            clubUUID
          });
        }
      }
      if (selectedShaft) {
        const isTapered = ((_e2 = (_d2 = selectedShaft == null ? void 0 : selectedShaft.meta) == null ? void 0 : _d2.find((m) => (m == null ? void 0 : m.key) === "tapered_shaft")) == null ? void 0 : _e2.value.toLowerCase()) === "true";
        let shaftToAdd = isTapered ? findTaperedShaftMatch(selectedShaft, headComponent, productDetails) : isShaftCompatible(selectedShaft, [headComponent], productDetails) ? selectedShaft : null;
        if (isTapered) {
          const taperedShafts = ((_h2 = (_g2 = (_f2 = selectedShaft == null ? void 0 : selectedShaft.taperedShafts) == null ? void 0 : _f2.find((metafield) => {
            var _a3, _b3;
            return ((_b3 = (_a3 = metafield == null ? void 0 : metafield.references) == null ? void 0 : _a3.nodes) == null ? void 0 : _b3.length) > 0;
          })) == null ? void 0 : _g2.references) == null ? void 0 : _h2.nodes) || [];
          const compatibleTaperedShaft = taperedShafts.find((taperedShafts2) => {
            var _a3, _b3;
            const shaftMapping = JSON.parse(
              ((_b3 = (_a3 = taperedShafts2 == null ? void 0 : taperedShafts2.meta) == null ? void 0 : _a3.find((m) => (m == null ? void 0 : m.key) === "club_component_mapping")) == null ? void 0 : _b3.value) || "{}"
            );
            const shaftMappingValues = Object.values(shaftMapping);
            return shaftMappingValues.some(
              (shaftValue) => typeof headComponentMapping === "string" && headComponentMapping.includes(shaftValue)
            );
          });
          shaftToAdd = compatibleTaperedShaft;
        }
        if (shaftToAdd) {
          club.components.push({
            gid: shaftToAdd.id,
            product: shaftToAdd,
            qty: 1,
            type: "shaft",
            clubUUID
          });
        }
      }
      if (selectedGrip) {
        club.components.push({
          gid: selectedGrip.id,
          product: selectedGrip,
          qty: 1,
          type: "grip",
          clubUUID
        });
      }
      const compatiblePatch = getCompatiblePatchForHead(headComponent, selectedBaseHead);
      if (compatiblePatch) {
        club.components.push({
          gid: compatiblePatch.id,
          product: compatiblePatch,
          qty: 1,
          type: "patch",
          clubUUID
        });
      }
      return club;
    }).filter(Boolean);
    const allComponents = clubs.flatMap((club) => {
      const components = [...club.components];
      if (selectedHeadcover) {
        const headcoverInventory = getInventoryInfo(selectedHeadcover);
        if (headcoverInventory.available) {
          components.push({
            product: selectedHeadcover,
            gid: selectedHeadcover.id,
            qty: 1,
            type: "headcover",
            clubUUID: club.uuid
          });
        }
      }
      return components;
    });
    if (selectedTorqueWrench) {
      const torqueWrenchInventory = getInventoryInfo(selectedTorqueWrench);
      if (torqueWrenchInventory.available) {
        allComponents.push({
          product: selectedTorqueWrench,
          gid: selectedTorqueWrench.id,
          qty: 1,
          type: "torque"
        });
      }
    }
    const sortedComponents = allComponents.sort((a, b) => {
      const typeOrder = { head: 0, hosel: 1, shaft: 2, grip: 3, patch: 4, torque: 5, headcover: 6 };
      return typeOrder[a.type] - typeOrder[b.type];
    });
    setSelectedForCartItems(sortedComponents);
  }, [
    selectedBaseHead,
    selectedLofts,
    selectedGrip,
    selectedDexterity,
    productDetails,
    selectedTorqueWrench,
    selectedBounce,
    selectedHeadcover,
    selectedShaft,
    selectedHosel,
    selectedShaftProduct
  ]);
  const selectedGripTitle = reactExports.useMemo(() => {
    var _a2, _b2;
    if (!selectedGrip || !selectedGrip) return "";
    return ((_b2 = (_a2 = selectedGrip == null ? void 0 : selectedGrip.meta) == null ? void 0 : _a2.find((m) => (m == null ? void 0 : m.key) === "display_title")) == null ? void 0 : _b2.value) || (selectedGrip == null ? void 0 : selectedGrip.title) || "";
  }, [selectedGrip]);
  const selectedShaftTitle = reactExports.useMemo(() => {
    var _a2, _b2;
    if (!selectedShaft || !selectedShaft) return "";
    return ((_b2 = (_a2 = selectedShaft == null ? void 0 : selectedShaft.meta) == null ? void 0 : _a2.find((m) => (m == null ? void 0 : m.key) === "display_title")) == null ? void 0 : _b2.value) || (selectedShaft == null ? void 0 : selectedShaft.title) || "";
  }, [selectedShaft]);
  const loftSelectionString = reactExports.useMemo(() => {
    if (!selectedLofts || selectedLofts.length === 0) return "";
    return selectedLofts.map((loft) => {
      return loft.trim();
    }).sort((a, b) => parseFloat(a) - parseFloat(b)).join(", ");
  }, [selectedLofts]);
  const handleAddToCart = async (isPreview = false) => {
    var _a2, _b2;
    if (!selectedForCartItems.length) {
      console.warn("No items to add to cart");
      return;
    }
    try {
      const cartResponse = await fetch("/cart.js");
      if (!cartResponse.ok) throw new Error("Failed to fetch cart");
      const cartData = await cartResponse.json();
      const currentBundledItems = cartData.items.reduce((total, item) => {
        var _a3;
        if (!((_a3 = item.properties) == null ? void 0 : _a3._bundled)) return total;
        const bundledCount = Number(item.properties._bundled) || 0;
        return total + bundledCount;
      }, 0);
      const currentConfigCount = cartData.items.reduce(
        (total, item) => {
          var _a3;
          return ((_a3 = item.properties) == null ? void 0 : _a3._bundled) ? total + 1 : total;
        },
        0
      );
      if (currentBundledItems + totalSelectedItemsCount >= MAX_BUNDLED_COMPONENTS_IN_CART) {
        alert(`Sorry, you can only have ${MAX_BUNDLED_COMPONENTS_IN_CART} bundled items in your cart at a time.`);
        setCartQuantity(currentBundledItems);
        setConfigurationInCartCount(currentConfigCount);
        return;
      }
      if (currentConfigCount >= MAX_BUNDLE_CONFIGURATIONS_IN_CART) {
        alert(`Sorry, you can only have ${MAX_BUNDLE_CONFIGURATIONS_IN_CART} configurations in your cart at a time.`);
        setCartQuantity(currentBundledItems);
        setConfigurationInCartCount(currentConfigCount);
        return;
      }
      setCartQuantity(currentBundledItems);
      setConfigurationInCartCount(currentConfigCount);
    } catch (error2) {
      console.error("Error fetching latest cart state:", error2);
    }
    const _bundleId = crypto.randomUUID();
    const _bundleTitle = productData == null ? void 0 : productData.title;
    const _baseVid = productData.variants.edges[0].node.id;
    try {
      if (isAdvancedPanelOpen) {
        setIsAdvancedPanelOpen(false);
      }
      const clubUUIDMap = /* @__PURE__ */ new Map();
      const processedItems = selectedForCartItems.map((item) => {
        const processedItem = { ...item };
        if (item.clubUUID) {
          if (!clubUUIDMap.has(item.clubUUID)) {
            clubUUIDMap.set(item.clubUUID, crypto.randomUUID());
          }
          processedItem.clubUUID = clubUUIDMap.get(item.clubUUID);
        }
        return processedItem;
      });
      const totalPriceOfPatches = processedItems.reduce((acc, item) => {
        if (item.type === "patch") {
          return acc + item.product.variants.nodes[0].price.amount * item.qty;
        }
        return acc;
      }, 0);
      const totalPriceOfHeadcovers = processedItems.reduce((acc, item) => {
        if (item.type === "headcover") {
          return acc + item.product.variants.nodes[0].price.amount * item.qty;
        }
        return acc;
      }, 0);
      const totalPriceOfPatchesAndHeadcoversInCents = (totalPriceOfPatches + totalPriceOfHeadcovers) * 100;
      const items = processedItems.map((item, index) => {
        var _a3, _b3, _c2, _d2;
        const variantId = item.product.variants.nodes[0].id;
        if (!variantId) {
          throw new Error(`Could not find variant ID for product: ${item.gid}`);
        }
        const image = item.type === "head" && (selectedBaseHead == null ? void 0 : selectedBaseHead.id) && ((_d2 = (_c2 = (_b3 = (_a3 = selectedBaseHead == null ? void 0 : selectedBaseHead.images) == null ? void 0 : _a3.edges) == null ? void 0 : _b3[0]) == null ? void 0 : _c2.node) == null ? void 0 : _d2.url) ? selectedBaseHead.images.edges[0].node.url.split("?")[0] : "";
        const headcoverAndPatchList = processedItems.filter(
          (item2) => item2.type === "headcover" || item2.type === "patch"
        );
        const transformedHeadcoverAndPatchList = headcoverAndPatchList.map((item2) => {
          const variantId2 = item2.product.variants.nodes[0].id;
          return {
            id: variantId2.replace("gid://shopify/ProductVariant/", ""),
            quantity: item2.qty,
            sku: item2.product.variants.nodes[0].sku,
            properties: [
              { name: "_bundleId", value: _bundleId },
              { name: "_club_uuid", value: item2.clubUUID },
              { name: "_baseVid", value: variantId2 }
            ]
          };
        });
        return {
          id: variantId.replace("gid://shopify/ProductVariant/", ""),
          quantity: item.qty,
          properties: {
            _bundleId,
            _club_uuid: item.clubUUID,
            _baseVid,
            ...index === 0 && {
              _count: processedItems.filter((item2) => item2.type === "head").length
            },
            ...index === 0 && {
              _image: image
            },
            // Only include _notes for the first item
            ...index === 0 && {
              // Notes are separated by @ and @@, @ separates the key from the value, and the @@ separates each note in order to create a new line in the cart. That calculation is done in a Shopify Function.
              _notes: [
                `${(cartNoteLabels == null ? void 0 : cartNoteLabels.cart_note_title) || "Title"}@${_bundleTitle}`,
                // Dexterity
                `${(cartNoteLabels == null ? void 0 : cartNoteLabels.cart_note_dexterity) || "Dexterity"}@${getTranslatedDexterity}`,
                // Model
                ...availableModels.length > 1 ? [`${(cartNoteLabels == null ? void 0 : cartNoteLabels.cart_note_head_model) || "Model"}@${selectedModel}`] : [],
                // Color
                ...availableColors.length > 1 ? [`${(cartNoteLabels == null ? void 0 : cartNoteLabels.cart_note_head_color) || "Color"}@${selectedColor}`] : [],
                // Club Selection
                ...clubType !== "putters" ? [`${(cartNoteLabels == null ? void 0 : cartNoteLabels.cart_note_club_selection) || "Club Selection"}@${loftSelectionString}`] : [],
                // Bounce
                ...clubType === "wedges" && selectedBounce ? [`${(cartNoteLabels == null ? void 0 : cartNoteLabels.cart_note_bounce) || "Bounce"}@${selectedBounce}`] : [],
                // Hosel
                ...clubType === "putters" && selectedHosel ? [`${(cartNoteLabels == null ? void 0 : cartNoteLabels.cart_note_hosel) || "Hosel"}@${selectedHosel}`] : [],
                // Headcover
                ...selectedHeadcover ? [`${(cartNoteLabels == null ? void 0 : cartNoteLabels.cart_note_headcover) || "Headcover"}@${selectedHeadcover.title}`] : [],
                // Shaft
                ...selectedShaftTitle ? [`${(cartNoteLabels == null ? void 0 : cartNoteLabels.cart_note_shaft) || "Shaft"}@${selectedShaftTitle}`] : [],
                // Material
                ...selectedShaftMaterial && (shaftMaterials == null ? void 0 : shaftMaterials.length) > 1 ? [
                  `${(cartNoteLabels == null ? void 0 : cartNoteLabels.cart_note_material) || "Material"}@${shopLocale !== "en" && materialTranslationsValues[selectedShaftMaterial] ? materialTranslationsValues[selectedShaftMaterial] : selectedShaftMaterial}`
                ] : [],
                // Flex
                ...selectedShaftFlex && selectedShaftMaterial ? [`${(cartNoteLabels == null ? void 0 : cartNoteLabels.cart_note_flex) || "Flex"}@${getTranslatedShaftFlex(selectedShaftFlex)}`] : [],
                // Length
                ...selectedLengthBuildSpec ? [
                  `${(cartNoteLabels == null ? void 0 : cartNoteLabels.cart_note_length) || "Length"}@${shopLocale !== "en" && shaftLengthTranslationsValues[selectedLengthBuildSpec] ? shaftLengthTranslationsValues[selectedLengthBuildSpec] : selectedLengthBuildSpec}`
                ] : [],
                // Grip
                ...selectedGrip ? [`${(cartNoteLabels == null ? void 0 : cartNoteLabels.cart_note_grip) || "Grip"}@${selectedGripTitle}`] : [],
                // Grip Size
                ...selectedGripSize ? [
                  `${(cartNoteLabels == null ? void 0 : cartNoteLabels.cart_note_grip_size) || "Grip Size"}@${shopLocale !== "en" && gripSizeTranslationsValues[selectedGripSize] ? gripSizeTranslationsValues[selectedGripSize] : selectedGripSize}`
                ] : [],
                // Loft
                ...["irons", "wedges", "putters"].includes(clubType) && selectedLoftBuildSpec ? [
                  `${(cartNoteLabels == null ? void 0 : cartNoteLabels.cart_note_loft) || "Loft"}@${shopLocale !== "en" && clubLoftTranslationsValues[selectedLoftBuildSpec] ? clubLoftTranslationsValues[selectedLoftBuildSpec] : selectedLoftBuildSpec}`
                ] : [],
                // Lie
                ...selectedLieBuildSpec ? [
                  `${(cartNoteLabels == null ? void 0 : cartNoteLabels.cart_note_lie) || "Lie"}@${shopLocale !== "en" && clubLieTranslationsValues[selectedLieBuildSpec] ? clubLieTranslationsValues[selectedLieBuildSpec] : selectedLieBuildSpec}`
                ] : [],
                // Torque Wrench
                ...selectedTorqueWrench ? [
                  `${(cartNoteLabels == null ? void 0 : cartNoteLabels.cart_note_torque_tool_text) || "Torque Tool"}@${(cartNoteLabels == null ? void 0 : cartNoteLabels.cart_note_included_text) || "Included"}`
                ] : []
              ].filter(Boolean).join("@@")
            },
            ...index === 0 && transformedHeadcoverAndPatchList.length > 0 && {
              _bundled_items: JSON.stringify({ items: transformedHeadcoverAndPatchList })
            },
            // Adapter Setting
            ...["drivers", "fairways", "hybrids"].includes(clubType) && item.type === "head" && {
              _hidden_Adapter_Setting: "Standard"
            },
            // Swingweight
            ...["irons", "wedges"].includes(clubType) && item.type === "head" && {
              _hidden_Swingweight: "Standard"
            },
            // Step
            ...["irons", "wedges"].includes(clubType) && item.type === "head" && {
              _hidden_Step: "Standard"
            },
            // Tipping
            ...["fairways", "hybrids", "irons", "wedges", "putters", "drivers"].includes(clubType) && item.type === "shaft" && {
              _hidden_Tipping: "Standard"
            },
            // Wrap
            ...item.type === "grip" && {
              _hidden_Wrap: "Standard"
            },
            // Weights
            ...["drivers", "fairways", "hybrids", "putters"].includes(clubType) && item.type === "head" && selectedDefaultWeightConfig && {
              _hidden_Weights: selectedDefaultWeightConfig
            },
            // Length
            ...["irons", "wedges", "putters", "hybrids", "drivers", "fairways"].includes(clubType) && item.type === "shaft" && {
              _hidden_Length: selectedLengthBuildSpec
            },
            // Loft
            ...["irons", "wedges", "putters"].includes(clubType) && item.type === "head" && {
              _hidden_Loft: selectedLoftBuildSpec
            },
            // Lie
            ...["irons", "wedges", "putters"].includes(clubType) && item.type === "head" && {
              _hidden_Lie: selectedLieBuildSpec
            },
            _hidden_item_type: item.type
          }
        };
      });
      const itemsWithoutHeadcoverAndPatch = items.filter(
        (item) => item.properties._hidden_item_type !== "headcover" && item.properties._hidden_item_type !== "patch"
      );
      if (Shopify.theme.role !== "main" && isPreview) {
        console.log("itemsWithoutHeadcoverAndPatch", itemsWithoutHeadcoverAndPatch);
        console.table(itemsWithoutHeadcoverAndPatch.map((item) => item.properties));
      }
      if (isPreview) {
        return;
      }
      setIsLoadingOnAddToCartButton(true);
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          items: itemsWithoutHeadcoverAndPatch.reverse()
          // Reverse the items so that they show in the checkout in the correct order.
        })
      });
      setIsLoadingOnAddToCartButton(false);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || "Failed to add items to cart");
      }
      const result = await response.json();
      await fetchCartState();
      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer) {
        cartDrawer.classList.remove("is-empty");
        const sectionsToRender = [
          {
            id: "cart-drawer",
            section: "cart-drawer",
            selector: ".drawer__inner"
          },
          {
            id: "cart-icon-bubble",
            section: "cart-icon-bubble",
            selector: ".cart-count-bubble"
          }
        ];
        const sectionsResponse = await fetch(
          `${window.location.pathname}?sections=${sectionsToRender.map((section) => section.section).join(",")}`
        );
        const sectionsData = await sectionsResponse.json();
        cartDrawer.renderContents({
          sections: sectionsData,
          id: ((_b2 = (_a2 = result.items) == null ? void 0 : _a2[0]) == null ? void 0 : _b2.id) || null
        });
        const drawerInner = cartDrawer.querySelector(".drawer__inner");
        if (!drawerInner || !drawerInner.children.length) {
          window.location.reload();
        }
      }
    } catch (error2) {
      console.error("Error adding items to cart:", error2);
      alert(error2.message || "Failed to add items to cart. Please try again.");
    } finally {
      setIsLoadingOnAddToCartButton(false);
    }
  };
  const handleLoftSelection = (loft) => {
    setUserSelectedLoft(true);
    if (clubType === "irons") {
      if (selectedLofts.includes(loft) && selectedLofts.length > 1) {
        const newLofts = selectedLofts.filter((l) => l !== loft);
        setSelectedLofts(newLofts);
        updateUrlWithLofts(newLofts);
        setSelectedComposition("");
      } else if (!selectedLofts.includes(loft)) {
        const newLofts = [...selectedLofts, loft];
        setSelectedLofts(newLofts);
        updateUrlWithLofts(newLofts);
        setSelectedComposition("");
      }
    } else {
      const newLofts = [loft];
      setSelectedLofts(newLofts);
      updateUrlWithLofts(newLofts);
      setSelectedComposition("");
    }
  };
  const getInventoryInfo = (product) => {
    return {
      available: (product == null ? void 0 : product.availableForSale) || false,
      quantity: (product == null ? void 0 : product.totalInventory) || 0
    };
  };
  const isLoftAvailable = (loft, baseHead, dexterity) => {
    if (!baseHead) return false;
    const components = getSelectedHeadComponents(baseHead, dexterity, "", "");
    const component = components.find((comp) => {
      var _a2, _b2;
      return ((_b2 = (_a2 = comp.meta) == null ? void 0 : _a2.find((m) => (m == null ? void 0 : m.key) === "loft")) == null ? void 0 : _b2.value) === loft;
    });
    if (!component) return false;
    const inventory = getInventoryInfo(component);
    return inventory.available;
  };
  const getComponentTypeName = (type) => {
    switch (type) {
      case "head":
        return "Head";
      case "shaft":
        return "Shaft";
      case "grip":
        return "Grip";
      case "torque_wrench":
        return "Torque Wrench";
      case "headcover":
        return "Headcover";
      case "patch":
        return "Patch";
      default:
        return type;
    }
  };
  const getAvailableBounces = (baseHead, selectedDexterity2, selectedLoft) => {
    var _a2, _b2;
    try {
      const headComponent = (_a2 = baseHead == null ? void 0 : baseHead.headComponents) == null ? void 0 : _a2.find((component) => (component == null ? void 0 : component.key) === "list_of_heads");
      const heads = ((_b2 = headComponent == null ? void 0 : headComponent.references) == null ? void 0 : _b2.nodes) || [];
      const loftValue = Array.isArray(selectedLoft) ? selectedLoft[0] : selectedLoft;
      const availableHeads = heads.filter((head) => {
        var _a3, _b3, _c2, _d2;
        const dexterityMatch = ((_b3 = (_a3 = head == null ? void 0 : head.meta) == null ? void 0 : _a3.find((m) => (m == null ? void 0 : m.key) === "dexterity")) == null ? void 0 : _b3.value) === selectedDexterity2;
        const loftMatch = ((_d2 = (_c2 = head == null ? void 0 : head.meta) == null ? void 0 : _c2.find((m) => (m == null ? void 0 : m.key) === "loft")) == null ? void 0 : _d2.value) === loftValue;
        const inventory = getInventoryInfo(head);
        return dexterityMatch && loftMatch && inventory.available;
      });
      const bounces = availableHeads.map((head) => {
        var _a3, _b3;
        return (_b3 = (_a3 = head == null ? void 0 : head.meta) == null ? void 0 : _a3.find((m) => (m == null ? void 0 : m.key) === "bounce")) == null ? void 0 : _b3.value;
      }).filter(Boolean).sort((a, b) => parseFloat(a) - parseFloat(b));
      return [...new Set(bounces)];
    } catch (error2) {
      console.error("Error getting available bounces:", error2);
      return [];
    }
  };
  reactExports.useEffect(() => {
    var _a2, _b2, _c2, _d2;
    if (selectedBaseHead && selectedLofts.length > 0) {
      const headComponents = getSelectedHeadComponents(
        selectedBaseHead,
        selectedDexterity,
        productDetails,
        selectedBounce
      );
      const selectedHeadComponent = headComponents.find(
        (component) => {
          var _a3, _b3;
          return ((_b3 = (_a3 = component.meta) == null ? void 0 : _a3.find((m) => (m == null ? void 0 : m.key) === "loft")) == null ? void 0 : _b3.value) === selectedLofts[0];
        }
      );
      if ((_b2 = (_a2 = selectedHeadComponent == null ? void 0 : selectedHeadComponent.meta) == null ? void 0 : _a2.find((m) => (m == null ? void 0 : m.key) === "default_weight_configuration")) == null ? void 0 : _b2.value) {
        setSelectedDefaultWeightConfig(
          (_d2 = (_c2 = selectedHeadComponent == null ? void 0 : selectedHeadComponent.meta) == null ? void 0 : _c2.find((m) => (m == null ? void 0 : m.key) === "default_weight_configuration")) == null ? void 0 : _d2.value
        );
      } else {
        setSelectedDefaultWeightConfig("");
      }
      if (clubType === "wedges") {
        const availableBounces = getAvailableBounces(
          selectedBaseHead,
          selectedDexterity,
          selectedLofts[0]
        );
        if (!availableBounces.includes(selectedBounce)) {
          const firstBounce = availableBounces[0] || "";
          setSelectedBounce(firstBounce);
        }
      } else {
        if (selectedBounce && clubType !== "wedges") {
          setSelectedBounce("");
        }
      }
    } else {
      setSelectedDefaultWeightConfig("");
      if (selectedBounce && clubType !== "wedges") {
        setSelectedBounce("");
      }
    }
  }, [selectedLofts, selectedBaseHead, selectedDexterity, productDetails, clubType, selectedHosel]);
  reactExports.useEffect(() => {
    if (clubType === "wedges" && selectedBaseHead && selectedLofts.length > 0) {
      const availableBounces = getAvailableBounces(
        selectedBaseHead,
        selectedDexterity,
        selectedLofts[0]
      );
      if (!availableBounces.includes(selectedBounce)) {
        const firstBounce = availableBounces[0] || "";
        setSelectedBounce(firstBounce);
      }
    }
  }, [selectedLofts, selectedDexterity, clubType, selectedBaseHead, productDetails]);
  reactExports.useEffect(() => {
    var _a2, _b2;
    if (!selectedBaseHead) return;
    try {
      const defaultShaftsComponent = selectedBaseHead.shaftComponents.find(
        (component) => (component == null ? void 0 : component.key) === "list_of_default_shafts"
      );
      const defaultShafts = ((_a2 = defaultShaftsComponent == null ? void 0 : defaultShaftsComponent.references) == null ? void 0 : _a2.nodes) || [];
      const currentFlexListKey = `list_of_shafts${selectedShaftFlex}`;
      const currentFlexComponent = selectedBaseHead.shaftComponents.find(
        (component) => (component == null ? void 0 : component.key) === currentFlexListKey
      );
      const currentFlexShafts = ((_b2 = currentFlexComponent == null ? void 0 : currentFlexComponent.references) == null ? void 0 : _b2.nodes) || [];
      const materialFilteredShafts = selectedShaftMaterial ? currentFlexShafts.filter((shaft) => {
        var _a3, _b3;
        const material = (_b3 = (_a3 = shaft == null ? void 0 : shaft.meta) == null ? void 0 : _a3.find((m) => (m == null ? void 0 : m.key) === "material")) == null ? void 0 : _b3.value;
        return material === selectedShaftMaterial;
      }) : currentFlexShafts;
      let defaultShaftInFlex;
      if (clubType !== "putters") {
        defaultShaftInFlex = defaultShafts.find((defaultShaft) => {
          var _a3, _b3;
          const isInCurrentFlex = materialFilteredShafts.some((flexShaft) => flexShaft.id === defaultShaft.id);
          if (!isInCurrentFlex) return false;
          if (selectedShaftMaterial) {
            const material = (_b3 = (_a3 = defaultShaft == null ? void 0 : defaultShaft.meta) == null ? void 0 : _a3.find((m) => (m == null ? void 0 : m.key) === "material")) == null ? void 0 : _b3.value;
            return material === selectedShaftMaterial;
          }
          return true;
        });
      } else {
        defaultShaftInFlex = defaultShafts.find((defaultShaft) => {
          var _a3, _b3, _c2, _d2;
          const isInCurrentFlex = materialFilteredShafts.some((flexShaft) => flexShaft.id === defaultShaft.id);
          if (!isInCurrentFlex) return false;
          if (selectedShaftMaterial) {
            const material = (_b3 = (_a3 = defaultShaft == null ? void 0 : defaultShaft.meta) == null ? void 0 : _a3.find((m) => (m == null ? void 0 : m.key) === "material")) == null ? void 0 : _b3.value;
            if (material !== selectedShaftMaterial) return false;
          }
          if (selectedHosel) {
            try {
              const hoselMapping = (_d2 = (_c2 = defaultShaft == null ? void 0 : defaultShaft.meta) == null ? void 0 : _c2.find((m) => (m == null ? void 0 : m.key) === "club_component_mapping")) == null ? void 0 : _d2.value;
              if (!hoselMapping) return false;
              const mappingValues = JSON.parse(hoselMapping);
              const values = Array.isArray(mappingValues) ? mappingValues : Object.values(mappingValues);
              return values.includes(selectedHosel);
            } catch (error2) {
              console.error("Error parsing shaft hosel mapping:", error2);
              return false;
            }
          }
          return true;
        });
      }
      const defaultShaftIsAvailable = getInventoryInfo(defaultShaftInFlex);
      if (defaultShaftInFlex && defaultShaftIsAvailable.available) {
        setSelectedShaft(defaultShaftInFlex);
      } else if (materialFilteredShafts.length > 0) {
        const availableShafts = materialFilteredShafts.filter((shaft) => {
          const inventory = getInventoryInfo(shaft);
          const isCompatible = isShaftCompatible(
            shaft,
            getSelectedHeadComponents(selectedBaseHead, selectedDexterity, selectedBounce, selectedHosel),
            productDetails
          );
          return inventory.available && isCompatible;
        });
        if (availableShafts.length > 0) {
          const freeShafts = availableShafts.filter((shaft) => {
            var _a3, _b3, _c2, _d2;
            const price = ((_d2 = (_c2 = (_b3 = (_a3 = shaft == null ? void 0 : shaft.variants) == null ? void 0 : _a3.nodes) == null ? void 0 : _b3[0]) == null ? void 0 : _c2.price) == null ? void 0 : _d2.amount) ? parseFloat(shaft.variants.nodes[0].price.amount) : 0;
            return price === 0;
          });
          if (freeShafts.length > 0) {
            setSelectedShaft(freeShafts[0]);
          } else {
            setSelectedShaft(availableShafts[0]);
          }
        } else {
          setSelectedShaft(null);
        }
      } else {
        setSelectedShaft(null);
      }
    } catch (error2) {
      console.error("Error setting default shaft:", error2);
      setSelectedShaft(null);
    }
  }, [selectedBaseHead, productDetails, selectedShaftFlex, selectedShaftMaterial, selectedHosel]);
  const handleTorqueWrenchChange = (checked) => {
    var _a2, _b2, _c2;
    const torqueWrenchNodes = (_c2 = (_b2 = (_a2 = selectedBaseHead == null ? void 0 : selectedBaseHead.torqueWrenchComponents) == null ? void 0 : _a2.find(
      (component) => (component == null ? void 0 : component.key) === "torque_wrench_product"
    )) == null ? void 0 : _b2.references) == null ? void 0 : _c2.nodes;
    if (checked && (torqueWrenchNodes == null ? void 0 : torqueWrenchNodes[0])) {
      setSelectedTorqueWrench(torqueWrenchNodes[0]);
      setIncludeTorqueWrench(true);
    } else {
      setSelectedTorqueWrench(null);
      setIncludeTorqueWrench(false);
    }
  };
  const getHeadcoverInfo = (baseHead) => {
    var _a2, _b2, _c2, _d2;
    const headcoverGids = ((_d2 = (_c2 = (_b2 = (_a2 = baseHead == null ? void 0 : baseHead.headcoverComponents) == null ? void 0 : _a2[0]) == null ? void 0 : _b2.references) == null ? void 0 : _c2.nodes) == null ? void 0 : _d2.map((node) => node.id)) || [];
    return headcoverGids.length > 0 ? headcoverGids[0] : null;
  };
  reactExports.useMemo(() => {
    if (!selectedBaseHead || !selectedLofts.length) return [];
    const allHeadComponents = getSelectedHeadComponents(
      selectedBaseHead,
      selectedDexterity,
      productDetails,
      selectedBounce
    );
    return selectedLofts.map((loft) => allHeadComponents.find((component) => {
      var _a2;
      return ((_a2 = component.head_component_loft) == null ? void 0 : _a2.value) === loft;
    })).filter(Boolean);
  }, [selectedBaseHead, selectedLofts, selectedDexterity, productDetails, selectedBounce, selectedHosel]);
  const selectedHoselProduct = reactExports.useMemo(() => {
    var _a2, _b2, _c2, _d2, _e2;
    if (!((_b2 = (_a2 = selectedBaseHead == null ? void 0 : selectedBaseHead.hoselComponents) == null ? void 0 : _a2.find((component) => (component == null ? void 0 : component.key) === "list_of_hosels")) == null ? void 0 : _b2.references) || !selectedHosel) {
      return null;
    }
    try {
      const hoselSkus = ((_e2 = (_d2 = (_c2 = selectedBaseHead == null ? void 0 : selectedBaseHead.hoselComponents) == null ? void 0 : _c2.find((component) => (component == null ? void 0 : component.key) === "list_of_hosels")) == null ? void 0 : _d2.references) == null ? void 0 : _e2.nodes) || [];
      if (!Array.isArray(hoselSkus) || hoselSkus.length === 0) return null;
      return hoselSkus.find((hosel) => {
        var _a3, _b3, _c3, _d3;
        if (!hosel) return false;
        const dexterityMatch = ((_b3 = (_a3 = hosel.meta) == null ? void 0 : _a3.find((m) => (m == null ? void 0 : m.key) === "dexterity")) == null ? void 0 : _b3.value) === selectedDexterity;
        if (!dexterityMatch) return false;
        const inventory = getInventoryInfo(hosel);
        if (!inventory.available) return false;
        try {
          const mapping = (_d3 = (_c3 = hosel.meta) == null ? void 0 : _c3.find((m) => (m == null ? void 0 : m.key) === "club_component_mapping")) == null ? void 0 : _d3.value;
          if (!mapping) return false;
          const mappingValues = JSON.parse(mapping);
          const values = Array.isArray(mappingValues) ? mappingValues : Object.values(mappingValues);
          return values.includes(selectedHosel);
        } catch (error2) {
          console.error("Error parsing hosel mapping:", error2);
          return false;
        }
      }) || null;
    } catch (error2) {
      console.error("Error finding matching hosel product:", error2);
      return null;
    }
  }, [selectedBaseHead, selectedDexterity, selectedHosel]);
  const isShaftCompatible = reactExports.useCallback(
    (shaft, selectedHeadComponents2) => {
      var _a2, _b2;
      if (!shaft || !(selectedHeadComponents2 == null ? void 0 : selectedHeadComponents2.length) && !selectedHosel) return false;
      const shaftMapping = JSON.parse(((_b2 = (_a2 = shaft == null ? void 0 : shaft.meta) == null ? void 0 : _a2.find((m) => (m == null ? void 0 : m.key) === "club_component_mapping")) == null ? void 0 : _b2.value) || "{}");
      const shaftCompatibleSpecs = Object.values(shaftMapping);
      if (selectedHosel) {
        return shaftCompatibleSpecs.includes(selectedHosel);
      }
      return selectedHeadComponents2.every((headComponent) => {
        var _a3, _b3;
        const headMapping = JSON.parse(
          ((_b3 = (_a3 = headComponent == null ? void 0 : headComponent.meta) == null ? void 0 : _a3.find((m) => (m == null ? void 0 : m.key) === "club_component_mapping")) == null ? void 0 : _b3.value) || "{}"
        );
        const headComponentSpecs = Object.values(headMapping);
        const isCompatible = headComponentSpecs.some((spec) => shaftCompatibleSpecs.includes(spec));
        return isCompatible;
      });
    },
    [selectedHosel]
  );
  const torqueWrenchProduct = reactExports.useMemo(() => {
    var _a2, _b2, _c2;
    return (_c2 = (_b2 = (_a2 = selectedBaseHead == null ? void 0 : selectedBaseHead.torqueWrenchComponents) == null ? void 0 : _a2.find((component) => (component == null ? void 0 : component.key) === "torque_wrench_product")) == null ? void 0 : _b2.references) == null ? void 0 : _c2.nodes;
  }, [selectedBaseHead]);
  const hasTorqueWrench = reactExports.useMemo(() => {
    return (torqueWrenchProduct == null ? void 0 : torqueWrenchProduct.length) > 0;
  }, [torqueWrenchProduct]);
  const shaftMaterials = reactExports.useMemo(() => {
    var _a2, _b2, _c2, _d2, _e2, _f2, _g2, _h2, _i2, _j2, _k2, _l2;
    if (!selectedBaseHead) return [];
    try {
      const allShaftLists = [
        (_b2 = (_a2 = selectedBaseHead.shaftComponents.find((component) => (component == null ? void 0 : component.key) === "list_of_shaftsladies")) == null ? void 0 : _a2.references) == null ? void 0 : _b2.nodes,
        (_d2 = (_c2 = selectedBaseHead.shaftComponents.find((component) => (component == null ? void 0 : component.key) === "list_of_shaftssenior")) == null ? void 0 : _c2.references) == null ? void 0 : _d2.nodes,
        (_f2 = (_e2 = selectedBaseHead.shaftComponents.find((component) => (component == null ? void 0 : component.key) === "list_of_shaftsregular")) == null ? void 0 : _e2.references) == null ? void 0 : _f2.nodes,
        (_h2 = (_g2 = selectedBaseHead.shaftComponents.find((component) => (component == null ? void 0 : component.key) === "list_of_shaftsstiff")) == null ? void 0 : _g2.references) == null ? void 0 : _h2.nodes,
        (_j2 = (_i2 = selectedBaseHead.shaftComponents.find((component) => (component == null ? void 0 : component.key) === "list_of_shaftsxstiff")) == null ? void 0 : _i2.references) == null ? void 0 : _j2.nodes,
        (_l2 = (_k2 = selectedBaseHead.shaftComponents.find((component) => (component == null ? void 0 : component.key) === "list_of_shaftsputter")) == null ? void 0 : _k2.references) == null ? void 0 : _l2.nodes
      ];
      const materials = /* @__PURE__ */ new Set();
      allShaftLists.forEach((listValue) => {
        if (listValue) {
          listValue.forEach((shaft) => {
            var _a3;
            const materialMeta = (_a3 = shaft == null ? void 0 : shaft.meta) == null ? void 0 : _a3.find((m) => (m == null ? void 0 : m.namespace) === "shaft_component" && (m == null ? void 0 : m.key) === "material");
            if (materialMeta == null ? void 0 : materialMeta.value) {
              materials.add(materialMeta.value);
            }
          });
        }
      });
      return Array.from(materials).sort();
    } catch (error2) {
      console.error("Error getting shaft materials:", error2);
      return [];
    }
  }, [selectedBaseHead, productDetails]);
  shaftMaterials.length > 0 && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mb-4", children: [
    /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "p4 mb-2 text-shadow-black", children: "Material" }),
    /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex flex-wrap gap-2", children: shaftMaterials.map((material) => {
      const isAvailable = getAvailableShafts(selectedBaseHead, selectedShaftFlex, false).some(
        (shaft) => {
          var _a2, _b2;
          return ((_b2 = (_a2 = shaft == null ? void 0 : shaft.meta) == null ? void 0 : _a2.find((m) => (m == null ? void 0 : m.key) === "material")) == null ? void 0 : _b2.value) === material;
        }
      );
      const translatedMaterial = shopLocale !== "en" && materialTranslationsValues[material] ? materialTranslationsValues[material] : material;
      return /* @__PURE__ */ jsxRuntimeExports.jsx(
        "button",
        {
          onClick: () => {
            setSelectedShaftMaterial(material);
            setSelectedShaft("");
          },
          disabled: !isAvailable,
          className: `rounded-lg border border-shadow-black px-4 py-2 transition-colors ${selectedShaftMaterial === material ? "bg-shadow-black text-fairway" : "bg-transparent text-shadow-black hover:bg-grey-v3"} ${!isAvailable && "cursor-not-allowed border-grey-v3 bg-system-disabled text-grey-v3 opacity-50"}`,
          children: translatedMaterial
        },
        material
      );
    }) })
  ] });
  const formatLoftDisplay = (loft) => {
    const loftMap = {
      "Pitching Wedge": "W",
      "Gap Wedge": "G",
      "Sand Wedge": "S",
      "Lob Wedge": "L"
    };
    if (loftMap[loft]) {
      return loftMap[loft];
    }
    if (loft.includes("Iron")) {
      return loft.split(" ")[0];
    }
    return loft;
  };
  const getCompositionLofts = (composition) => {
    const availableLofts = getAvailableLofts(selectedBaseHead, selectedDexterity).filter(
      (loft) => isLoftAvailable(loft, selectedBaseHead, selectedDexterity)
    );
    const loftSortOrder = (loft) => {
      const wedgeOrder = {
        "Pitching Wedge": 9.5,
        "Gap Wedge": 9.6,
        "Sand Wedge": 9.7,
        "Lob Wedge": 9.8
      };
      if (wedgeOrder[loft]) return wedgeOrder[loft];
      return parseInt(loft.split(" ")[0]);
    };
    const loftOrder = availableLofts.sort((a, b) => loftSortOrder(a) - loftSortOrder(b));
    const [start, end] = composition.split(" - ");
    const startLoft = start.includes("Iron") ? start : `${start} Iron`;
    const endLoft = {
      W: "Pitching Wedge",
      G: "Gap Wedge",
      S: "Sand Wedge",
      L: "Lob Wedge"
    }[end] || `${end} Iron`;
    const allPossibleLofts = {};
    for (let i = 3; i <= 9; i++) {
      allPossibleLofts[`${i} Iron`] = i;
    }
    allPossibleLofts["Pitching Wedge"] = 9.5;
    allPossibleLofts["Gap Wedge"] = 9.6;
    allPossibleLofts["Sand Wedge"] = 9.7;
    allPossibleLofts["Lob Wedge"] = 9.8;
    const startValue = allPossibleLofts[startLoft];
    const endValue = allPossibleLofts[endLoft];
    if (startValue === void 0 || endValue === void 0) {
      console.error("Invalid loft range:", startLoft, endLoft);
      return [];
    }
    return loftOrder.filter((loft) => {
      const loftValue = loftSortOrder(loft);
      return loftValue >= startValue && loftValue <= endValue;
    });
  };
  reactExports.useEffect(() => {
    var _a2, _b2, _c2, _d2;
    if (!selectedBaseHead) return;
    if ((_b2 = (_a2 = selectedBaseHead.meta) == null ? void 0 : _a2.filter(Boolean).find((m) => (m == null ? void 0 : m.key) === "list_of_default_heads")) == null ? void 0 : _b2.value) {
      const defaultLofts = JSON.parse(
        (_d2 = (_c2 = selectedBaseHead.meta) == null ? void 0 : _c2.filter(Boolean).find((m) => (m == null ? void 0 : m.key) === "list_of_default_heads")) == null ? void 0 : _d2.value
      );
      const availableDefaultLofts = defaultLofts.filter(
        (loft) => isLoftAvailable(loft, selectedBaseHead, selectedDexterity)
      );
      if (availableDefaultLofts.length > 0) {
        setSelectedLofts(availableDefaultLofts);
        setSelectedComposition("");
        return;
      }
    }
    const availableLofts = getAvailableLofts(selectedBaseHead, selectedDexterity).filter(
      (loft) => isLoftAvailable(loft, selectedBaseHead, selectedDexterity)
    );
    if (availableLofts.length > 0) {
      if (clubType === "irons") {
        setSelectedLofts(availableLofts);
      } else {
        setSelectedLofts([availableLofts[0]]);
      }
      setSelectedComposition("");
      return;
    }
  }, [selectedBaseHead, selectedDexterity, clubType]);
  const getAvailableHosels = (baseHead) => {
    var _a2, _b2;
    try {
      return JSON.parse(((_b2 = (_a2 = baseHead == null ? void 0 : baseHead.meta) == null ? void 0 : _a2.find((m) => (m == null ? void 0 : m.key) === "available_putter_hosels")) == null ? void 0 : _b2.value) || "[]");
    } catch (error2) {
      console.error("Error parsing available hosels:", error2);
      return [];
    }
  };
  reactExports.useEffect(() => {
    var _a2, _b2;
    if (selectedBaseHead && clubType === "putters") {
      const availableHosels = getAvailableHosels(selectedBaseHead);
      if (!selectedHosel) {
        const defaultHosel = (_b2 = (_a2 = selectedBaseHead == null ? void 0 : selectedBaseHead.meta) == null ? void 0 : _a2.find((m) => (m == null ? void 0 : m.key) === "default_putter_hosel")) == null ? void 0 : _b2.value;
        if (defaultHosel && availableHosels.includes(defaultHosel)) {
          setSelectedHosel(defaultHosel);
        } else if (availableHosels.length > 0) {
          setSelectedHosel(availableHosels[0]);
        }
      } else if (!availableHosels.includes(selectedHosel)) {
        setSelectedHosel("");
      }
    } else {
      setSelectedHosel("");
    }
  }, [selectedBaseHead, clubType]);
  const hasBalancedComponents = (items) => {
    if (!items.length) return false;
    const counts = items.reduce((acc, item) => {
      acc[item.type] = (acc[item.type] || 0) + item.qty;
      return acc;
    }, {});
    const headCount = counts.head || 0;
    if (headCount === 0) return false;
    return (counts.shaft || 0) === headCount && (counts.grip || 0) === headCount;
  };
  const cartValidation = reactExports.useMemo(() => {
    const insufficientStockItems = selectedForCartItems.filter((item) => {
      const inventory = getInventoryInfo(item.product);
      return !inventory.available;
    });
    const isBalanced = hasBalancedComponents(selectedForCartItems);
    const currentCartTotal = cartQuantity || 0;
    const wouldExceedMaxItems = currentCartTotal + totalSelectedItemsCount >= MAX_BUNDLED_COMPONENTS_IN_CART;
    const wouldExceedMaxConfigurations = configurationInCartCount >= MAX_BUNDLE_CONFIGURATIONS_IN_CART;
    return {
      hasIssue: insufficientStockItems.length > 0 || !isBalanced || wouldExceedMaxItems || wouldExceedMaxConfigurations,
      items: insufficientStockItems.map((item) => {
        var _a2;
        return {
          title: ((_a2 = item.product) == null ? void 0 : _a2.title) || "Unknown Product",
          type: item.type,
          requested: item.qty,
          available: getInventoryInfo(item.product).available
        };
      }),
      isBalanced,
      exceedsMaxItems: wouldExceedMaxItems,
      maxItems: MAX_BUNDLED_COMPONENTS_IN_CART,
      currentTotal: currentCartTotal,
      exceedsMaxConfigurations: wouldExceedMaxConfigurations,
      maxConfigurations: MAX_BUNDLE_CONFIGURATIONS_IN_CART,
      currentConfigurations: configurationInCartCount
    };
  }, [selectedForCartItems, productDetails, cartQuantity, totalSelectedItemsCount, configurationInCartCount]);
  reactExports.useEffect(() => {
    var _a2, _b2, _c2, _d2, _e2;
    if (!((_b2 = (_a2 = selectedBaseHead == null ? void 0 : selectedBaseHead.hoselComponents) == null ? void 0 : _a2.find((component) => (component == null ? void 0 : component.key) === "list_of_hosels")) == null ? void 0 : _b2.references))
      return;
    const hoselSkus = ((_e2 = (_d2 = (_c2 = selectedBaseHead == null ? void 0 : selectedBaseHead.hoselComponents) == null ? void 0 : _c2.find((component) => (component == null ? void 0 : component.key) === "list_of_hosels")) == null ? void 0 : _d2.references) == null ? void 0 : _e2.nodes) || [];
    if (!Array.isArray(hoselSkus) || hoselSkus.length === 0) return;
  }, [(_a = selectedBaseHead == null ? void 0 : selectedBaseHead.list_of_hosels) == null ? void 0 : _a.value, selectedDexterity, selectedHosel, productDetails, getInventoryInfo]);
  reactExports.useEffect(() => {
    if (selectedBaseHead && selectedDexterity) {
      const components = getSelectedHeadComponents(
        selectedBaseHead,
        selectedDexterity,
        productDetails,
        selectedBounce
      );
      setSelectedHeadComponents(components);
    } else {
      setSelectedHeadComponents([]);
    }
  }, [selectedBaseHead, selectedDexterity, selectedBounce, selectedHosel, productDetails]);
  reactExports.useEffect(() => {
    if (defaultShaftMaterial) {
      setSelectedShaftMaterial(defaultShaftMaterial);
    }
  }, [defaultShaftMaterial]);
  reactExports.useEffect(() => {
    if (defaultShaftFlex) {
      setSelectedShaftFlex(defaultShaftFlex.toLowerCase());
    } else if (clubType === "putters") {
      setSelectedShaftFlex("putter");
    } else {
      setSelectedShaftFlex("regular");
    }
  }, [defaultShaftFlex, clubType]);
  reactExports.useEffect(() => {
    if (defaultGripSize) {
      setSelectedGripSize(defaultGripSize);
    }
  }, [defaultGripSize]);
  reactExports.useEffect(() => {
    if (defaultLengthAdjustment) {
      setSelectedLengthBuildSpec(defaultLengthAdjustment);
    }
  }, [defaultLengthAdjustment]);
  const handleResetToDefaults = () => {
    window.location.reload();
  };
  const showManualChangesWarning = () => {
    const hasManualChanges = Object.values(hasManualShaftChanges).some(Boolean) || Object.values(hasManualGripChanges).some(Boolean) || Object.values(hasManualBuildSpecChanges).some(Boolean);
    setShowManualChangesMessage(hasManualChanges);
  };
  const handleMainConfigChange = (callback) => {
    showManualChangesWarning();
    callback();
  };
  const handleAdvancedPanelOpen = () => {
    setIsAdvancedPanelOpen(true);
    setHasManualShaftChanges({
      material: false,
      flex: false,
      shaft: false
    });
    setHasManualGripChanges({
      grip: false,
      size: false
    });
    setHasManualBuildSpecChanges({
      loft: false,
      lie: false
    });
    setShowManualChangesMessage(false);
  };
  const fetchCartState = async () => {
    try {
      const response = await fetch("/cart.js");
      if (!response.ok) throw new Error("Failed to fetch cart");
      const cartData = await response.json();
      const totalBundledItems = cartData.items.reduce((total, item) => {
        var _a2;
        if (!((_a2 = item.properties) == null ? void 0 : _a2._bundled)) return total;
        const bundledCount = Number(item.properties._bundled) || 0;
        return total + bundledCount;
      }, 0);
      const ConfigurationInCartCount = cartData.items.reduce(
        (total, item) => {
          var _a2;
          return ((_a2 = item.properties) == null ? void 0 : _a2._bundled) ? total + 1 : total;
        },
        0
      );
      setCartQuantity(totalBundledItems);
      setConfigurationInCartCount(ConfigurationInCartCount);
      setIsLoadingCart(false);
    } catch (error2) {
      console.error("Error fetching cart:", error2);
      setIsLoadingCart(false);
    }
  };
  reactExports.useEffect(() => {
    const handleCartUpdate = () => {
      fetchCartState();
    };
    document.addEventListener("cart:updated", handleCartUpdate);
    fetchCartState();
    return () => {
      document.removeEventListener("cart:updated", handleCartUpdate);
    };
  }, []);
  const totalPrice = reactExports.useMemo(() => {
    if (!(selectedForCartItems == null ? void 0 : selectedForCartItems.length)) return { price: 0 };
    return selectedForCartItems.reduce(
      (acc, item) => {
        var _a2, _b2, _c2, _d2, _e2, _f2, _g2, _h2, _i2, _j2;
        if (!((_e2 = (_d2 = (_c2 = (_b2 = (_a2 = item == null ? void 0 : item.product) == null ? void 0 : _a2.variants) == null ? void 0 : _b2.nodes) == null ? void 0 : _c2[0]) == null ? void 0 : _d2.price) == null ? void 0 : _e2.amount)) return acc;
        if (item.type === "headcover" || item.type === "patch") return acc;
        const variant = (_h2 = (_g2 = (_f2 = item == null ? void 0 : item.product) == null ? void 0 : _f2.variants) == null ? void 0 : _g2.nodes) == null ? void 0 : _h2[0];
        const itemPrice = parseFloat((_i2 = variant == null ? void 0 : variant.price) == null ? void 0 : _i2.amount) * item.qty;
        const currencyCode = (_j2 = variant == null ? void 0 : variant.price) == null ? void 0 : _j2.currencyCode;
        return {
          price: acc.price + itemPrice,
          currencyCode
        };
      },
      { price: 0 }
    );
  }, [selectedForCartItems]);
  const totalCompareAtPrice = reactExports.useMemo(() => {
    if (!(selectedForCartItems == null ? void 0 : selectedForCartItems.length)) return { compareAtPrice: 0 };
    return selectedForCartItems.reduce(
      (acc, item) => {
        var _a2, _b2, _c2, _d2, _e2;
        if (item.type === "headcover" || item.type === "patch") return acc;
        const variant = (_c2 = (_b2 = (_a2 = item == null ? void 0 : item.product) == null ? void 0 : _a2.variants) == null ? void 0 : _b2.nodes) == null ? void 0 : _c2[0];
        const itemComparePrice = parseFloat(((_d2 = variant == null ? void 0 : variant.compareAtPrice) == null ? void 0 : _d2.amount) || 0) * item.qty;
        const currencyCode = (_e2 = variant == null ? void 0 : variant.compareAtPrice) == null ? void 0 : _e2.currencyCode;
        return {
          compareAtPrice: acc.compareAtPrice + itemComparePrice,
          currencyCode
        };
      },
      { compareAtPrice: 0 }
    );
  }, [selectedForCartItems]);
  const formattedTotalPrice = reactExports.useMemo(() => formatPrice(totalPrice.price, totalPrice.currencyCode), [totalPrice]);
  const formattedTotalCompareAtPrice = reactExports.useMemo(
    () => formatPrice(totalCompareAtPrice.compareAtPrice, totalPrice.currencyCode),
    [totalCompareAtPrice]
  );
  const addToCartButtonState = reactExports.useMemo(() => {
    if (isLoadingCart) {
      return {
        disabled: true,
        text: labels.loading_text || "Loading..."
      };
    }
    if (cartQuantity + totalSelectedItemsCount >= MAX_BUNDLED_COMPONENTS_IN_CART) {
      return {
        disabled: true,
        text: labels.add_to_cart_button_text || "Add to Cart"
      };
    }
    if (configurationInCartCount >= MAX_BUNDLE_CONFIGURATIONS_IN_CART) {
      return {
        disabled: true,
        text: labels.add_to_cart_button_text || "Add to Cart"
      };
    }
    if (!selectedLofts.length || !selectedForCartItems.length || !selectedShaft || cartValidation.hasIssue) {
      return {
        disabled: true,
        text: labels.add_to_cart_button_text || "Add to Cart"
      };
    }
    return {
      disabled: false,
      text: labels.add_to_cart_button_text || "Add to Cart"
    };
  }, [
    isLoadingCart,
    cartQuantity,
    totalSelectedItemsCount,
    configurationInCartCount,
    selectedLofts.length,
    selectedForCartItems.length,
    selectedShaft,
    cartValidation.hasIssue,
    labels.add_to_cart_button_text
  ]);
  const hasUserInteracted = reactExports.useRef(false);
  const updateUrlWithLofts = reactExports.useCallback((lofts) => {
    if (!lofts || lofts.length === 0 || !hasUserInteracted.current) return;
    const url = new URL(window.location.href);
    Array.from(params.keys()).forEach((key) => {
      if (key.startsWith("clubNumber")) {
        params.delete(key);
      }
    });
    lofts.forEach((loft, index) => {
      params.append(`clubNumber${index + 1}`, loft);
    });
    window.history.replaceState({}, "", `${url.pathname}?${params.toString()}`);
  }, []);
  reactExports.useEffect(() => {
    if (selectedComposition && selectedBaseHead) {
      const compositionLofts = getCompositionLofts(selectedComposition);
      setSelectedLofts(compositionLofts);
      updateUrlWithLofts(compositionLofts);
    }
  }, [selectedComposition, selectedBaseHead, updateUrlWithLofts]);
  reactExports.useEffect(() => {
    if (selectedBaseHead && selectedDexterity) {
      const urlLofts = getLoftsFromUrl();
      if (urlLofts.length > 0) {
        const validLofts = urlLofts.filter((loft) => isLoftAvailable(loft, selectedBaseHead, selectedDexterity));
        if (validLofts.length > 0) {
          setSelectedLofts(validLofts);
          setSelectedComposition("");
          return;
        }
      }
    }
  }, [selectedBaseHead, selectedDexterity, getLoftsFromUrl]);
  const updateUrlWithModel = reactExports.useCallback((model) => {
    if (!model || !hasUserInteracted.current) return;
    const url = new URL(window.location.href);
    if (model) {
      params.set("model", model);
    } else {
      params.delete("model");
    }
    window.history.replaceState({}, "", `${url.pathname}?${params.toString()}`);
  }, []);
  const updateUrlWithColor = reactExports.useCallback((color) => {
    if (!color || !hasUserInteracted.current) return;
    const url = new URL(window.location.href);
    if (color) {
      params.set("color", color);
    } else {
      params.delete("color");
    }
    window.history.replaceState({}, "", `${url.pathname}?${params.toString()}`);
  }, []);
  reactExports.useEffect(() => {
    const urlModel = getParamFromUrl("model");
    if (urlModel && availableModels.includes(urlModel)) {
      setSelectedModel(urlModel);
    }
  }, [availableModels]);
  reactExports.useEffect(() => {
    if (selectedModel) {
      updateUrlWithModel(selectedModel);
    }
  }, [selectedModel]);
  reactExports.useEffect(() => {
    const urlColor = getParamFromUrl("color");
    if (urlColor && availableColors.includes(urlColor)) {
      setSelectedColor(urlColor);
    }
  }, [availableColors]);
  reactExports.useEffect(() => {
    if (selectedColor) {
      updateUrlWithColor(selectedColor);
    }
  }, [selectedColor]);
  const getComponentMapping = (product) => {
    var _a2, _b2;
    try {
      const mapping = JSON.parse(((_b2 = (_a2 = product == null ? void 0 : product.meta) == null ? void 0 : _a2.find((m) => (m == null ? void 0 : m.key) === "club_component_mapping")) == null ? void 0 : _b2.value) || "{}");
      return Object.values(mapping).join(", ") || "N/A";
    } catch (error2) {
      console.error("Error getting mapping:", error2);
      return "Error";
    }
  };
  const getTranslatedDexterity = reactExports.useMemo(() => {
    if (selectedDexterity === "right" || selectedDexterity === "R") {
      return (cartNoteLabels == null ? void 0 : cartNoteLabels.cart_note_dexterity_right) || "Right-Handed";
    } else if (selectedDexterity === "left" || selectedDexterity === "L") {
      return (cartNoteLabels == null ? void 0 : cartNoteLabels.cart_note_dexterity_left) || "Left-Handed";
    }
    return selectedDexterity;
  }, [selectedDexterity, cartNoteLabels]);
  reactExports.useEffect(() => {
    const skeletonElement = document.getElementById("club-configurator-skeleton");
    const isFullyLoaded = !isLoading && !isLoadingCart && !!productData && Array.isArray(baseHeads) && baseHeads.length > 0;
    if (skeletonElement && isFullyLoaded) {
      skeletonElement.style.display = "none";
      setSkeletonHidden(true);
    }
  }, [isLoading, isLoadingCart, productData, baseHeads]);
  if (isLoading) return /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex h-full min-h-[580px] w-full items-center justify-center" });
  if (error) return /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex h-full min-h-[580px] w-full items-center justify-center", children: error });
  if (!skeletonHidden) return /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "opacity-0" });
  return /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "club-configurator relative", children: [
    /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mx-auto flex max-w-[1156px] flex-col gap-6 lg:grid lg:grid-cols-[1fr_448px] lg:px-4 xl:px-0", children: [
      /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex flex-col gap-4 px-4 md:px-24 lg:px-0", children: selectedBaseHead && ((_b = cachedImages[selectedBaseHead.id]) == null ? void 0 : _b.length) > 0 ? /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex max-h-[566px] w-full gap-2", children: [
        /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "hidden w-24 lg:block", children: /* @__PURE__ */ jsxRuntimeExports.jsxs(
          Splide2,
          {
            ref: (slider) => {
              if (slider) {
                setThumbsSlider(slider.splide);
                splideRef.current = slider.splide.root;
              }
            },
            options: {
              type: "slide",
              direction: "ttb",
              height: "566px",
              perPage: 5,
              gap: "0.5rem",
              pagination: false,
              isNavigation: true,
              arrows: true,
              focus: "start",
              // Add this to align thumbnails to top
              waitForTransition: true,
              wheelSleep: 50
            },
            className: "thumbnail-slider",
            children: [
              (_c = cachedImages[selectedBaseHead.id]) == null ? void 0 : _c.map((edge, index) => /* @__PURE__ */ jsxRuntimeExports.jsx(SplideSlide, { className: "flex h-[94] cursor-pointer bg-gray-100", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                "img",
                {
                  src: edge.node.url,
                  srcSet: edge.node.small ? `${edge.node.small} 300w` : void 0,
                  alt: edge.node.altText || `Thumbnail ${index + 1}`,
                  className: "h-full w-full object-cover",
                  width: 94,
                  height: 94,
                  loading: index < 4 ? "eager" : "lazy"
                }
              ) }, index)),
              uniqueSelectedForCartItems == null ? void 0 : uniqueSelectedForCartItems.map(({ product }) => /* @__PURE__ */ jsxRuntimeExports.jsx(SplideSlide, { className: "flex h-[94] cursor-pointer bg-gray-100", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                "img",
                {
                  src: product.images.edges[0].node.url,
                  srcSet: product.images.edges[0].node.small ? `${product.images.edges[0].node.small} 300w` : void 0,
                  alt: product.title,
                  className: "h-full w-full object-contain",
                  width: 94,
                  height: 94,
                  loading: "lazy"
                }
              ) }, product.id))
            ]
          }
        ) }),
        /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex-1", children: /* @__PURE__ */ jsxRuntimeExports.jsxs(
          Splide2,
          {
            ref: (slider) => {
              if (slider) {
                setMainSlider(slider.splide);
                if (thumbsSlider) {
                  slider.splide.sync(thumbsSlider);
                }
              }
            },
            options: {
              type: "fade",
              rewind: true,
              perPage: 1,
              height: "100%",
              pagination: false,
              arrows: true,
              drag: true,
              mediaQuery: "min",
              breakpoints: {
                768: {
                  arrows: false
                }
              }
            },
            className: "main-slider flex",
            children: [
              (_d = cachedImages[selectedBaseHead.id]) == null ? void 0 : _d.map((edge, index) => {
                var _a2, _b2, _c2, _d2, _e2, _f2, _g2, _h2, _i2, _j2, _k2, _l2;
                return /* @__PURE__ */ jsxRuntimeExports.jsx(SplideSlide, { children: /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "relative flex w-full items-start justify-center bg-gray-100 md:h-[566px]", children: [
                  /* @__PURE__ */ jsxRuntimeExports.jsx(
                    "img",
                    {
                      src: edge.node.large || edge.node.url,
                      srcSet: edge.node.small ? `${edge.node.small} 300w,
                                       ${edge.node.medium || edge.node.url} 600w,
                                       ${edge.node.large || edge.node.url} 1200w` : void 0,
                      sizes: "(max-width: 768px) 100vw, 566px",
                      alt: edge.node.altText || ((_g2 = (_f2 = (_e2 = (_d2 = (_c2 = (_b2 = (_a2 = edge.node.url) == null ? void 0 : _a2.match(/\/files\/([^\/]+?)(?:_[^\/]*|$)/)) == null ? void 0 : _b2[1]) == null ? void 0 : _c2.replace(/\.jpg.*$/i, "")) == null ? void 0 : _d2.replace(/-Beauty.*/i, "")) == null ? void 0 : _e2.replace(/-\w{8,}$/, "")) == null ? void 0 : _f2.replace(/-/g, " ")) == null ? void 0 : _g2.trim()) || `Club Hero Image ${index + 1}`,
                      className: "object-cover object-center md:h-[566px] md:w-[566px]",
                      width: 566,
                      height: 566,
                      loading: index === 0 ? "eager" : "lazy"
                    }
                  ),
                  ((_h2 = window.clubConfiguratorData.translations) == null ? void 0 : _h2.promotions_badge_icon) && index === 0 && /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "absolute bottom-4 left-4 w-[64px] lg:w-[128px]", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                    "img",
                    {
                      src: (_i2 = window.clubConfiguratorData.translations) == null ? void 0 : _i2.promotions_badge_icon,
                      alt: ((_l2 = (_k2 = (_j2 = window.clubConfiguratorData.translations) == null ? void 0 : _j2.promotions_badge_icon) == null ? void 0 : _k2.split("/").pop()) == null ? void 0 : _l2.split(".")[0].replace(/-/g, " ")) || "Promotions Badge",
                      width: 128,
                      height: 128
                    }
                  ) })
                ] }) }, index);
              }),
              uniqueSelectedForCartItems == null ? void 0 : uniqueSelectedForCartItems.map(({ product, type }) => /* @__PURE__ */ jsxRuntimeExports.jsx(SplideSlide, { className: `${type}-slide`, children: /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex h-full w-full items-center justify-center bg-gray-100 md:h-[566px]", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                "img",
                {
                  src: product.images.edges[0].node.url,
                  srcSet: product.images.edges[0].node.small ? `${product.images.edges[0].node.small} 300w, ${product.images.edges[0].node.medium} 600w, ${product.images.edges[0].node.large} 1200w` : void 0,
                  sizes: "(max-width: 768px) 100vw, 566px",
                  alt: product.title,
                  className: "object-contain object-center md:h-[566px] md:w-[566px]",
                  width: 566,
                  height: 566,
                  loading: "lazy"
                }
              ) }) }, product.id))
            ]
          }
        ) })
      ] }) : /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex h-[684px] w-full max-w-[684px] items-center justify-center bg-fairway-light p-4", children: /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "h2 text-center text-fairway", children: (selectedBaseHead == null ? void 0 : selectedBaseHead.title) || (productData == null ? void 0 : productData.title) || "" }) }) }),
      /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { id: "club-configurator-main", className: "relative flex h-full max-h-fit flex-col px-4 md:px-24 lg:px-0", children: [
        ((_g = (_f = (_e = productData == null ? void 0 : productData.productMeta) == null ? void 0 : _e.filter(Boolean)) == null ? void 0 : _f.find((m) => (m == null ? void 0 : m.key) === "custom_badge")) == null ? void 0 : _g.value) && /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "badge mb-2", children: (_j = (_i = (_h = productData == null ? void 0 : productData.productMeta) == null ? void 0 : _h.filter(Boolean)) == null ? void 0 : _i.find((m) => (m == null ? void 0 : m.key) === "custom_badge")) == null ? void 0 : _j.value }),
        /* @__PURE__ */ jsxRuntimeExports.jsx("h1", { className: "display-3 mb-6 text-balance", children: (productData == null ? void 0 : productData.title) || "" }),
        /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex-1", children: [
          /* @__PURE__ */ jsxRuntimeExports.jsx(
            DexteritySelector,
            {
              label: (labels == null ? void 0 : labels.dexterity) || "Dexterity",
              labelRight: (labels == null ? void 0 : labels.dexterity_right) || "Right",
              labelLeft: (labels == null ? void 0 : labels.dexterity_left) || "Left",
              selectedDexterity,
              setSelectedDexterity: (dexterity) => handleMainConfigChange(() => setSelectedDexterity(dexterity)),
              selectedBaseHead,
              productDetails,
              getSelectedHeadComponents,
              selectedHosel
            }
          ),
          /* @__PURE__ */ jsxRuntimeExports.jsx(
            HeadModelColorSelector,
            {
              labelModel: (labels == null ? void 0 : labels.head_model) || "Head Model",
              labelColor: (labels == null ? void 0 : labels.head_color) || "Head Color",
              headModelTranslationsValues,
              headColorTranslationsValues,
              selectedModel,
              setSelectedModel: (model) => handleMainConfigChange(() => setSelectedModel(model)),
              selectedColor,
              setSelectedColor: (color) => handleMainConfigChange(() => setSelectedColor(color)),
              availableModels,
              availableModelsForColor,
              availableColors,
              availableColorsForModel,
              defaultModel,
              defaultColor,
              baseHeads,
              onUserInteraction: () => {
                hasUserInteracted.current = true;
              }
            }
          ),
          clubType === "putters" && selectedBaseHead && getAvailableHosels(selectedBaseHead).length > 0 && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mb-4", children: [
            /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "p4 mb-2 text-shadow-black", children: labels.select_hosel || "Hosel" }),
            /* @__PURE__ */ jsxRuntimeExports.jsx(
              Mo,
              {
                value: selectedHosel,
                onChange: (hosel) => handleMainConfigChange(() => setSelectedHosel(hosel)),
                children: /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "relative", children: [
                  /* @__PURE__ */ jsxRuntimeExports.jsxs(Mo.Button, { className: "relative w-full cursor-pointer rounded border bg-white py-2 pl-3 pr-10 text-left focus:outline-none focus:ring-2 focus:ring-shadow-black sm:text-sm", children: [
                    /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "block truncate", children: selectedHosel || "Select a Hosel" }),
                    /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                      "svg",
                      {
                        className: "h-5 w-5 text-gray-400",
                        xmlns: "http://www.w3.org/2000/svg",
                        viewBox: "0 0 20 20",
                        fill: "currentColor",
                        "aria-hidden": "true",
                        children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                          "path",
                          {
                            fillRule: "evenodd",
                            d: "M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z",
                            clipRule: "evenodd"
                          }
                        )
                      }
                    ) })
                  ] }),
                  /* @__PURE__ */ jsxRuntimeExports.jsx(Mo.Options, { className: "absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm", children: getAvailableHosels(selectedBaseHead).map((hosel) => {
                    const hasComponents = getSelectedHeadComponents(selectedBaseHead, selectedDexterity, "", hosel).length > 0;
                    return /* @__PURE__ */ jsxRuntimeExports.jsx(
                      Mo.Option,
                      {
                        value: hosel,
                        disabled: !hasComponents,
                        className: ({ active }) => `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active ? "bg-shadow-black text-fairway" : "text-gray-900"} ${!hasComponents ? "cursor-not-allowed bg-system-disabled opacity-50" : ""}`,
                        children: ({ selected, active }) => /* @__PURE__ */ jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [
                          /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: `block truncate ${selected ? "font-medium" : "font-normal"}`, children: hosel }),
                          selected && /* @__PURE__ */ jsxRuntimeExports.jsx(
                            "span",
                            {
                              className: `absolute inset-y-0 left-0 flex items-center pl-3 ${active ? "text-fairway" : "text-shadow-black"}`,
                              children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                                "svg",
                                {
                                  xmlns: "http://www.w3.org/2000/svg",
                                  className: "h-5 w-5",
                                  viewBox: "0 0 20 20",
                                  fill: "currentColor",
                                  children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                                    "path",
                                    {
                                      fillRule: "evenodd",
                                      d: "M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z",
                                      clipRule: "evenodd"
                                    }
                                  )
                                }
                              )
                            }
                          )
                        ] })
                      },
                      hosel
                    );
                  }) })
                ] })
              }
            )
          ] }),
          /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex flex-col", children: [
            /* @__PURE__ */ jsxRuntimeExports.jsx(
              LoftSelector,
              {
                clubType,
                selectedBaseHead,
                selectedLofts,
                setSelectedLofts: (lofts) => {
                  setSelectedLofts(lofts);
                  updateUrlWithLofts(lofts);
                },
                selectedDexterity,
                productDetails,
                getAvailableLofts,
                isLoftAvailable,
                getCompositionLofts,
                formatLoftDisplay,
                handleLoftSelection,
                selectedComposition,
                setSelectedComposition,
                labelClubSelection: labels.club_selection,
                labelIncludedItems: labels.included_items,
                onUserInteraction: () => {
                  hasUserInteracted.current = true;
                }
              }
            ),
            clubType === "wedges" && /* @__PURE__ */ jsxRuntimeExports.jsx(
              BounceSelector,
              {
                label: labels.select_bounce,
                clubType,
                selectedLofts,
                selectedBaseHead,
                selectedDexterity,
                productDetails,
                selectedBounce,
                setSelectedBounce: (bounce) => {
                  setSelectedBounce(bounce);
                  setUserSelectedBounce(true);
                },
                getAvailableBounces
              }
            ),
            /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex flex-col", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx(
                LoftBuildSpecSelector,
                {
                  className: "hidden",
                  label: labels.club_loft,
                  tooltipTitle: tooltips.club_loft.title,
                  tooltipText: tooltips.club_loft.text,
                  tooltipImage: tooltips.club_loft.image,
                  clubLoftTranslationsValues,
                  selectedBaseHead,
                  loftBuildSpecs,
                  selectedLoftBuildSpec,
                  setSelectedLoftBuildSpec
                }
              ),
              /* @__PURE__ */ jsxRuntimeExports.jsx(
                LieBuildSpecSelector,
                {
                  className: "hidden",
                  label: labels.club_lie,
                  tooltipTitle: tooltips.club_lie.title,
                  tooltipText: tooltips.club_lie.text,
                  tooltipImage: tooltips.club_lie.image,
                  selectedBaseHead,
                  lieBuildSpecs,
                  selectedLieBuildSpec,
                  setSelectedLieBuildSpec
                }
              ),
              /* @__PURE__ */ jsxRuntimeExports.jsx(
                ShaftMaterialSelector,
                {
                  label: labels.shaft_material,
                  clubType,
                  tooltipTitle: tooltips.shaft_material.title,
                  tooltipText: tooltips.shaft_material.text,
                  tooltipImage: tooltips.shaft_material.image,
                  materialTranslationsValues,
                  selectedBaseHead,
                  selectedShaftFlex,
                  selectedShaftMaterial,
                  setSelectedShaftMaterial: (material) => {
                    setSelectedShaftMaterial(material);
                    setSelectedShaft("");
                    setUserSelectedShaft(true);
                  },
                  shaftMaterials,
                  getAvailableShafts,
                  productDetails
                }
              ),
              /* @__PURE__ */ jsxRuntimeExports.jsx(
                ShaftFlexSelector,
                {
                  clubType,
                  label: labels.shaft_flex,
                  tooltipTitle: tooltips.shaft_flex.title,
                  tooltipText: tooltips.shaft_flex.text,
                  tooltipImage: tooltips.shaft_flex.image,
                  shaftFlexTranslationsValues,
                  selectedBaseHead,
                  selectedShaftFlex,
                  setSelectedShaftFlex: (flex) => {
                    if (flex !== selectedShaftFlex) {
                      setSelectedShaftFlex(flex);
                      setSelectedShaft("");
                      setUserSelectedShaft(true);
                    }
                  },
                  selectedShaftMaterial,
                  setSelectedShaftMaterial,
                  getAvailableShafts,
                  productDetails
                }
              ),
              /* @__PURE__ */ jsxRuntimeExports.jsx(
                ShaftSelector,
                {
                  className: "hidden",
                  label: labels.shaft,
                  selectedShaft,
                  setSelectedShaft,
                  selectedBaseHead,
                  selectedShaftFlex,
                  selectedShaftMaterial,
                  getAvailableShafts,
                  productDetails,
                  isShaftCompatible,
                  getInventoryInfo,
                  selectedHeadComponents: getSelectedHeadComponents(
                    selectedBaseHead,
                    selectedDexterity,
                    selectedBounce,
                    selectedHosel
                  ),
                  clubType
                }
              ),
              /* @__PURE__ */ jsxRuntimeExports.jsx(
                LengthBuildSpecSelector,
                {
                  className: "hidden",
                  label: labels.shaft_length,
                  shaftLengthTranslationsValues,
                  tooltipTitle: tooltips.shaft_length.title,
                  tooltipText: tooltips.shaft_length.text,
                  tooltipImage: tooltips.shaft_length.image,
                  selectedBaseHead,
                  lengthBuildSpecs,
                  selectedLengthBuildSpec,
                  setSelectedLengthBuildSpec
                }
              ),
              (selectedShaft || selectedGrip) && /* @__PURE__ */ jsxRuntimeExports.jsxs("p", { className: "mb-4 flex flex-col gap-2", children: [
                selectedShaft && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex flex-row gap-2", children: [
                  /* @__PURE__ */ jsxRuntimeExports.jsxs("span", { className: "font-bold", children: [
                    labels.shaft || "Shaft",
                    ":"
                  ] }),
                  " ",
                  ((_l = (_k = selectedShaft == null ? void 0 : selectedShaft.meta) == null ? void 0 : _k.find((m) => (m == null ? void 0 : m.key) === "display_title")) == null ? void 0 : _l.value) || (selectedShaft == null ? void 0 : selectedShaft.title) || labels.loading_text || "Loading..."
                ] }),
                selectedGrip && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex flex-row gap-2", children: [
                  /* @__PURE__ */ jsxRuntimeExports.jsxs("span", { className: "font-bold", children: [
                    labels.grip || "Grip",
                    ":"
                  ] }),
                  " ",
                  ((_n = (_m = selectedGrip == null ? void 0 : selectedGrip.meta) == null ? void 0 : _m.find((m) => (m == null ? void 0 : m.key) === "display_title")) == null ? void 0 : _n.value) || (selectedGrip == null ? void 0 : selectedGrip.title) || labels.loading_text || "Loading..."
                ] })
              ] }),
              selectedBaseHead && hasTorqueWrench && /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                TorqueWrenchSelector,
                {
                  label: labels.add_torque_wrench,
                  includeTorqueWrench,
                  handleTorqueWrenchChange,
                  torqueWrenchProduct
                }
              ) }),
              formattedTotalPrice && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mb-2 flex items-baseline gap-2", children: [
                formattedTotalCompareAtPrice && totalCompareAtPrice.compareAtPrice > totalPrice.price && /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "p2 text-grey-v3 line-through", children: formattedTotalCompareAtPrice }),
                /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "p1 text-shadow-black", children: formattedTotalPrice }),
                /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "text-xs text-gray-500", children: (_q = (_p = (_o = window.clubConfiguratorData) == null ? void 0 : _o.translations) == null ? void 0 : _p.pricing_info) == null ? void 0 : _q.labels_pricing_vat_tax_copy })
              ] }),
              /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "content-hero-hidden", children: /* @__PURE__ */ jsxRuntimeExports.jsx(PromoMessaging, { productData }) }),
              /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "content-hero-visible", children: ((_t = (_s = (_r = window.clubConfiguratorData) == null ? void 0 : _r.translations) == null ? void 0 : _s.pricing_info) == null ? void 0 : _t.is_discount_eligible) && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mb-4 flex items-center", children: [
                /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "mr-1 size-[18px] text-swing-red", children: /* @__PURE__ */ jsxRuntimeExports.jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "18", height: "18", viewBox: "0 0 24 25", fill: "none", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                  "path",
                  {
                    d: "M11.99 2.5C6.47 2.5 2 6.98 2 12.5C2 18.02 6.47 22.5 11.99 22.5C17.52 22.5 22 18.02 22 12.5C22 6.98 17.52 2.5 11.99 2.5ZM16.23 18.5L12 15.95L7.77 18.5L8.89 13.69L5.16 10.46L10.08 10.04L12 5.5L13.92 10.03L18.84 10.45L15.11 13.68L16.23 18.5Z",
                    fill: "#FFB649"
                  }
                ) }) }),
                ((_w = (_v = (_u = window.clubConfiguratorData) == null ? void 0 : _u.translations) == null ? void 0 : _v.pricing_info) == null ? void 0 : _w.best_value_link) ? /* @__PURE__ */ jsxRuntimeExports.jsx(
                  "a",
                  {
                    href: window.clubConfiguratorData.translations.pricing_info.best_value_link,
                    className: "p5 font-normal hover:underline focus:underline",
                    children: (_z = (_y = (_x = window.clubConfiguratorData) == null ? void 0 : _x.translations) == null ? void 0 : _y.pricing_info) == null ? void 0 : _z.labels_pricing_hero_best_offer_text
                  }
                ) : /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "p5 font-normal", children: (_C = (_B = (_A = window.clubConfiguratorData) == null ? void 0 : _A.translations) == null ? void 0 : _B.pricing_info) == null ? void 0 : _C.labels_pricing_hero_best_offer_text })
              ] }) }),
              ((_F = (_E = (_D = window.clubConfiguratorData) == null ? void 0 : _D.translations) == null ? void 0 : _E.product_promotions) == null ? void 0 : _F.promotional_messaging) != null && /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "mb-4", children: (_I = (_H = (_G = window.clubConfiguratorData) == null ? void 0 : _G.translations) == null ? void 0 : _H.product_promotions) == null ? void 0 : _I.promotional_messaging }),
              /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex flex-col", children: [
                /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mb-2 flex w-full flex-row gap-2", children: [
                  /* @__PURE__ */ jsxRuntimeExports.jsx(
                    "button",
                    {
                      className: "btn btn-lg btn-primary w-full capitalize disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-shadow-black-light",
                      onClick: () => handleAddToCart(false),
                      disabled: addToCartButtonState.disabled || isLoadingOnAddToCartButton,
                      children: addToCartButtonState.text
                    }
                  ),
                  /* @__PURE__ */ jsxRuntimeExports.jsx("button", { className: "btn btn-lg btn-secondary w-full", onClick: handleAdvancedPanelOpen, children: labels.customize_button_text || "Customize" })
                ] }),
                showDetailedView && isDetailedViewOpen && /* @__PURE__ */ jsxRuntimeExports.jsx(
                  "button",
                  {
                    className: "btn btn-lg btn-primary bg-blue-500 hover:text-blue-300 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-shadow-black-light",
                    onClick: () => handleAddToCart(true),
                    children: "Console Log Cart Items"
                  }
                )
              ] }),
              formattedTotalPrice && /* @__PURE__ */ jsxRuntimeExports.jsx(AffirmProduct, { price: totalPrice.price })
            ] })
          ] })
        ] }),
        /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "sticky bottom-1 left-0 right-0 z-10", children: [
          /* @__PURE__ */ jsxRuntimeExports.jsx(
            ValidationMessage,
            {
              cartValidation,
              selectedForCartItems,
              type: "error",
              validationMessages
            }
          ),
          showManualChangesMessage && /* @__PURE__ */ jsxRuntimeExports.jsx(
            ValidationMessage,
            {
              cartValidation: {
                hasIssue: true,
                items: [],
                isBalanced: true
              },
              selectedForCartItems: [],
              type: "warning",
              validationMessages,
              children: labels.manual_changes_warning || 'Warning: Your new selection may have affected your customized configuration options. Please review your "customize" settings.'
            }
          )
        ] })
      ] })
    ] }),
    /* @__PURE__ */ jsxRuntimeExports.jsx(
      ClubConfiguratorAdvancedPanel,
      {
        clubType,
        isOpen: isAdvancedPanelOpen,
        onClose: () => setIsAdvancedPanelOpen(false),
        labels,
        tooltips,
        headModelTranslationsValues,
        headColorTranslationsValues,
        shaftFlexTranslationsValues,
        shaftLengthTranslationsValues,
        materialTranslationsValues,
        gripSizeTranslationsValues,
        clubLoftTranslationsValues,
        clubLieTranslationsValues,
        selectedHeadcover,
        selectedGripSize,
        onGripSizeChange: setSelectedGripSize,
        selectedBaseHead,
        productDetails,
        selectedGrip,
        setSelectedGrip,
        includeTorqueWrench,
        handleTorqueWrenchChange,
        torqueWrenchProduct,
        hasTorqueWrench,
        selectedHosel,
        setSelectedHosel,
        selectedDexterity,
        setSelectedDexterity,
        getSelectedHeadComponents,
        selectedModel,
        setSelectedModel,
        selectedColor,
        setSelectedColor,
        availableModels,
        availableModelsForColor,
        availableColors,
        availableColorsForModel,
        baseHeads,
        selectedLofts,
        setSelectedLofts,
        getAvailableLofts,
        isLoftAvailable,
        getCompositionLofts,
        formatLoftDisplay,
        handleLoftSelection,
        selectedComposition,
        setSelectedComposition,
        selectedLoftBuildSpec,
        setSelectedLoftBuildSpec,
        selectedLieBuildSpec,
        setSelectedLieBuildSpec,
        lengthBuildSpecs,
        selectedLengthBuildSpec,
        setSelectedLengthBuildSpec,
        loftBuildSpecs,
        lieBuildSpecs,
        selectedShaftMaterial,
        setSelectedShaftMaterial,
        selectedShaftFlex,
        setSelectedShaftFlex,
        selectedShaft,
        setSelectedShaft,
        shaftMaterials,
        getAvailableShafts,
        isShaftCompatible,
        getInventoryInfo,
        selectedBounce,
        setSelectedBounce,
        getAvailableBounces,
        handleResetToDefaults,
        hasManualShaftChanges,
        setHasManualShaftChanges,
        hasManualGripChanges,
        setHasManualGripChanges,
        handleAddToCart,
        getHeadcoverInfo,
        cachedImages,
        cartQuantity,
        isLoadingCart,
        maxBundledItems: MAX_BUNDLED_COMPONENTS_IN_CART,
        totalSelectedItemsCount,
        addToCartButtonState,
        totalPrice,
        totalCompareAtPrice,
        formattedTotalPrice,
        formattedTotalCompareAtPrice,
        getAvailableHosels,
        handleMainConfigChange,
        activeDisclosurePanel,
        setActiveDisclosurePanel
      }
    ),
    showDetailedView && /* @__PURE__ */ jsxRuntimeExports.jsx(
      "button",
      {
        className: "btn btn-lg btn-primary absolute left-2 top-2 bg-blue-500",
        onClick: () => setIsDetailedViewOpen(!isDetailedViewOpen),
        children: isDetailedViewOpen ? "Close Detailed View" : "Open Detailed View"
      }
    ),
    showDetailedView && isDetailedViewOpen && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { id: "testing-area", className: "relative block bg-blue-100", children: [
      /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "p4 my-4 bg-blue-500 py-2 text-center font-bold text-white", children: "Detailed View - Not clickable" }),
      /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mx-12 mt-4 border-4 bg-fairway p-4 shadow-lg", children: [
        /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { children: [
          /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "h1", children: "Cart preview:" }),
          " "
        ] }),
        selectedBaseHead && (selectedLoftBuildSpec || selectedLieBuildSpec) && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mb-4 grid grid-cols-2 justify-between gap-2 border-2 p-4", children: [
          /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex flex-col", children: [
            /* @__PURE__ */ jsxRuntimeExports.jsxs("p", { className: "p4", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "Unique SKUs:" }),
              " ",
              selectedForCartItems.map((item) => item.gid).filter((value, index, self) => self.indexOf(value) === index).length
            ] }),
            /* @__PURE__ */ jsxRuntimeExports.jsxs("p", { className: "p4", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "Total Items:" }),
              " ",
              totalSelectedItemsCount
            ] }),
            /* @__PURE__ */ jsxRuntimeExports.jsxs("p", { className: "p4", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "Dexterity:" }),
              " ",
              selectedDexterity
            ] }),
            selectedModel && /* @__PURE__ */ jsxRuntimeExports.jsxs("p", { className: "p4", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "Model:" }),
              " ",
              selectedModel
            ] }),
            selectedColor && /* @__PURE__ */ jsxRuntimeExports.jsxs("p", { className: "p4", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "Color:" }),
              " ",
              selectedColor
            ] }),
            selectedBaseHead && /* @__PURE__ */ jsxRuntimeExports.jsxs("p", { className: "p4", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "Base Head:" }),
              " ",
              selectedBaseHead.title
            ] }),
            selectedBounce && /* @__PURE__ */ jsxRuntimeExports.jsxs("p", { className: "p4", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "Bounce:" }),
              " ",
              selectedBounce
            ] }),
            clubType === "putters" && selectedHosel && /* @__PURE__ */ jsxRuntimeExports.jsxs("p", { className: "p4", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "Selected Hosel:" }),
              " ",
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "text-blue-600", children: selectedHosel })
            ] }),
            clubType === "putters" && selectedHoselProduct && /* @__PURE__ */ jsxRuntimeExports.jsxs("p", { className: "p4", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "Selected Hosel Product:" }),
              " ",
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "", children: selectedHoselProduct == null ? void 0 : selectedHoselProduct.title })
            ] }),
            selectedLofts.length > 0 && /* @__PURE__ */ jsxRuntimeExports.jsxs("p", { className: "p4", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "Lofts:" }),
              " ",
              selectedLofts.length > 1 ? selectedLofts.join(", ") : selectedLofts[0]
            ] }),
            selectedLoftBuildSpec && /* @__PURE__ */ jsxRuntimeExports.jsxs("p", { className: "p4", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "Loft build spec:" }),
              " ",
              selectedLoftBuildSpec
            ] }),
            selectedLieBuildSpec && /* @__PURE__ */ jsxRuntimeExports.jsxs("p", { className: "p4", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "Lie build spec:" }),
              " ",
              selectedLieBuildSpec
            ] }),
            selectedShaftFlex && /* @__PURE__ */ jsxRuntimeExports.jsxs("p", { className: "p4", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "Shaft Flex:" }),
              " ",
              selectedShaftFlex
            ] }),
            selectedShaftMaterial && /* @__PURE__ */ jsxRuntimeExports.jsxs("p", { className: "p4", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "Shaft Material:" }),
              " ",
              selectedShaftMaterial
            ] }),
            selectedShaft && /* @__PURE__ */ jsxRuntimeExports.jsxs("p", { className: "p4", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "Selected Shaft:" }),
              " ",
              ((_K = (_J = selectedShaft == null ? void 0 : selectedShaft.meta) == null ? void 0 : _J.find((m) => (m == null ? void 0 : m.key) === "display_title")) == null ? void 0 : _K.value) || (selectedShaft == null ? void 0 : selectedShaft.title) || "Loading..."
            ] }),
            selectedLengthBuildSpec && /* @__PURE__ */ jsxRuntimeExports.jsxs("p", { className: "p4", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "Shaft Length Build Spec:" }),
              " ",
              selectedLengthBuildSpec
            ] }),
            selectedGripSize && /* @__PURE__ */ jsxRuntimeExports.jsxs("p", { className: "p4", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "Grip Size:" }),
              " ",
              selectedGripSize
            ] }),
            selectedGrip && /* @__PURE__ */ jsxRuntimeExports.jsxs("p", { className: "p4", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "Grip:" }),
              " ",
              ((_M = (_L = selectedGrip == null ? void 0 : selectedGrip.meta) == null ? void 0 : _L.find((m) => (m == null ? void 0 : m.key) === "display_title")) == null ? void 0 : _M.value) || (selectedGrip == null ? void 0 : selectedGrip.title) || "Loading..."
            ] })
          ] }),
          /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex flex-col", children: [
            /* @__PURE__ */ jsxRuntimeExports.jsx("h5", { className: "p4 font-bold underline", children: "Cart Validation:" }),
            /* @__PURE__ */ jsxRuntimeExports.jsxs("p", { className: "p4", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold", children: "Same number of heads, shafts, and grips:" }),
              " ",
              /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: cartValidation.isBalanced ? "text-purple-500" : "font-bold text-swing-red", children: cartValidation.isBalanced ? "Yes" : "No" })
            ] })
          ] })
        ] }),
        /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { children: [
          /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "p4 font-bold", children: "selectedForCartItems:" }),
          selectedForCartItems.length > 0 ? /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "space-y-4", children: selectedForCartItems.map((item, index) => {
            var _a2, _b2;
            const inventory = getInventoryInfo(item.product);
            return /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "rounded border border-grey-v2 p-4", children: /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mb-2", children: [
              /* @__PURE__ */ jsxRuntimeExports.jsxs("span", { className: "font-medium", children: [
                getComponentTypeName(item.type),
                " |"
              ] }),
              /* @__PURE__ */ jsxRuntimeExports.jsxs("span", { className: "ml-2 font-bold", children: [
                (_a2 = item == null ? void 0 : item.product) == null ? void 0 : _a2.title,
                " |"
              ] }),
              /* @__PURE__ */ jsxRuntimeExports.jsxs("span", { className: "text-sm text-grey-v3", children: [
                " - ",
                (_b2 = item == null ? void 0 : item.product) == null ? void 0 : _b2.id,
                " | "
              ] }),
              /* @__PURE__ */ jsxRuntimeExports.jsxs("span", { className: "ml-2", children: [
                "In Cart: ",
                item.qty,
                " |"
              ] }),
              /* @__PURE__ */ jsxRuntimeExports.jsxs("span", { className: "ml-2", children: [
                "In Stock:",
                " ",
                inventory.available ? /* @__PURE__ */ jsxRuntimeExports.jsxs(
                  "span",
                  {
                    className: `font-medium ${inventory.quantity < item.qty ? "text-system-negative" : "text-system-positive"}`,
                    children: [
                      inventory.quantity,
                      " available",
                      inventory.quantity < item.qty && " (Not enough stock!)"
                    ]
                  }
                ) : /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-medium text-system-negative", children: "Out of stock" })
              ] }),
              /* @__PURE__ */ jsxRuntimeExports.jsxs("span", { className: "ml-2 text-blue-600", children: [
                /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-bold !text-shadow-black", children: " | Mapping:" }),
                " ",
                getComponentMapping(item == null ? void 0 : item.product)
              ] })
            ] }) }, index);
          }) }) : /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "text-gray-500", children: "No components selected. Please select loft(s) and a grip to see the complete configuration." })
        ] })
      ] }),
      /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { id: "product-loaded", children: [
        /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex items-center justify-between p-1", children: /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "w-full p-1", children: /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "mx-auto max-w-[550px] border-2 border-black bg-fairway-dark p-1", children: /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex flex-col", children: [
          /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex flex-row gap-2", children: [
            /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "min-w-[220px] font-bold", children: "Base Product:" }),
            " ",
            productData == null ? void 0 : productData.title
          ] }),
          /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex flex-row gap-2", children: [
            /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "min-w-[220px] font-bold", children: "Club Type:" }),
            " ",
            clubType
          ] }),
          /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex flex-row gap-2", children: [
            /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "min-w-[220px] font-bold", children: "GID:" }),
            " ",
            productData == null ? void 0 : productData.id
          ] })
        ] }) }) }) }),
        /* @__PURE__ */ jsxRuntimeExports.jsx("h1", { className: "p4 my-1 text-center", children: "User Selection that drives the Selected Base Head:" }),
        /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mx-auto max-w-[550px] border-2 border-black bg-fairway p-1", children: [
          /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex flex-row gap-2", children: [
            /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "min-w-[220px] font-bold", children: "Selected Model:" }),
            " ",
            selectedModel
          ] }),
          /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex flex-row gap-2", children: [
            /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "min-w-[220px] font-bold", children: "Selected Color:" }),
            " ",
            selectedColor
          ] })
        ] }),
        (baseHeads == null ? void 0 : baseHeads.length) > 0 ? /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex flex-row gap-4 p-4", children: baseHeads.map((baseHead) => {
          var _a2, _b2, _c2, _d2, _e2, _f2, _g2, _h2, _i2, _j2, _k2, _l2, _m2, _n2, _o2, _p2, _q2, _r2, _s2, _t2, _u2, _v2, _w2, _x2, _y2, _z2, _A2, _B2, _C2, _D2, _E2, _F2, _G2, _H2, _I2, _J2, _K2, _L2, _M2, _N, _O, _P, _Q, _R, _S, _T, _U, _V, _W, _X, _Y, _Z, __, _$, _aa, _ba, _ca, _da, _ea, _fa, _ga, _ha, _ia, _ja, _ka, _la, _ma, _na, _oa, _pa, _qa, _ra, _sa, _ta, _ua, _va, _wa, _xa, _ya, _za;
          return /* @__PURE__ */ jsxRuntimeExports.jsxs(
            "div",
            {
              className: `w-full rounded border-4 p-4 ${baseHead === selectedBaseHead ? "border-pink-600 bg-white" : "overflow-hidden border-grey-v3 opacity-30"}`,
              children: [
                /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "mx-auto max-w-[550px] border-2 border-black bg-fairway-dark p-1", children: /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex flex-col", children: [
                  /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex flex-row gap-2", children: [
                    /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "p4 min-w-[220px] font-bold", children: "Base Head:" }),
                    " ",
                    baseHead.title
                  ] }),
                  /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex flex-row gap-2", children: [
                    /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "min-w-[220px] font-bold", children: "GID:" }),
                    " ",
                    baseHead.id
                  ] }),
                  /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex flex-row gap-2", children: [
                    /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "min-w-[220px] font-bold", children: "Model:" }),
                    ((_b2 = (_a2 = baseHead.meta) == null ? void 0 : _a2.find((m) => (m == null ? void 0 : m.key) === "head_model")) == null ? void 0 : _b2.value) || "N/A"
                  ] }),
                  /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex flex-row gap-2", children: [
                    /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "min-w-[220px] font-bold", children: "Color:" }),
                    ((_d2 = (_c2 = baseHead.meta) == null ? void 0 : _c2.find((m) => (m == null ? void 0 : m.key) === "head_color")) == null ? void 0 : _d2.value) || "N/A"
                  ] })
                ] }) }),
                /* @__PURE__ */ jsxRuntimeExports.jsx("h1", { className: "p4 my-4 text-center", children: "User Selection that drives the Selected Head Component:" }),
                /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mx-auto max-w-[550px] border-2 border-black bg-fairway p-1", children: [
                  /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex flex-row gap-2", children: [
                    /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "min-w-[220px] font-bold", children: "Selected Dexterity:" }),
                    " ",
                    selectedDexterity
                  ] }),
                  /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex flex-row gap-2", children: [
                    /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "min-w-[220px] font-bold", children: "Selected Lofts:" }),
                    " ",
                    selectedLofts.length > 0 ? selectedLofts.map((loft) => `${loft}`).join(", ") : "None"
                  ] }),
                  selectedBounce && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex flex-row gap-2", children: [
                    /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "min-w-[220px] font-bold", children: "Selected Bounce:" }),
                    " ",
                    selectedBounce
                  ] }),
                  clubType === "putters" && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex flex-row gap-2", children: [
                    /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "min-w-[220px] font-bold", children: "Selected Hosel:" }),
                    " ",
                    selectedHosel,
                    /* @__PURE__ */ jsxRuntimeExports.jsxs("span", { className: "text-blue-600", children: [
                      " mapping: ",
                      selectedHosel
                    ] })
                  ] })
                ] }),
                /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mt-3", children: [
                  /* @__PURE__ */ jsxRuntimeExports.jsx("h2", { className: "h3 text-center", children: "Components:" }),
                  /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mt-2 space-y-4 p-4", children: [
                    /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "h4", children: "Head Components" }),
                    clubType === "putters" && /* @__PURE__ */ jsxRuntimeExports.jsxs("h1", { className: "p4 mx-auto flex w-fit justify-center border-4 border-blue-600 p-2 text-center text-blue-600", children: [
                      "Mapping of the hosel: ",
                      selectedHosel
                    ] }),
                    /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex flex-row flex-wrap gap-2 rounded p-4", children: (() => {
                      var _a3, _b3;
                      try {
                        const listOfHeads = (_a3 = baseHead == null ? void 0 : baseHead.headComponents) == null ? void 0 : _a3.find(
                          (component) => (component == null ? void 0 : component.key) === "list_of_heads"
                        );
                        const headComponents = ((_b3 = listOfHeads == null ? void 0 : listOfHeads.references) == null ? void 0 : _b3.nodes) || [];
                        return /* @__PURE__ */ jsxRuntimeExports.jsx(
                          HeadComponentList,
                          {
                            headComponents,
                            selectedLofts,
                            getInventoryInfo
                          }
                        );
                      } catch (error2) {
                        console.error("Error parsing head components:", error2);
                        return /* @__PURE__ */ jsxRuntimeExports.jsx("p", { children: "Error loading head components" });
                      }
                    })() }),
                    /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: `p-4 ${clubType !== "putters" ? "hidden" : ""}`, children: [
                      /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "h4", children: "Hosels Components" }),
                      clubType === "putters" && /* @__PURE__ */ jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [
                        /* @__PURE__ */ jsxRuntimeExports.jsxs("h1", { className: "p4 mx-auto flex w-fit justify-center border-4 border-blue-600 p-2 text-center text-blue-600", children: [
                          "Selected Hosel Mapping: ",
                          selectedHosel
                        ] }),
                        /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "mt-4 flex flex-row flex-wrap gap-2", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                          HoselList,
                          {
                            listValue: (_g2 = (_f2 = (_e2 = baseHead.hoselComponents) == null ? void 0 : _e2.find((m) => (m == null ? void 0 : m.key) === "list_of_hosels")) == null ? void 0 : _f2.references) == null ? void 0 : _g2.nodes,
                            productDetails,
                            getInventoryInfo,
                            selectedHosel
                          }
                        ) })
                      ] })
                    ] }),
                    /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "p-4", children: [
                      /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "h4", children: "Shaft Components" }),
                      clubType === "putters" && /* @__PURE__ */ jsxRuntimeExports.jsxs("h1", { className: "p4 mx-auto flex w-fit justify-center border-4 border-blue-600 p-2 text-center text-blue-600", children: [
                        "Mapping of the hosel: ",
                        selectedHosel
                      ] }),
                      /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mt-2 space-y-2", children: [
                        ((_j2 = (_i2 = (_h2 = baseHead.shaftComponents) == null ? void 0 : _h2.find((m) => (m == null ? void 0 : m.key) === "list_of_default_shafts")) == null ? void 0 : _i2.references) == null ? void 0 : _j2.nodes) && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { children: [
                          /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "h4 mb-1 font-medium text-gray-700", children: "Default" }),
                          /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex flex-row flex-wrap gap-2", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                            ShaftList,
                            {
                              listValue: baseHead.shaftComponents.find(
                                (component) => (component == null ? void 0 : component.key) === "list_of_default_shafts"
                              ).references.nodes,
                              productDetails,
                              getInventoryInfo
                            }
                          ) })
                        ] }),
                        ((_l2 = (_k2 = baseHead.shaftComponents.find((component) => (component == null ? void 0 : component.key) === "list_of_shaftsladies")) == null ? void 0 : _k2.references) == null ? void 0 : _l2.nodes) && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { children: [
                          /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "h4 mb-1 font-medium text-gray-700", children: "Ladies" }),
                          /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex flex-row flex-wrap gap-2", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                            ShaftList,
                            {
                              listValue: (_n2 = (_m2 = baseHead.shaftComponents.find(
                                (component) => (component == null ? void 0 : component.key) === "list_of_shaftsladies"
                              )) == null ? void 0 : _m2.references) == null ? void 0 : _n2.nodes,
                              productDetails,
                              getInventoryInfo
                            }
                          ) })
                        ] }),
                        ((_q2 = (_p2 = (_o2 = baseHead.shaftComponents) == null ? void 0 : _o2.find((component) => (component == null ? void 0 : component.key) === "list_of_shaftssenior")) == null ? void 0 : _p2.references) == null ? void 0 : _q2.nodes) && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { children: [
                          /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "h4 mb-1 font-medium text-gray-700", children: "Senior" }),
                          /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex flex-row flex-wrap gap-2", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                            ShaftList,
                            {
                              listValue: baseHead.shaftComponents.find(
                                (component) => (component == null ? void 0 : component.key) === "list_of_shaftssenior"
                              ).references.nodes,
                              productDetails,
                              getInventoryInfo
                            }
                          ) })
                        ] }),
                        ((_t2 = (_s2 = (_r2 = baseHead.shaftComponents) == null ? void 0 : _r2.find((component) => (component == null ? void 0 : component.key) === "list_of_shaftsregular")) == null ? void 0 : _s2.references) == null ? void 0 : _t2.nodes) && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { children: [
                          /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "h4 mb-1 font-medium text-gray-700", children: "Regular" }),
                          /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex flex-row flex-wrap gap-2", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                            ShaftList,
                            {
                              listValue: (_v2 = (_u2 = baseHead.shaftComponents.find(
                                (component) => (component == null ? void 0 : component.key) === "list_of_shaftsregular"
                              )) == null ? void 0 : _u2.references) == null ? void 0 : _v2.nodes,
                              productDetails,
                              getInventoryInfo
                            }
                          ) })
                        ] }),
                        ((_y2 = (_x2 = (_w2 = baseHead.shaftComponents) == null ? void 0 : _w2.find((component) => (component == null ? void 0 : component.key) === "list_of_shaftsstiff")) == null ? void 0 : _x2.references) == null ? void 0 : _y2.nodes) && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { children: [
                          /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "h4 mb-1 font-medium text-gray-700", children: "Stiff" }),
                          /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex flex-row flex-wrap gap-2", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                            ShaftList,
                            {
                              listValue: (_A2 = (_z2 = baseHead.shaftComponents.find(
                                (component) => (component == null ? void 0 : component.key) === "list_of_shaftsstiff"
                              )) == null ? void 0 : _z2.references) == null ? void 0 : _A2.nodes,
                              productDetails,
                              getInventoryInfo
                            }
                          ) })
                        ] }),
                        ((_D2 = (_C2 = (_B2 = baseHead.shaftComponents) == null ? void 0 : _B2.find((component) => (component == null ? void 0 : component.key) === "list_of_shaftsxstiff")) == null ? void 0 : _C2.references) == null ? void 0 : _D2.nodes) && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { children: [
                          /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "h4 mb-1 font-medium text-gray-700", children: "X-Stiff" }),
                          /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex flex-row flex-wrap gap-2", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                            ShaftList,
                            {
                              listValue: (_F2 = (_E2 = baseHead.shaftComponents.find(
                                (component) => (component == null ? void 0 : component.key) === "list_of_shaftsxstiff"
                              )) == null ? void 0 : _E2.references) == null ? void 0 : _F2.nodes,
                              productDetails,
                              getInventoryInfo
                            }
                          ) })
                        ] }),
                        ((_I2 = (_H2 = (_G2 = baseHead.shaftComponents) == null ? void 0 : _G2.find((component) => (component == null ? void 0 : component.key) === "list_of_shaftsputter")) == null ? void 0 : _H2.references) == null ? void 0 : _I2.nodes) && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { children: [
                          /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "h4 mb-1 font-medium text-gray-700", children: "Shaft for Putter" }),
                          /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex flex-row flex-wrap gap-2", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                            ShaftList,
                            {
                              listValue: (_K2 = (_J2 = baseHead.shaftComponents.find(
                                (component) => (component == null ? void 0 : component.key) === "list_of_shaftsputter"
                              )) == null ? void 0 : _J2.references) == null ? void 0 : _K2.nodes,
                              productDetails,
                              getInventoryInfo
                            }
                          ) })
                        ] })
                      ] })
                    ] }),
                    /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "rounded p-4", children: [
                      /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "h4", children: "grip_component" }),
                      clubType === "putters" && /* @__PURE__ */ jsxRuntimeExports.jsxs("h1", { className: "p4 mx-auto flex w-fit justify-center border-4 border-blue-600 p-2 text-center text-blue-600", children: [
                        "Mapping of the hosel: ",
                        selectedHosel
                      ] }),
                      /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "mt-2 space-y-2", children: [
                        ((_N = (_M2 = (_L2 = baseHead.gripComponents) == null ? void 0 : _L2.find((component) => (component == null ? void 0 : component.key) === "list_of_gripsputter")) == null ? void 0 : _M2.references) == null ? void 0 : _N.nodes) && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { children: [
                          /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "h4 mb-1 font-medium text-gray-700", children: "Putter Grip" }),
                          /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex flex-row flex-wrap gap-2", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                            GripList,
                            {
                              listValue: (_P = (_O = baseHead.gripComponents.find(
                                (component) => (component == null ? void 0 : component.key) === "list_of_gripsputter"
                              )) == null ? void 0 : _O.references) == null ? void 0 : _P.nodes,
                              productDetails,
                              getInventoryInfo
                            }
                          ) })
                        ] }),
                        ((_S = (_R = (_Q = baseHead.gripComponents) == null ? void 0 : _Q.find((component) => (component == null ? void 0 : component.key) === "list_of_gripsundersize")) == null ? void 0 : _R.references) == null ? void 0 : _S.nodes) && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { children: [
                          /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "h4 mb-1 font-medium text-gray-700", children: "Undersize" }),
                          /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex flex-row flex-wrap gap-2", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                            GripList,
                            {
                              listValue: (_U = (_T = baseHead.gripComponents.find(
                                (component) => (component == null ? void 0 : component.key) === "list_of_gripsundersize"
                              )) == null ? void 0 : _T.references) == null ? void 0 : _U.nodes,
                              productDetails,
                              getInventoryInfo
                            }
                          ) })
                        ] }),
                        ((_X = (_W = (_V = baseHead.gripComponents) == null ? void 0 : _V.find((component) => (component == null ? void 0 : component.key) === "list_of_gripsstandard")) == null ? void 0 : _W.references) == null ? void 0 : _X.nodes) && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { children: [
                          /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "h4 mb-1 font-medium text-gray-700", children: "Standard" }),
                          /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex flex-row flex-wrap gap-2", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                            GripList,
                            {
                              listValue: (_Z = (_Y = baseHead.gripComponents.find(
                                (component) => (component == null ? void 0 : component.key) === "list_of_gripsstandard"
                              )) == null ? void 0 : _Y.references) == null ? void 0 : _Z.nodes,
                              productDetails,
                              getInventoryInfo
                            }
                          ) })
                        ] }),
                        ((_aa = (_$ = (__ = baseHead.gripComponents) == null ? void 0 : __.find((component) => (component == null ? void 0 : component.key) === "list_of_gripsmidsize")) == null ? void 0 : _$.references) == null ? void 0 : _aa.nodes) && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { children: [
                          /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "h4 mb-1 font-medium text-gray-700", children: "Midsize" }),
                          /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex flex-row flex-wrap gap-2", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                            GripList,
                            {
                              listValue: (_ca = (_ba = baseHead.gripComponents.find(
                                (component) => (component == null ? void 0 : component.key) === "list_of_gripsmidsize"
                              )) == null ? void 0 : _ba.references) == null ? void 0 : _ca.nodes,
                              productDetails,
                              getInventoryInfo
                            }
                          ) })
                        ] }),
                        ((_fa = (_ea = (_da = baseHead.gripComponents) == null ? void 0 : _da.find((component) => (component == null ? void 0 : component.key) === "list_of_gripsoversize")) == null ? void 0 : _ea.references) == null ? void 0 : _fa.nodes) && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { children: [
                          /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "h4 mb-1 font-medium text-gray-700", children: "Oversize" }),
                          /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex flex-row flex-wrap gap-2", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                            GripList,
                            {
                              listValue: (_ha = (_ga = baseHead.gripComponents.find(
                                (component) => (component == null ? void 0 : component.key) === "list_of_gripsoversize"
                              )) == null ? void 0 : _ga.references) == null ? void 0 : _ha.nodes,
                              productDetails,
                              getInventoryInfo
                            }
                          ) })
                        ] })
                      ] })
                    ] }),
                    ((_ka = (_ja = (_ia = baseHead.patchesComponents) == null ? void 0 : _ia.find((component) => (component == null ? void 0 : component.key) === "list_of_patches")) == null ? void 0 : _ja.references) == null ? void 0 : _ka.nodes) && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "p-4", children: [
                      /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "h4", children: "Patches Components" }),
                      clubType === "putters" && /* @__PURE__ */ jsxRuntimeExports.jsxs("h1", { className: "p4 mx-auto flex w-fit justify-center border-4 border-blue-600 p-2 text-center text-blue-600", children: [
                        "Mapping of the hosel: ",
                        selectedHosel
                      ] }),
                      /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "mt-2 space-y-2", children: ((_na = (_ma = (_la = baseHead.patchesComponents) == null ? void 0 : _la.find((component) => (component == null ? void 0 : component.key) === "list_of_patches")) == null ? void 0 : _ma.references) == null ? void 0 : _na.nodes) && /* @__PURE__ */ jsxRuntimeExports.jsx("div", { children: /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex flex-row flex-wrap gap-2", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                        PatchList,
                        {
                          listValue: (_pa = (_oa = baseHead.patchesComponents.find(
                            (component) => (component == null ? void 0 : component.key) === "list_of_patches"
                          )) == null ? void 0 : _oa.references) == null ? void 0 : _pa.nodes,
                          productDetails,
                          getInventoryInfo
                        }
                      ) }) }) })
                    ] }),
                    /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "p-4", children: [
                      /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "h4", children: "Torque Wrench Components" }),
                      clubType === "putters" && /* @__PURE__ */ jsxRuntimeExports.jsxs("h1", { className: "p4 mx-auto flex w-fit justify-center border-4 border-blue-600 p-2 text-center text-blue-600", children: [
                        "Mapping of the hosel: ",
                        selectedHosel
                      ] }),
                      /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "mt-2 space-y-2", children: ((_sa = (_ra = (_qa = baseHead.torqueWrenchComponents) == null ? void 0 : _qa.find(
                        (component) => (component == null ? void 0 : component.key) === "torque_wrench_product"
                      )) == null ? void 0 : _ra.references) == null ? void 0 : _sa.nodes) && /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { children: [
                        /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "h4 mb-1 font-medium text-gray-700", children: "Available Torque Wrenches" }),
                        /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex flex-row flex-wrap gap-2", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                          TorqueWrenchList,
                          {
                            listValue: (_ua = (_ta = baseHead.torqueWrenchComponents.find(
                              (component) => (component == null ? void 0 : component.key) === "torque_wrench_product"
                            )) == null ? void 0 : _ta.references) == null ? void 0 : _ua.nodes,
                            productDetails,
                            getInventoryInfo
                          }
                        ) })
                      ] }) })
                    ] }),
                    /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "p-4", children: [
                      /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "h4", children: "Headcover Components" }),
                      clubType === "putters" && /* @__PURE__ */ jsxRuntimeExports.jsxs("h1", { className: "p4 mx-auto flex w-fit justify-center border-4 border-blue-600 p-2 text-center text-blue-600", children: [
                        "Mapping of the hosel: ",
                        selectedHosel
                      ] }),
                      /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "mt-2 space-y-2", children: ((_xa = (_wa = (_va = baseHead.headcoverComponents) == null ? void 0 : _va.find((component) => (component == null ? void 0 : component.key) === "list_of_headcover")) == null ? void 0 : _wa.references) == null ? void 0 : _xa.nodes) && /* @__PURE__ */ jsxRuntimeExports.jsx("div", { children: /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex flex-row flex-wrap gap-2", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
                        HeadcoverList,
                        {
                          listValue: (_za = (_ya = baseHead.headcoverComponents.find(
                            (component) => (component == null ? void 0 : component.key) === "list_of_headcover"
                          )) == null ? void 0 : _ya.references) == null ? void 0 : _za.nodes,
                          productDetails,
                          getInventoryInfo
                        }
                      ) }) }) })
                    ] })
                  ] })
                ] })
              ]
            },
            baseHead.id
          );
        }) }) : /* @__PURE__ */ jsxRuntimeExports.jsx("p", { children: "No base heads available" })
      ] })
    ] })
  ] });
};
const cdpMountingElement = document.getElementById("club-configurator-app");
if (cdpMountingElement) {
  const root = clientExports.createRoot(cdpMountingElement);
  root.render(
    /* @__PURE__ */ jsxRuntimeExports.jsx(React.StrictMode, { children: /* @__PURE__ */ jsxRuntimeExports.jsx(ClubConfiguratorApp, {}) })
  );
}
